import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import LeftAccordion from './LeftAccordion';
import AlbumGrid from './AlbumGrid';
import AlbumDetail from './AlbumDetail';
import MediaPlayer from './MediaPlayer';
import SpotifyPlayer from './SpotifyPlayer';
import SwipeOverlay from './SwipeOverlay';
import SavedPlaylists from './SavedPlaylists';
import SetupWizard from './SetupWizard';
import Turntable from './Turntable';
import VoiceAssistant from './VoiceAssistant';
import GamepadController from './GamepadController';
import { getNavigationItems } from '../config/navigation';
import { API_BASE, getAuthHeaders } from '../config/api';
import { spotifyAPI, spotifyAuth } from '../services/spotify';
import { barcodeService } from '../services/barcode';
import { barcodeSaveService } from '../services/barcode/BarcodeSaveService';
import { pushoverService } from '../services/pushover';
import { Html5Qrcode } from 'html5-qrcode';
import HitsterCard from './HitsterCard';
import './LibraryLayout.css';

// Memory optimization: Maximum queue size to prevent memory issues
const MAX_QUEUE_SIZE = 1000;

// Helper function to save a single track's DisplayTitle to Firestore
async function saveTrackDisplayTitleToFirestore(track, album) {
  if (!album || !album.id) {
    console.warn('Cannot save to Firestore: album missing id');
    throw new Error('Album missing id field');
  }

  console.log('💾 Saving track DisplayTitle to Firestore:', {
    trackTitle: track.Title,
    trackId: track.Id,
    displayTitle: track.DisplayTitle,
    albumId: album.id,
    albumName: album.album || album.albumName,
    artist: album.artist || album.artistName,
    flattenedFrom: album.flattenedFrom,
  });

  try {
    // Note: JBXX collection is read-only from client (security rules block writes)
    // We skip JBXX updates and only update albums-flat
    // JBXX should be updated via server-side scripts if needed
    console.log(
      '⚠️ Skipping JBXX update (read-only from client). Updating albums-flat only.'
    );

    // Strategy 1: Try album.id directly (might be albums-flat document ID)
    let flatDocRef = null;
    console.log('🔍 Strategy 1: Trying album.id directly:', album.id);
    const directDocRef = doc(db, 'albums-flat', album.id);
    const directDocSnap = await getDoc(directDocRef);
    if (directDocSnap.exists()) {
      const flatData = directDocSnap.data();
      if (flatData.songs && Array.isArray(flatData.songs)) {
        const updatedFlatSongs = flatData.songs.map((song) =>
          song.Id === track.Id || song.Title === track.Title
            ? { ...song, DisplayTitle: track.DisplayTitle }
            : song
        );
        console.log(
          '📝 Updating albums-flat document (by direct ID):',
          album.id
        );
        await updateDoc(directDocRef, { songs: updatedFlatSongs });
        console.log(
          '✅ Updated albums-flat with DisplayTitle for track:',
          track.Title
        );
        return;
      }
    }

    // Strategy 2: Try to find by flattenedFrom
    if (album.flattenedFrom) {
      const flatDocId = album.flattenedFrom.split('/').pop();
      console.log(
        '🔍 Strategy 2: Looking for albums-flat by flattenedFrom:',
        flatDocId
      );
      if (flatDocId && flatDocId !== album.id) {
        flatDocRef = doc(db, 'albums-flat', flatDocId);
        const flatDocSnap = await getDoc(flatDocRef);
        if (flatDocSnap.exists()) {
          const flatData = flatDocSnap.data();
          if (flatData.songs && Array.isArray(flatData.songs)) {
            const updatedFlatSongs = flatData.songs.map((song) =>
              song.Id === track.Id || song.Title === track.Title
                ? { ...song, DisplayTitle: track.DisplayTitle }
                : song
            );
            console.log(
              '📝 Updating albums-flat document (by flattenedFrom):',
              flatDocId
            );
            await updateDoc(flatDocRef, { songs: updatedFlatSongs });
            console.log(
              '✅ Updated albums-flat with DisplayTitle for track:',
              track.Title
            );
            return;
          }
        }
      }
    }

    // Strategy 3: Try to find by album/artist match (with all fields)
    console.log('🔍 Strategy 3: Trying to find albums-flat by full match:', {
      type: album.type || 'Albums',
      language: album.language,
      artist: album.artist || album.artistName,
      album: album.album || album.albumName,
    });

    const queryConditions = [];
    if (album.type) queryConditions.push(where('type', '==', album.type));
    if (album.language)
      queryConditions.push(where('language', '==', album.language));
    if (album.artist || album.artistName)
      queryConditions.push(
        where('artist', '==', album.artist || album.artistName)
      );
    if (album.album || album.albumName)
      queryConditions.push(
        where('album', '==', album.album || album.albumName)
      );

    if (queryConditions.length > 0) {
      const flatQuery = query(
        collection(db, 'albums-flat'),
        ...queryConditions
      );
      const flatQuerySnap = await getDocs(flatQuery);
      console.log(
        '📊 Query results:',
        flatQuerySnap.empty
          ? 'No documents found'
          : `${flatQuerySnap.size} document(s) found`
      );
      if (!flatQuerySnap.empty) {
        flatDocRef = doc(db, 'albums-flat', flatQuerySnap.docs[0].id);
        const flatData = flatQuerySnap.docs[0].data();
        if (flatData.songs && Array.isArray(flatData.songs)) {
          const updatedFlatSongs = flatData.songs.map((song) =>
            song.Id === track.Id || song.Title === track.Title
              ? { ...song, DisplayTitle: track.DisplayTitle }
              : song
          );
          console.log(
            '📝 Updating albums-flat document (found by match):',
            flatQuerySnap.docs[0].id
          );
          await updateDoc(flatDocRef, { songs: updatedFlatSongs });
          console.log(
            '✅ Updated albums-flat (found by match) with DisplayTitle for track:',
            track.Title
          );
          return;
        }
      }
    }

    // Strategy 4: Try simpler query with just artist and album name
    if (album.artist || album.artistName) {
      console.log(
        '🔍 Strategy 4: Trying simpler query (artist + album only):',
        {
          artist: album.artist || album.artistName,
          album: album.album || album.albumName,
        }
      );
      const simpleQuery = query(
        collection(db, 'albums-flat'),
        where('artist', '==', album.artist || album.artistName),
        where('album', '==', album.album || album.albumName)
      );
      const simpleQuerySnap = await getDocs(simpleQuery);
      console.log(
        '📊 Simple query results:',
        simpleQuerySnap.empty
          ? 'No documents found'
          : `${simpleQuerySnap.size} document(s) found`
      );
      if (!simpleQuerySnap.empty) {
        flatDocRef = doc(db, 'albums-flat', simpleQuerySnap.docs[0].id);
        const flatData = simpleQuerySnap.docs[0].data();
        if (flatData.songs && Array.isArray(flatData.songs)) {
          const updatedFlatSongs = flatData.songs.map((song) =>
            song.Id === track.Id || song.Title === track.Title
              ? { ...song, DisplayTitle: track.DisplayTitle }
              : song
          );
          console.log(
            '📝 Updating albums-flat document (found by simple match):',
            simpleQuerySnap.docs[0].id
          );
          await updateDoc(flatDocRef, { songs: updatedFlatSongs });
          console.log(
            '✅ Updated albums-flat (found by simple match) with DisplayTitle for track:',
            track.Title
          );
          return;
        }
      }
    }

    // If we get here, we couldn't find or update the albums-flat document
    throw new Error(
      'Could not find albums-flat document to update. Please check album.flattenedFrom or album metadata.'
    );
  } catch (error) {
    console.error('Error saving track DisplayTitle to Firestore:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    // Re-throw with more context
    throw new Error(
      `Failed to save DisplayTitle: ${error.message} (Code: ${error.code})`
    );
  }
}

// Helper function to save all tracks' DisplayTitles to Firestore
async function saveAlbumDisplayTitlesToFirestore(album) {
  if (!album || !album.id || !album.songs) {
    console.warn('Cannot save to Firestore: album missing id or songs');
    throw new Error('Album missing id or songs array');
  }

  console.log('💾 Saving album DisplayTitles to Firestore:', {
    albumId: album.id,
    albumName: album.album || album.albumName,
    artist: album.artist || album.artistName,
    songCount: album.songs?.length || 0,
    flattenedFrom: album.flattenedFrom,
  });

  try {
    // Note: JBXX collection is read-only from client (security rules block writes)
    // We skip JBXX updates and only update albums-flat
    // JBXX should be updated via server-side scripts if needed
    console.log(
      '⚠️ Skipping JBXX update (read-only from client). Updating albums-flat only.'
    );

    // Strategy 1: Try album.id directly (might be albums-flat document ID)
    console.log('🔍 Strategy 1: Trying album.id directly:', album.id);
    const directDocRef = doc(db, 'albums-flat', album.id);
    const directDocSnap = await getDoc(directDocRef);
    if (directDocSnap.exists()) {
      console.log('📝 Updating albums-flat document (by direct ID):', album.id);
      await updateDoc(directDocRef, { songs: album.songs });
      console.log('✅ Updated albums-flat with DisplayTitles for all tracks');
      return;
    }

    // Strategy 2: Try to find by flattenedFrom
    let flatDocRef = null;
    if (album.flattenedFrom) {
      const flatDocId = album.flattenedFrom.split('/').pop();
      console.log(
        '🔍 Strategy 2: Looking for albums-flat by flattenedFrom:',
        flatDocId
      );
      if (flatDocId && flatDocId !== album.id) {
        flatDocRef = doc(db, 'albums-flat', flatDocId);
        const flatDocSnap = await getDoc(flatDocRef);
        if (flatDocSnap.exists()) {
          console.log(
            '📝 Updating albums-flat document (by flattenedFrom):',
            flatDocId
          );
          await updateDoc(flatDocRef, { songs: album.songs });
          console.log(
            '✅ Updated albums-flat with DisplayTitles for all tracks'
          );
          return;
        }
      }
    }

    // Strategy 3: Try to find by album/artist match (with all fields)
    console.log('🔍 Strategy 3: Trying to find albums-flat by full match:', {
      type: album.type || 'Albums',
      language: album.language,
      artist: album.artist || album.artistName,
      album: album.album || album.albumName,
    });

    const queryConditions = [];
    if (album.type) queryConditions.push(where('type', '==', album.type));
    if (album.language)
      queryConditions.push(where('language', '==', album.language));
    if (album.artist || album.artistName)
      queryConditions.push(
        where('artist', '==', album.artist || album.artistName)
      );
    if (album.album || album.albumName)
      queryConditions.push(
        where('album', '==', album.album || album.albumName)
      );

    if (queryConditions.length > 0) {
      const flatQuery = query(
        collection(db, 'albums-flat'),
        ...queryConditions
      );
      const flatQuerySnap = await getDocs(flatQuery);
      console.log(
        '📊 Query results:',
        flatQuerySnap.empty
          ? 'No documents found'
          : `${flatQuerySnap.size} document(s) found`
      );
      if (!flatQuerySnap.empty) {
        flatDocRef = doc(db, 'albums-flat', flatQuerySnap.docs[0].id);
        console.log(
          '📝 Updating albums-flat document (found by match):',
          flatQuerySnap.docs[0].id
        );
        await updateDoc(flatDocRef, { songs: album.songs });
        console.log(
          '✅ Updated albums-flat (found by match) with DisplayTitles for all tracks'
        );
        return;
      }
    }

    // Strategy 4: Try simpler query with just artist and album name
    if (album.artist || album.artistName) {
      console.log(
        '🔍 Strategy 4: Trying simpler query (artist + album only):',
        {
          artist: album.artist || album.artistName,
          album: album.album || album.albumName,
        }
      );
      const simpleQuery = query(
        collection(db, 'albums-flat'),
        where('artist', '==', album.artist || album.artistName),
        where('album', '==', album.album || album.albumName)
      );
      const simpleQuerySnap = await getDocs(simpleQuery);
      console.log(
        '📊 Simple query results:',
        simpleQuerySnap.empty
          ? 'No documents found'
          : `${simpleQuerySnap.size} document(s) found`
      );
      if (!simpleQuerySnap.empty) {
        flatDocRef = doc(db, 'albums-flat', simpleQuerySnap.docs[0].id);
        console.log(
          '📝 Updating albums-flat document (found by simple match):',
          simpleQuerySnap.docs[0].id
        );
        await updateDoc(flatDocRef, { songs: album.songs });
        console.log(
          '✅ Updated albums-flat (found by simple match) with DisplayTitles for all tracks'
        );
        return;
      }
    }

    // If we get here, we couldn't find or update the albums-flat document
    throw new Error(
      'Could not find albums-flat document to update. Please check album.flattenedFrom or album metadata.'
    );
  } catch (error) {
    console.error('Error saving album DisplayTitles to Firestore:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    // Re-throw with more context
    throw new Error(
      `Failed to save DisplayTitles: ${error.message} (Code: ${error.code})`
    );
  }
}

// Helper function to normalize text: remove accents and convert to Title Case
function normalizeText(text) {
  if (!text) return text;
  // Remove accents
  const normalized = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Convert to Title Case
  return normalized
    .split(' ')
    .map((word) => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

// Track Edit Accordion Component
function TrackEditAccordion({
  track,
  album,
  onUpdateTrack,
  onUpdateAlbum,
  onSaveToDatabase,
}) {
  const [expandedAccordion, setExpandedAccordion] = useState({
    display: true,
    filename: true,
  });
  const [displayTitle, setDisplayTitle] = useState(track.DisplayTitle || '');
  const [displayArtist, setDisplayArtist] = useState(
    track.DisplayArtist || track.artist || ''
  );
  const [isFirstClick, setIsFirstClick] = useState(true);
  const [showApplyButtons, setShowApplyButtons] = useState(false);
  const [canApplyToAll, setCanApplyToAll] = useState(false);
  const [clickedIndices, setClickedIndices] = useState([]); // Track which filename part indices were clicked
  const [pendingUpdates, setPendingUpdates] = useState({
    track: null,
    album: null,
    albums: null,
  }); // Track what needs to be saved
  const [hasClickedButton, setHasClickedButton] = useState(false); // Track if any filename button has been clicked
  const [isLoadingAllAlbums, setIsLoadingAllAlbums] = useState(false);

  // Update state when track changes
  useEffect(() => {
    setDisplayTitle(track.DisplayTitle || '');
    setDisplayArtist(track.DisplayArtist || track.artist || '');
  }, [track]);

  const toggleAccordion = (accordionName) => {
    setExpandedAccordion((prev) => ({
      ...prev,
      [accordionName]: !prev[accordionName],
    }));
  };

  // Get filename from track (could be Title, Filename, or storagePath)
  const getFilename = () => {
    if (track.Filename) return track.Filename;
    if (track.Title) return track.Title;
    if (track.storagePath) {
      const parts = track.storagePath.split('/');
      return parts[parts.length - 1];
    }
    return 'N/A';
  };

  // Split filename by dash "-" or underscore "_" separator (whichever is present)
  const getFilenameParts = () => {
    const filename = getFilename();
    if (!filename || filename === 'N/A') return [];

    // Remove file extension if present
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');

    // Check which separator is present (dash or underscore)
    const hasUnderscore = nameWithoutExt.includes('_');
    const hasDash = nameWithoutExt.includes('-');

    // Use underscore if present, otherwise use dash
    const separator = hasUnderscore ? '_' : hasDash ? '-' : null;

    if (!separator) {
      // No separator found, return the whole filename as a single part
      return [nameWithoutExt.trim()].filter((part) => part.length > 0);
    }

    // Split by the detected separator and trim each part
    const parts = nameWithoutExt
      .split(separator)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    return parts;
  };

  return (
    <div className="track-edit-accordions">
      {/* Accordion 1: Display Values */}
      <div className="track-edit-accordion-item">
        <button
          className={`track-edit-accordion-header ${
            expandedAccordion.display ? 'expanded' : ''
          }`}
          onClick={() => toggleAccordion('display')}
        >
          <span>Display Values</span>
          <span className="accordion-icon">
            {expandedAccordion.display ? '▼' : '▶'}
          </span>
        </button>
        {expandedAccordion.display && (
          <div className="track-edit-accordion-content">
            <div className="track-edit-buttons-vertical">
              <div className="track-edit-button-with-label">
                <div className="track-edit-button-label">DisplayTitle</div>
                <button
                  className={`track-edit-set-button ${
                    hasClickedButton &&
                    displayTitle !== (track.DisplayTitle || '')
                      ? 'modified'
                      : ''
                  }`}
                  onClick={() => {
                    // Keep existing DisplayTitle (don't overwrite with Title)
                    // This allows user to build up the DisplayTitle by clicking other buttons
                    const currentDisplayTitle =
                      displayTitle || track.DisplayTitle || '';
                    if (currentDisplayTitle) {
                      setDisplayTitle(currentDisplayTitle);
                      setIsFirstClick(false); // Mark that we're building, so subsequent clicks will concatenate
                      console.log(
                        'Preserving DisplayTitle:',
                        currentDisplayTitle
                      );
                    } else {
                      // If no DisplayTitle exists, use Title as fallback
                      const titleValue = track.Title || '';
                      setDisplayTitle(titleValue);
                      setIsFirstClick(false);
                      console.log('Setting DisplayTitle to Title:', titleValue);
                    }
                  }}
                  title={`Keep current DisplayTitle: ${
                    displayTitle || track.DisplayTitle || 'N/A'
                  }`}
                >
                  <div className="track-edit-button-content">
                    <div className="track-edit-button-main">
                      {displayTitle || 'DisplayTitle (not set)'}
                    </div>
                    {track?.metadataSource && (
                      <div className="track-edit-button-source">
                        Source:{' '}
                        {track.metadataSource === 'musicbrainz'
                          ? 'MusicBrainz'
                          : track.metadataSource === 'lastfm'
                          ? 'Last.fm'
                          : track.metadataSource}
                      </div>
                    )}
                  </div>
                </button>
              </div>
              <div className="track-edit-button-with-label">
                <div className="track-edit-button-label">DisplayArtist</div>
                <button
                  className={`track-edit-set-button ${
                    displayArtist !==
                    (track.DisplayArtist || track.artist || '')
                      ? 'modified'
                      : ''
                  }`}
                  onClick={() => {
                    // Get artist from track document first, then fallback to album
                    const artistValue =
                      track?.artist || album?.artist || album?.artistName || '';
                    setDisplayArtist(artistValue);
                    setHasClickedButton(true);
                    console.log('Setting DisplayArtist to:', artistValue);
                  }}
                  title={`Set DisplayArtist to: ${
                    track?.artist || album?.artist || album?.artistName || 'N/A'
                  }`}
                >
                  <div className="track-edit-button-content">
                    <div className="track-edit-button-main">
                      {displayArtist ||
                        track?.artist ||
                        'DisplayArtist (not set)'}
                    </div>
                    {track?.metadataSource && (
                      <div className="track-edit-button-source">
                        Source:{' '}
                        {track.metadataSource === 'musicbrainz'
                          ? 'MusicBrainz'
                          : track.metadataSource === 'lastfm'
                          ? 'Last.fm'
                          : track.metadataSource}
                      </div>
                    )}
                  </div>
                </button>
              </div>
              <div className="track-edit-button-with-label">
                <div className="track-edit-button-label">DisplayAlbum</div>
                <button
                  className="track-edit-set-button"
                  onClick={() => {
                    const albumValue = album?.album || album?.albumName || '';
                    // TODO: Save DisplayAlbum to Firestore
                    console.log('Setting DisplayAlbum to:', albumValue);
                  }}
                  title={`Set DisplayAlbum to: ${
                    album?.album || album?.albumName || 'N/A'
                  }`}
                >
                  <div className="track-edit-button-content">
                    <div className="track-edit-button-main">
                      {track.DisplayAlbum || 'DisplayAlbum (not set)'}
                    </div>
                    {album?.metadataSource && (
                      <div className="track-edit-button-source">
                        Source:{' '}
                        {album.metadataSource === 'musicbrainz'
                          ? 'MusicBrainz'
                          : album.metadataSource === 'lastfm'
                          ? 'Last.fm'
                          : album.metadataSource}
                      </div>
                    )}
                  </div>
                </button>
              </div>
              <div className="track-edit-button-with-label">
                <div className="track-edit-button-label">
                  Artist (from Firestore)
                </div>
                <button
                  className="track-edit-set-button"
                  onClick={() => {
                    // Get artist from track document
                    const artistValue = track?.artist || '';
                    if (!artistValue) {
                      console.log('No artist value available');
                      return;
                    }

                    // Normalize the artist (remove accents, Title Case)
                    const normalizedArtist = normalizeText(artistValue);

                    // Always concatenate to existing DisplayTitle (if DisplayTitle button was clicked first, it preserves the value)
                    const currentDisplayTitle = displayTitle || '';
                    let newTitle;
                    if (isFirstClick && !currentDisplayTitle) {
                      // First click and no DisplayTitle: start with just the normalized artist
                      newTitle = normalizedArtist;
                      setIsFirstClick(false);
                    } else {
                      // Concatenate to existing DisplayTitle
                      const normalizedDisplayTitle = currentDisplayTitle
                        ? normalizeText(currentDisplayTitle)
                        : '';
                      newTitle = normalizedDisplayTitle
                        ? `${normalizedDisplayTitle} - ${normalizedArtist}`
                        : normalizedArtist;
                      setIsFirstClick(false);
                    }

                    setDisplayTitle(newTitle);
                    setHasClickedButton(true);
                    console.log('Adding normalized artist to DisplayTitle:', {
                      artistValue,
                      normalizedArtist,
                      newTitle,
                    });
                  }}
                  title={`Add "${
                    track?.artist || 'N/A'
                  }" to DisplayTitle (normalized)`}
                  disabled={!track?.artist}
                >
                  <div className="track-edit-button-content">
                    <div className="track-edit-button-main">
                      {track?.artist || 'Artist (not set)'}
                    </div>
                    {track?.metadataSource && (
                      <div className="track-edit-button-source">
                        Source:{' '}
                        {track.metadataSource === 'musicbrainz'
                          ? 'MusicBrainz'
                          : track.metadataSource === 'lastfm'
                          ? 'Last.fm'
                          : track.metadataSource}
                      </div>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Accordion 2: Filename Values */}
      <div className="track-edit-accordion-item">
        <button
          className={`track-edit-accordion-header ${
            expandedAccordion.filename ? 'expanded' : ''
          }`}
          onClick={() => toggleAccordion('filename')}
        >
          <span>Filename Values</span>
          <span className="accordion-icon">
            {expandedAccordion.filename ? '▼' : '▶'}
          </span>
        </button>
        {expandedAccordion.filename && (
          <div className="track-edit-accordion-content">
            <div className="track-edit-buttons-vertical">
              {getFilenameParts().map((part, index) => (
                <button
                  key={index}
                  className="track-edit-set-button"
                  onClick={() => {
                    // Normalize the part (remove accents, Title Case)
                    const normalizedPart = normalizeText(part);

                    // Always concatenate to existing DisplayTitle (if DisplayTitle button was clicked first, it preserves the value)
                    // If DisplayTitle is empty, start fresh with this part
                    let newTitle;
                    let newIndices;
                    const currentDisplayTitle = displayTitle || '';
                    if (isFirstClick && !currentDisplayTitle) {
                      // First click and no DisplayTitle: start fresh
                      newTitle = normalizedPart;
                      newIndices = [index];
                      setIsFirstClick(false);
                    } else {
                      // Concatenate to existing DisplayTitle
                      const normalizedDisplayTitle = currentDisplayTitle
                        ? normalizeText(currentDisplayTitle)
                        : '';
                      newTitle = normalizedDisplayTitle
                        ? `${normalizedDisplayTitle} - ${normalizedPart}`
                        : normalizedPart;
                      newIndices = [...clickedIndices, index];
                      setIsFirstClick(false);
                    }
                    setDisplayTitle(newTitle);
                    setClickedIndices(newIndices);
                    setHasClickedButton(true); // Mark that a button has been clicked
                    // TODO: Save DisplayTitle to Firestore
                    console.log(
                      'Updating DisplayTitle to:',
                      newTitle,
                      'Indices:',
                      newIndices
                    );
                  }}
                  title={`Add "${part}" to DisplayTitle (normalized)`}
                >
                  {part}
                </button>
              ))}
              {getFilenameParts().length === 0 && (
                <div className="track-edit-no-parts">
                  No filename parts available
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Done and Apply Buttons - Outside both accordions */}
      <div className="track-edit-done-container">
        <div className="track-edit-action-buttons">
          <button
            className="track-edit-done-button"
            onClick={() => {
              if (displayTitle || displayArtist) {
                // TODO: Save DisplayTitle and DisplayArtist to Firestore for this track
                console.log(
                  'Saving DisplayTitle and DisplayArtist to Firestore:',
                  { displayTitle, displayArtist }
                );
                setIsFirstClick(true); // Reset for next time
                setShowApplyButtons(true);
                // Don't reset clickedIndices here - keep them for "Apply to all"
              }
            }}
            disabled={!displayTitle && !displayArtist}
          >
            Done
          </button>
          <button
            className="track-edit-apply-track-button"
            onClick={() => {
              if (displayTitle && track) {
                // Update the track with the new DisplayTitle and DisplayArtist
                const updatedTrack = {
                  ...track,
                  DisplayTitle: displayTitle,
                  DisplayArtist: displayArtist || track.artist || null,
                };
                if (onUpdateTrack) {
                  onUpdateTrack(updatedTrack);
                }
                // Store pending update for database save
                setPendingUpdates((prev) => ({ ...prev, track: updatedTrack }));
                console.log(
                  'Applying DisplayTitle and DisplayArtist to this track:',
                  { displayTitle, displayArtist },
                  track
                );
                setIsFirstClick(true);
                setCanApplyToAll(true);
              }
            }}
            disabled={!displayTitle || !track || !showApplyButtons}
          >
            Apply to this track
          </button>
          <button
            className="track-edit-apply-all-button"
            onClick={() => {
              if (
                displayTitle &&
                album &&
                album.songs &&
                clickedIndices.length > 0
              ) {
                console.log('🔄 Starting "Apply to all tracks" with:', {
                  clickedIndices,
                  currentDisplayTitle: displayTitle,
                  currentTrackFilename: track.Filename || track.Title,
                  albumSongsCount: album.songs.length,
                });

                // Get the current track's filename structure for reference
                const currentTrackFilename =
                  track.Filename || track.Title || '';
                const currentNameWithoutExt = currentTrackFilename.replace(
                  /\.[^/.]+$/,
                  ''
                );
                const currentHasUnderscore =
                  currentNameWithoutExt.includes('_');
                const currentHasDash = currentNameWithoutExt.includes('-');
                const currentSeparator = currentHasUnderscore
                  ? '_'
                  : currentHasDash
                  ? '-'
                  : null;

                // Get current track's parts to understand the pattern
                const currentParts = currentSeparator
                  ? currentNameWithoutExt
                      .split(currentSeparator)
                      .map((part) => part.trim())
                      .filter((part) => part.length > 0)
                  : [currentNameWithoutExt.trim()].filter(
                      (part) => part.length > 0
                    );

                // Check if the current DisplayTitle includes an artist part (ends with " - ArtistName")
                // This happens when the user clicked the artist button after clicking filename parts
                let includeArtistInPattern = false;
                let artistPattern = null;
                const currentFilenamePartsPattern = clickedIndices
                  .map((idx) => currentParts[idx])
                  .join(' - ');

                // Check if displayTitle matches "filename parts - artist" pattern
                if (displayTitle && displayTitle.includes(' - ')) {
                  const parts = displayTitle.split(' - ');
                  const filenamePart = parts.slice(0, -1).join(' - '); // Everything except last part
                  const possibleArtist = parts[parts.length - 1]; // Last part

                  // Check if the filename part matches what we expect from clickedIndices
                  const normalizedFilenamePart = normalizeText(
                    currentFilenamePartsPattern
                  );
                  if (normalizeText(filenamePart) === normalizedFilenamePart) {
                    // The DisplayTitle follows the pattern: "filename parts - artist"
                    includeArtistInPattern = true;
                    artistPattern = possibleArtist; // This is the artist from the current track
                    console.log('🎨 Detected artist pattern in DisplayTitle:', {
                      filenamePart,
                      artistPart: possibleArtist,
                      fullPattern: displayTitle,
                    });
                  }
                }

                console.log('📋 Current track pattern:', {
                  separator: currentSeparator,
                  parts: currentParts,
                  clickedIndices,
                  expectedPattern: clickedIndices
                    .map((idx) => currentParts[idx])
                    .join(' - '),
                  includeArtist: includeArtistInPattern,
                  artistPattern,
                });

                // IMPORTANT: Use album.songs but preserve existing DisplayTitle values
                // Apply the same pattern to all tracks in the album that have the same filename structure
                let updatedCount = 0;
                let skippedCount = 0;
                const updatedSongs = album.songs.map((song, songIndex) => {
                  // Preserve the existing DisplayTitle in case we don't update this track
                  const existingDisplayTitle = song.DisplayTitle;
                  // Get filename parts for this song
                  const songFilename = song.Filename || song.Title || '';
                  const nameWithoutExt = songFilename.replace(/\.[^/.]+$/, '');

                  // Check which separator is present (dash or underscore)
                  const hasUnderscore = nameWithoutExt.includes('_');
                  const hasDash = nameWithoutExt.includes('-');
                  const separator = hasUnderscore ? '_' : hasDash ? '-' : null;

                  // Only apply pattern if this track has the same separator type as the current track
                  if (separator !== currentSeparator) {
                    // Different filename structure - preserve existing DisplayTitle
                    skippedCount++;
                    console.log(
                      `⏭️ Skipping track ${songIndex + 1} (${
                        song.Title
                      }): different separator (${separator} vs ${currentSeparator}), preserving DisplayTitle: "${
                        existingDisplayTitle || '(none)'
                      }"`
                    );
                    // Return song with existing DisplayTitle preserved
                    return {
                      ...song,
                      DisplayTitle: existingDisplayTitle, // Explicitly preserve
                    };
                  }

                  const songParts = separator
                    ? nameWithoutExt
                        .split(separator)
                        .map((part) => part.trim())
                        .filter((part) => part.length > 0)
                    : [nameWithoutExt.trim()].filter((part) => part.length > 0);

                  // Apply the same indices pattern to build DisplayTitle
                  if (
                    songParts.length > 0 &&
                    clickedIndices.every((idx) => idx < songParts.length)
                  ) {
                    // Normalize each part - don't filter out parts, use all clicked indices
                    const normalizedParts = clickedIndices.map((idx) => {
                      const part = songParts[idx];
                      if (!part || part.trim().length === 0) {
                        console.warn(
                          `  ⚠️ Part ${idx} is empty for track ${songIndex + 1}`
                        );
                        return '';
                      }
                      const normalized = normalizeText(part);
                      console.log(
                        `  Part ${idx}: "${part}" -> "${normalized}"`
                      );
                      return normalized;
                    });

                    // Only join non-empty parts
                    const validParts = normalizedParts.filter(
                      (part) => part && part.trim().length > 0
                    );
                    let newDisplayTitle = validParts.join(' - ');

                    // If the pattern includes artist, append the artist from this track
                    if (includeArtistInPattern && newDisplayTitle) {
                      const trackArtist = song.artist || track.artist || '';
                      if (trackArtist) {
                        const normalizedTrackArtist =
                          normalizeText(trackArtist);
                        newDisplayTitle = `${newDisplayTitle} - ${normalizedTrackArtist}`;
                        console.log(
                          `  🎨 Added artist to track ${
                            songIndex + 1
                          }: "${trackArtist}" -> "${normalizedTrackArtist}"`
                        );
                      } else {
                        console.log(
                          `  ⚠️ Track ${
                            songIndex + 1
                          } has no artist field, skipping artist in pattern`
                        );
                      }
                    }

                    // Only update if we have a valid DisplayTitle from the pattern with at least one part
                    if (
                      newDisplayTitle &&
                      newDisplayTitle.trim().length > 0 &&
                      validParts.length > 0
                    ) {
                      updatedCount++;
                      console.log(
                        `✅ Updating track ${songIndex + 1} (${song.Title}): "${
                          song.DisplayTitle || '(none)'
                        }" -> "${newDisplayTitle}"`
                      );
                      return {
                        ...song,
                        DisplayTitle: newDisplayTitle,
                      };
                    } else {
                      skippedCount++;
                      console.log(
                        `⚠️ Skipping track ${songIndex + 1} (${
                          song.Title
                        }): normalized parts resulted in empty DisplayTitle (validParts: ${
                          validParts.length
                        }), preserving DisplayTitle: "${
                          existingDisplayTitle || '(none)'
                        }"`
                      );
                      // Preserve original DisplayTitle explicitly
                      return {
                        ...song,
                        DisplayTitle: existingDisplayTitle, // Explicitly preserve
                      };
                    }
                  } else {
                    skippedCount++;
                    console.log(
                      `⏭️ Skipping track ${songIndex + 1} (${
                        song.Title
                      }): not enough parts (${
                        songParts.length
                      } parts, need indices: ${clickedIndices.join(
                        ', '
                      )}), preserving DisplayTitle: "${
                        existingDisplayTitle || '(none)'
                      }"`
                    );
                    // Preserve original DisplayTitle explicitly
                    return {
                      ...song,
                      DisplayTitle: existingDisplayTitle, // Explicitly preserve
                    };
                  }
                });

                console.log(
                  `📊 Summary: Applied pattern to ${updatedCount} tracks, kept ${skippedCount} unchanged`
                );

                // Verify all songs have DisplayTitle preserved
                const songsWithDisplayTitle = updatedSongs.map((song) => ({
                  title: song.Title,
                  displayTitle: song.DisplayTitle || '(none)',
                  hasDisplayTitle: !!song.DisplayTitle,
                }));
                console.log(
                  '📋 All songs after update:',
                  songsWithDisplayTitle
                );

                const updatedAlbum = {
                  ...album,
                  songs: updatedSongs,
                };

                // Verify the updated album before passing it
                console.log('📦 Updated album structure:', {
                  albumId: updatedAlbum.id,
                  songsCount: updatedAlbum.songs?.length,
                  firstSongDisplayTitle:
                    updatedAlbum.songs?.[0]?.DisplayTitle || '(none)',
                  allSongsHaveDisplayTitle: updatedAlbum.songs?.every(
                    (s) => s.DisplayTitle !== undefined
                  ),
                });

                // Update the entire album at once
                if (onUpdateAlbum) {
                  console.log('📤 Calling onUpdateAlbum with updated album');
                  onUpdateAlbum(updatedAlbum);
                } else if (onUpdateTrack) {
                  // Fallback: update each track individually
                  console.log('📤 Falling back to onUpdateTrack for each song');
                  updatedSongs.forEach((updatedSong) => {
                    const originalSong = album.songs.find(
                      (s) =>
                        s.Id === updatedSong.Id || s.Title === updatedSong.Title
                    );
                    if (
                      originalSong &&
                      updatedSong.DisplayTitle !== originalSong.DisplayTitle
                    ) {
                      onUpdateTrack(updatedSong);
                    }
                  });
                }

                // Store pending update for database save
                setPendingUpdates((prev) => ({ ...prev, album: updatedAlbum }));

                console.log('✅ Completed "Apply to all tracks"');
                setIsFirstClick(true);
              } else {
                console.warn('⚠️ Cannot apply to all tracks:', {
                  hasDisplayTitle: !!displayTitle,
                  hasAlbum: !!album,
                  hasSongs: !!(album && album.songs),
                  clickedIndicesLength: clickedIndices.length,
                });
              }
            }}
            disabled={
              !displayTitle ||
              !album ||
              !showApplyButtons ||
              !canApplyToAll ||
              clickedIndices.length === 0
            }
          >
            Apply to all tracks for this Album
          </button>
          <button
            className="track-edit-apply-all-artist-button"
            onClick={async () => {
              if (displayTitle && album && clickedIndices.length > 0) {
                const artistName = album.artist || album.artistName;
                if (!artistName) {
                  alert('❌ Cannot apply to all albums: Artist name not found');
                  return;
                }

                const confirmMessage = `Apply this DisplayTitle pattern to ALL tracks in ALL albums for "${artistName}"?\n\nThis will update all albums by this artist.`;
                if (!window.confirm(confirmMessage)) {
                  return;
                }

                setIsLoadingAllAlbums(true);
                try {
                  // Query all albums for this artist
                  console.log('🔍 Querying all albums for artist:', artistName);
                  const artistQuery = query(
                    collection(db, 'albums-flat'),
                    where('artist', '==', artistName)
                  );
                  const artistQuerySnap = await getDocs(artistQuery);

                  if (artistQuerySnap.empty) {
                    alert(`❌ No albums found for artist "${artistName}"`);
                    setIsLoadingAllAlbums(false);
                    return;
                  }

                  console.log(
                    `📚 Found ${artistQuerySnap.size} album(s) for artist "${artistName}"`
                  );

                  // Apply pattern to all tracks in all albums
                  const updatedAlbums = [];
                  artistQuerySnap.forEach((docSnap) => {
                    const albumData = { id: docSnap.id, ...docSnap.data() };
                    if (!albumData.songs || !Array.isArray(albumData.songs)) {
                      return;
                    }

                    const updatedSongs = albumData.songs.map((song) => {
                      // Get filename parts for this song
                      const songFilename = song.Filename || song.Title || '';
                      const nameWithoutExt = songFilename.replace(
                        /\.[^/.]+$/,
                        ''
                      );
                      const songParts = nameWithoutExt
                        .split('-')
                        .map((part) => part.trim())
                        .filter((part) => part.length > 0);

                      // Apply the same indices pattern to build DisplayTitle
                      if (songParts.length > 0) {
                        const newDisplayTitle = clickedIndices
                          .filter((idx) => idx < songParts.length)
                          .map((idx) => songParts[idx])
                          .join(' - ');

                        return {
                          ...song,
                          DisplayTitle: newDisplayTitle || song.DisplayTitle,
                        };
                      }
                      return song;
                    });

                    updatedAlbums.push({
                      ...albumData,
                      songs: updatedSongs,
                    });
                  });

                  console.log(
                    `✅ Updated ${updatedAlbums.length} album(s) with DisplayTitle pattern`
                  );

                  // Store all updated albums for database save
                  setPendingUpdates((prev) => ({
                    ...prev,
                    albums: updatedAlbums,
                  }));

                  // Update the current album if it's in the list
                  const currentAlbumUpdated = updatedAlbums.find(
                    (a) => a.id === album.id
                  );
                  if (currentAlbumUpdated && onUpdateAlbum) {
                    onUpdateAlbum(currentAlbumUpdated);
                  }

                  alert(
                    `✅ Pattern applied to ${updatedAlbums.length} album(s)!\n\nClick "Update database" to save all changes.`
                  );
                  setIsFirstClick(true);
                } catch (error) {
                  console.error('Error applying pattern to all albums:', error);
                  alert(
                    `❌ Error applying pattern to all albums: ${error.message}`
                  );
                } finally {
                  setIsLoadingAllAlbums(false);
                }
              }
            }}
            disabled={
              !displayTitle ||
              !album ||
              !showApplyButtons ||
              !canApplyToAll ||
              clickedIndices.length === 0 ||
              isLoadingAllAlbums
            }
          >
            {isLoadingAllAlbums
              ? 'Loading...'
              : 'Apply to all Albums for this Artist'}
          </button>
          <button
            className="track-edit-update-database-button"
            onClick={async () => {
              if (onSaveToDatabase) {
                try {
                  await onSaveToDatabase(pendingUpdates);
                  // Clear pending updates after successful save
                  setPendingUpdates({ track: null, album: null, albums: null });
                  alert('✅ Successfully updated database!');
                } catch (error) {
                  console.error('Error updating database:', error);
                  console.error('Full error object:', error);
                  // Show the actual error message to help debug
                  const errorMessage =
                    error.message || error.toString() || 'Unknown error';
                  alert(
                    `❌ Error updating database:\n\n${errorMessage}\n\nPlease check the browser console for more details.`
                  );
                }
              }
            }}
            disabled={
              !pendingUpdates.track &&
              !pendingUpdates.album &&
              !pendingUpdates.albums
            }
          >
            Update database
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function to check if DisplayTitle is valid (in sync with JBXX)
// DisplayTitle should NOT have accents or track number prefixes
// If it has these, it means albums-flat is out of sync with JBXX
const isDisplayTitleValid = (displayTitle) => {
  if (!displayTitle) return false;

  // Check if has track number prefix like "01 - " or "01_" (means out of sync)
  if (displayTitle.match(/^\d+\s*[-_]\s*/)) return false;

  // Check if has accents (DisplayTitle should never have accents, only Title can)
  // If it has accents, albums-flat is out of sync
  const normalized = displayTitle
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (displayTitle !== normalized) return false;

  // DisplayTitle is valid (in sync)
  return true;
};

// Helper function to fetch DisplayTitle from JBXX collection (source of truth)
// JBXX is the master, albums-flat should be synced from JBXX
const fetchDisplayTitleFromJBXX = async (song, album) => {
  try {
    // Try to find the original JBXX document
    // First try using flattenedFrom if available
    if (album.flattenedFrom) {
      // flattenedFrom format: "JBXX/{docId}"
      const jbxxDocId = album.flattenedFrom.split('/').pop();
      if (jbxxDocId) {
        const jbxxDocRef = doc(db, 'JBXX', jbxxDocId);
        const jbxxDocSnap = await getDoc(jbxxDocRef);
        if (jbxxDocSnap.exists()) {
          const jbxxData = jbxxDocSnap.data();
          // Find matching song in JBXX document
          if (jbxxData.songs && Array.isArray(jbxxData.songs)) {
            const matchingSong = jbxxData.songs.find(
              (s) => s.Title === song.Title || s.Id === song.Id
            );
            if (matchingSong?.DisplayTitle) {
              console.log(
                `✅ Found DisplayTitle from JBXX (source of truth): "${matchingSong.DisplayTitle}"`
              );
              return matchingSong.DisplayTitle;
            }
          }
        }
      }
    }

    // Fallback: Search JBXX by album/artist match
    const jbxxQuery = query(
      collection(db, 'JBXX'),
      where('album', '==', album.album),
      where('artist', '==', album.artist),
      where('type', '==', album.type || 'Albums')
    );
    const jbxxSnapshot = await getDocs(jbxxQuery);

    if (!jbxxSnapshot.empty) {
      const jbxxDoc = jbxxSnapshot.docs[0];
      const jbxxData = jbxxDoc.data();
      if (jbxxData.songs && Array.isArray(jbxxData.songs)) {
        const matchingSong = jbxxData.songs.find(
          (s) => s.Title === song.Title || s.Id === song.Id
        );
        if (matchingSong?.DisplayTitle) {
          console.log(
            `✅ Found DisplayTitle from JBXX (source of truth, by search): "${matchingSong.DisplayTitle}"`
          );
          return matchingSong.DisplayTitle;
        }
      }
    }
  } catch (error) {
    console.warn(
      '⚠️ Could not fetch DisplayTitle from JBXX (source of truth):',
      error.message
    );
  }
  return null;
};

// Helper function to enrich a song's DisplayTitle from JBXX (source of truth) if albums-flat is out of sync
// JBXX is the master - albums-flat should be synced FROM JBXX
const enrichSongDisplayTitle = async (song, album) => {
  let displayTitle = song.DisplayTitle;

  // Check if DisplayTitle from albums-flat is valid (in sync with JBXX)
  // If valid, use it directly - no need to fetch from JBXX
  if (isDisplayTitleValid(displayTitle)) {
    return displayTitle; // In sync with JBXX, use albums-flat value
  }

  // albums-flat is out of sync - fetch correct DisplayTitle from JBXX (source of truth)
  console.log(
    `⚠️ albums-flat out of sync for "${song.Title}", fetching from JBXX (source of truth):`,
    {
      albumsFlatDisplayTitle: displayTitle,
      hasAccents:
        displayTitle &&
        displayTitle !==
          displayTitle.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
      hasTrackPrefix: displayTitle?.match(/^\d+\s*[-_]\s*/) ? true : false,
    }
  );

  const jbxxDisplayTitle = await fetchDisplayTitleFromJBXX(song, album);
  if (jbxxDisplayTitle) {
    displayTitle = jbxxDisplayTitle;
    console.log(
      `✅ Fetched correct DisplayTitle from JBXX (source of truth): "${displayTitle}"`
    );
    // Note: albums-flat should be synced from JBXX via a sync script to keep them in sync
  } else {
    console.warn(
      `⚠️ Could not fetch DisplayTitle from JBXX, using albums-flat value: "${displayTitle}"`
    );
  }

  return displayTitle;
};

function LibraryLayout({ user }) {
  const navigate = useNavigate();

  // Logout handler - clears all state to free memory
  const handleLogout = async () => {
    try {
      // Clear all state to free memory before logout
      console.log('🧹 Clearing state before logout...');
      setShuffleQueue([]);
      setQueuedAlbumIds(new Set());
      setCurrentTrack(null);
      setCurrentTrackIndex(0);
      setSelectedAlbum(null);
      setIsPlaying(false);
      setSwipeQueue([]);
      setSavedPlaylists([]);
      setHierarchy(null);
      setIsLoadingHierarchy(true);
      setSelectedPath({
        type: null,
        language: null,
        genre: null,
        subgenre: null,
        artist: null,
      });

      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const [selectedPath, setSelectedPath] = useState({
    type: null, // null shows both Albums and Singles by default
    language: null, // 'English', 'Spanish', etc.
    genre: null, // 'Rock', 'Pop', etc.
    subgenre: null, // 'Classic Rock', etc.
    artist: null, // 'Beatles', etc.
  });

  // Array of selected artist names for multi-select (accumulated from all levels)
  const [selectedArtists, setSelectedArtists] = useState([]);
  // Array of artists selected from the Master Artist list (for dropdown)
  const [selectedFromMasterList, setSelectedFromMasterList] = useState([]);
  // Flag to track if "Done Searching" has been clicked (only filter when true)
  const [isFilteringByArtists, setIsFilteringByArtists] = useState(false);
  // Store the navigation path when Done is clicked, so we can restore it when Back is clicked
  const [savedNavigationPath, setSavedNavigationPath] = useState(null);
  // Master Playlist mode - when enabled, accumulates artists during session
  const [isMasterPlaylistMode, setIsMasterPlaylistMode] = useState(false);
  // Flag to track if we're in "Scanned" mode (browsing scanned albums)
  const [isInScannedMode, setIsInScannedMode] = useState(false);

  // Accordion states for navbar and albums
  const [isNavbarExpanded, setIsNavbarExpanded] = useState(true); // Expanded by default to load library automatically
  const [isAlbumsExpanded, setIsAlbumsExpanded] = useState(false); // Collapsed by default - only show Albums/Singles buttons
  const [isHipsterExpanded, setIsHipsterExpanded] = useState(false); // Hipster accordion state

  // Hitster playlist state
  const [hitsterTracks, setHitsterTracks] = useState([]);
  const [hitsterLoading, setHitsterLoading] = useState(false);
  const [hitsterError, setHitsterError] = useState(null);
  const [expandedDecades, setExpandedDecades] = useState(new Set());
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [expandedArtists, setExpandedArtists] = useState(new Set());
  const [selectedTrackForCard, setSelectedTrackForCard] = useState(null); // Track selected to show card
  const [randomTrack, setRandomTrack] = useState(null); // Random track for game mode

  // Spotify Premium status for full playback
  const [isSpotifyPremium, setIsSpotifyPremium] = useState(null);
  const [spotifyTrackUri, setSpotifyTrackUri] = useState(null);

  // QR Code Scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);
  const qrScannerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hierarchy, setHierarchy] = useState(null);
  const [isLoadingHierarchy, setIsLoadingHierarchy] = useState(true);

  // Ensure hierarchy starts loading immediately when Library section loads
  useEffect(() => {
    if (user && !hierarchy) {
      // Hierarchy will be loaded by LeftAccordion component on mount
      // This ensures we're ready to load as soon as user is available
      console.log('📚 Library section loaded - hierarchy will load automatically');
    }
  }, [user, hierarchy]);

  // Timeout to ensure loading completes even if hierarchy load hangs
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoadingHierarchy) {
        console.warn('⚠️ Hierarchy loading timeout - stopping loading state');
        setIsLoadingHierarchy(false);
        // Keep Navigation accordion collapsed - user can expand it manually
      }
    }, 30000); // 30 second timeout

    return () => clearTimeout(timeout);
  }, [isLoadingHierarchy]);

  // Mobile drawer states
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false); // Will be set to true on mobile mount
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  // Navigation links visibility state (for mobile menu button)
  const [showNavLinks, setShowNavLinks] = useState(false);

  // Ref for VoiceAssistant to trigger startListening from inline button
  const voiceAssistantRef = useRef(null);

  // Ref for Spotify preview audio playback
  const spotifyPreviewAudioRef = useRef(null);

  // Desktop left panel collapse state (for "Done Searching" feature)
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);

  // Ensure navbar is always open in viewport-top-column
  const isNavbarInTopColumn = true;

  // Desktop right panel collapse state (hidden until play action)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(true);

  // Gamepad panel focus: 'left', 'middle', 'right'
  const [focusedPanel, setFocusedPanel] = useState('middle');

  // Shuffle queue state
  const [shuffleQueue, setShuffleQueue] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [queuedAlbumIds, setQueuedAlbumIds] = useState(new Set()); // Track queued albums directly
  const [currentPlaylist, setCurrentPlaylist] = useState(null); // Store current playlist info (name, description, etc.)

  // Swipe mode state
  const [isSwipeModeActive, setIsSwipeModeActive] = useState(false);
  const [swipeQueue, setSwipeQueue] = useState([]);
  const [swipeMode, setSwipeMode] = useState('listening'); // 'preview' or 'listening'
  const [blockedArtists, setBlockedArtists] = useState([]);
  const [liveSavePlaylistId, setLiveSavePlaylistId] = useState(null); // Playlist ID for live saving
  const [liveSaveMode, setLiveSaveMode] = useState(null); // 'create' or 'append'
  const [liveSavePlaylistInfo, setLiveSavePlaylistInfo] = useState(null); // Playlist info (name, trackCount) for display

  // Expand Navigation accordion by default when Library section is visible (not in swipe mode)
  useEffect(() => {
    if (!isSwipeModeActive && user) {
      // Expand Navigation accordion to trigger library load
      setIsNavbarExpanded(true);
      setLeftPanelCollapsed(false);
      console.log('📚 Library section visible - expanding Navigation accordion to load library');
    }
  }, [isSwipeModeActive, user]);

  // Saved playlists state
  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [showPlaylists, setShowPlaylists] = useState(true); // Start in Playlist mode - show playlists on startup
  const [isCreatingMaster, setIsCreatingMaster] = useState(false);

  // Setup wizard state
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  // Note: setupChecked and userPreferences are set but not currently used
  // Keeping setters for potential future use
  const [, setSetupChecked] = useState(false);
  const [, setUserPreferences] = useState(null);

  // Turntable view state
  const [showTurntable, setShowTurntable] = useState(false);

  // Media player flyout state
  const [mediaPlayerVisible, setMediaPlayerVisible] = useState(false); // Hide footer by default

  // Track if we're on mobile
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Auto-detect Spotify tracks and set spotifyTrackUri if track has spotifyUri but URI isn't set
  useEffect(() => {
    if (currentTrack?.spotifyUri && isSpotifyPremium) {
      // Check if this is a Spotify track that should use SpotifyPlayer
      // In scanned mode, all tracks with spotifyUri should use SpotifyPlayer
      const isSpotifyTrack = 
        currentTrack?.spotifyUri?.startsWith('spotify:track:') &&
        (isInScannedMode || // Scanned albums are always Spotify
         currentTrack?.album?.storageStatus === 'spotify' || 
         currentTrack?.metadataSource === 'barcode-scan' ||
         currentTrack?.spotifyUri);
      
      if (isSpotifyTrack) {
        // Always update spotifyTrackUri to match currentTrack.spotifyUri if they differ
        // This ensures track changes are reflected immediately
        if (spotifyTrackUri !== currentTrack.spotifyUri) {
          const newUri = currentTrack.spotifyUri;
          if (!newUri || !newUri.startsWith('spotify:track:')) {
            console.error('❌ Auto-detection: Invalid Spotify URI:', newUri, 'for track:', currentTrack?.Title);
          } else {
            console.log('🎵 Auto-updating spotifyTrackUri:', spotifyTrackUri, '→', newUri, 'isInScannedMode:', isInScannedMode);
            setSpotifyTrackUri(newUri);
          }
        }
        // Update currentTrack source if not already set
        if (currentTrack.source !== 'spotify') {
          console.log('🎵 Auto-fixing currentTrack.source to "spotify"');
          setCurrentTrack({
            ...currentTrack,
            source: 'spotify',
            uri: currentTrack.spotifyUri,
          });
        }
      }
    }
  }, [currentTrack, spotifyTrackUri, isSpotifyPremium, isInScannedMode]);

  // Debug: Log player rendering decision
  useEffect(() => {
    if (currentTrack) {
      console.log('🎯 Player Render Check:', {
        'currentTrack.source': currentTrack?.source,
        'spotifyTrackUri': spotifyTrackUri,
        'isSpotifyPremium': isSpotifyPremium,
        'currentTrack.spotifyUri': currentTrack?.spotifyUri,
        'willRenderSpotifyPlayer': currentTrack?.source === 'spotify' && spotifyTrackUri && isSpotifyPremium,
        'willRenderMediaPlayer': !currentTrack || currentTrack?.source !== 'spotify' || !spotifyTrackUri || !isSpotifyPremium
      });
    }
  }, [currentTrack, spotifyTrackUri, isSpotifyPremium]);

  // Mobile view mode: 'searching' (show nav only) or 'browsing' (show albums only)
  const [mobileViewMode, setMobileViewMode] = useState(() => {
    // On mobile, start in 'searching' mode (show nav only)
    return window.innerWidth <= 768 ? 'searching' : 'browsing';
  });

  // Track edit popup state
  const [showTrackEditPopup, setShowTrackEditPopup] = useState(false);
  const [selectedTrackForEdit, setSelectedTrackForEdit] = useState(null);
  const [selectedAlbumForEdit, setSelectedAlbumForEdit] = useState(null);

  // Global volume state (for gamepad control even when player hidden)
  // Note: globalVolume is set but not currently used
  const [, setGlobalVolume] = useState(1);

  // Open left drawer by default on mobile and set view mode
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setLeftDrawerOpen(true);
        // On mobile, start in 'searching' mode (show nav only)
        setMobileViewMode('searching');
      } else {
        // On desktop, always show both (browsing mode)
        setMobileViewMode('browsing');
      }
    };
    // Check immediately
    checkMobile();
    // Also check on resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check Spotify Premium status
  useEffect(() => {
    const checkPremium = async () => {
      if (!spotifyAuth.isAuthenticated()) {
        setIsSpotifyPremium(false);
        return;
      }

      try {
        const profile = await spotifyAuth.getUserProfile();
        setIsSpotifyPremium(profile.product === 'premium');
      } catch (err) {
        console.error('Error checking Spotify Premium status:', err);
        setIsSpotifyPremium(false);
      }
    };

    checkPremium();
  }, [isHipsterExpanded]); // Re-check when Hipster accordion is opened

  // Handle scanned barcode (UPC/EAN)
  const handleBarcodeScanned = useCallback(
    async (barcode) => {
      console.log('🔍 Processing barcode:', barcode);
      
      try {
        pushoverService.debug(`Looking up barcode: ${barcode}`);
        // Lookup metadata from MusicBrainz
        const lookupResult = await barcodeService.lookupBarcode(barcode);
        
        if (!lookupResult.success || !lookupResult.releases || lookupResult.releases.length === 0) {
          pushoverService.debug(`❌ No release found for barcode: ${barcode}`);
          alert(
            `No release found for barcode: ${barcode}\n\n` +
            `This barcode might be:\n` +
            `• Not in the MusicBrainz or Discogs database\n` +
            `• An incorrect or incomplete scan\n\n` +
            `Please try scanning again or check if the barcode is correct.`
          );
          return;
        }
        
        // Get the best match (first release)
        const release = barcodeService.getBestMatch(lookupResult);
        console.log('✅ Found release:', release);
        pushoverService.debug(`✅ Found: "${release.title}" by ${release.artist}`);
        
        // Check if this is a video/DVD release
        const isVideo = release.type === 'video' || release.isVideo === true;
        
        if (isVideo) {
          // Handle video/DVD releases - show info but can't play on Spotify
          const format = release.format || 'Video';
          const year = release.year || release.date || 'Unknown year';
          
          alert(
            `📹 Found video release: "${release.title}"\n\n` +
            `Format: ${format}\n` +
            `Year: ${year}\n` +
            `Source: ${release.source || 'Discogs'}\n\n` +
            `Note: Videos/DVDs cannot be played on Spotify (music only).\n\n` +
            `Barcode: ${barcode}`
          );
          return;
        }
        
        // For music releases, continue with Spotify search
        // Try multiple search query formats for better matching
        const albumTitle = release.title || '';
        const albumArtist = release.artist || '';
        
        let spotifyResults = null;
        let searchQuery = '';
        
        // Try multiple search formats in order of specificity
        const searchAttempts = [
          `album:"${albumTitle}" artist:"${albumArtist}"`, // Exact match with quotes
          `album:${albumTitle} artist:${albumArtist}`, // Exact match without quotes
          `${albumTitle} ${albumArtist}`, // Simple concatenation
          albumTitle, // Just album name
        ];
        
        for (let i = 0; i < searchAttempts.length; i++) {
          searchQuery = searchAttempts[i];
          console.log(`🔍 Searching Spotify (attempt ${i + 1}/${searchAttempts.length}):`, searchQuery);
          pushoverService.debug(`Searching Spotify (attempt ${i + 1}): ${searchQuery}`);
          
          try {
            spotifyResults = await spotifyAPI.search(searchQuery, ['album'], 10);
            
            if (spotifyResults.albums && spotifyResults.albums.length > 0) {
              console.log(`✅ Found ${spotifyResults.albums.length} result(s) on attempt ${i + 1}`);
              break; // Success, exit loop
            }
          } catch (searchError) {
            console.warn(`⚠️ Search attempt ${i + 1} failed:`, searchError);
            // Continue to next attempt
          }
        }
        
        if (!spotifyResults || !spotifyResults.albums || spotifyResults.albums.length === 0) {
          alert(
            `Found album: "${release.title}" by ${release.artist}\n\n` +
            `However, this album was not found on Spotify after trying multiple search formats.\n\n` +
            `Source: ${release.source || 'MusicBrainz'}\n` +
            `Barcode: ${barcode}\n\n` +
            `Note: The album may not be available on Spotify, or the title/artist names may differ.`
          );
          return;
        }
        
        // Get the first matching album
        const spotifyAlbum = spotifyResults.albums[0];
        console.log('✅ Found on Spotify:', spotifyAlbum);
        
        // Get album tracks
        const albumData = await spotifyAPI.getAlbumTracks(spotifyAlbum.id);
        console.log('✅ Album tracks:', albumData.tracks.length);
        
        // Check for duplicate and save album to Jookbox
        let saveMessage = '';
        let isDuplicate = false;
        try {
          console.log('💾 Checking for duplicate and saving album to Jookbox...');
          pushoverService.debug(`Checking for duplicate: ${release.title}`);
          
          // Check for duplicate first
          isDuplicate = await barcodeSaveService.checkDuplicate(release.artist || spotifyAlbum.artist, release.title || albumData.album.name);
          
          if (!isDuplicate) {
            const saveResult = await barcodeSaveService.saveScannedAlbum({
              barcode: barcode,
              release: release,
              spotifyAlbum: spotifyAlbum,
              albumData: albumData,
              userId: user?.uid || null,
            });
            
            if (saveResult.success) {
              console.log(`✅ Album saved to Jookbox: ${saveResult.artist} - ${saveResult.album}`);
              pushoverService.debug(`✅ Album saved: ${saveResult.album}`);
              saveMessage = `\n✅ Album saved to Jookbox library!`;
            } else {
              console.log(`⚠️ Save failed: ${saveResult.reason || 'unknown'}`);
              saveMessage = `\n⚠️ Could not save album to library.`;
            }
          } else {
            console.log(`⏭️ Album already exists in library: ${release.title}`);
            pushoverService.debug(`⏭️ Album already exists: ${release.title}`);
            saveMessage = `\n⏭️ Album already exists in library (skipped).`;
          }
        } catch (saveError) {
          console.error('❌ Error saving album to Jookbox:', saveError);
          pushoverService.debug(`Save error: ${saveError.message}`);
          saveMessage = `\n⚠️ Error saving to library: ${saveError.message}`;
          // Continue with playback even if save fails
        }
        
        // Play the first track - the SpotifyPlayer component will handle playback
        if (albumData.tracks.length > 0 && isSpotifyPremium) {
          const firstTrack = albumData.tracks[0];
          
          console.log(`🎵 Setting track URI: ${firstTrack.uri}`);
          console.log(`🎵 Track info:`, { name: firstTrack.name, artist: firstTrack.artist, uri: firstTrack.uri });
          
          // Clear the track URI first to force a reset, then set it again
          // This ensures the SpotifyPlayer component recognizes it as a new track
          setSpotifyTrackUri(null);
          setCurrentTrack(null);
          
          // Wait a brief moment for the reset to take effect, then set the new track
          setTimeout(() => {
            setSpotifyTrackUri(firstTrack.uri);
            setCurrentTrack({
              name: firstTrack.name,
              artist: firstTrack.artist,
              image: spotifyAlbum.image || albumData.album.image,
              source: 'spotify',
            });
            console.log(`✅ Track URI set for album: "${albumData.album.name}"`);
            pushoverService.debug(`Setting track: ${firstTrack.name} from ${albumData.album.name} (${albumData.tracks.length} tracks)`);
          }, 100);
          
          alert(
            `✅ Found album: "${albumData.album.name}" by ${albumData.album.artist}\n\n` +
            `${albumData.tracks.length} tracks\n\n` +
            `Playing first track: "${firstTrack.name}"${saveMessage}\n\n` +
            `Found via barcode: ${barcode}`
          );
        } else {
          // Show album info but can't play (no premium or no tracks)
          alert(
            `✅ Found album: "${albumData.album.name}" by ${albumData.album.artist}\n\n` +
            `${albumData.tracks.length} tracks found.\n\n` +
            `${!isSpotifyPremium ? 'Spotify Premium required for playback.' : 'Unable to play tracks.'}${saveMessage}\n\n` +
            `Barcode: ${barcode}`
          );
        }
        
      } catch (error) {
        console.error('❌ Barcode lookup error:', error);
        alert(`Error looking up barcode: ${error.message}\n\nPlease try again.`);
      }
    },
    [isSpotifyPremium, user]
  );

  // Handle scanned QR code - must be defined before QR scanner useEffect
  const handleQRCodeScanned = useCallback(
    (qrData) => {
      console.log('Processing QR code:', qrData);

      // Check if this is a barcode (numeric only, 6-14 digits for UPC/EAN/other formats)
      // Trim whitespace first in case scanner adds it
      const trimmedData = qrData.trim();
      // Barcode formats: UPC-A (12), EAN-13 (13), EAN-8 (8), UPC-E (6-8), others (6-14)
      const isNumericBarcode = /^\d{6,14}$/.test(trimmedData);
      
      const debugMsg = `Scanned: "${trimmedData}" (${trimmedData.length} chars) - Is barcode: ${isNumericBarcode}`;
      console.log('📋 Scanned data:', trimmedData, 'Length:', trimmedData.length, 'Is barcode?', isNumericBarcode);
      pushoverService.debug(debugMsg);
      
      if (isNumericBarcode) {
        console.log('🔍 Detected barcode format, processing as barcode...');
        pushoverService.debug(`Processing barcode: ${trimmedData}`);
        handleBarcodeScanned(trimmedData);
        return;
      }

      // Try to find the song by various methods
      let foundTrack = null;

      // Method 1: QR code contains Spotify track ID
      if (
        qrData.includes('spotify.com/track/') ||
        qrData.includes('spotify:track:')
      ) {
        const trackIdMatch = qrData.match(
          /(?:spotify\.com\/track\/|spotify:track:)([a-zA-Z0-9]+)/
        );
        if (trackIdMatch) {
          const trackId = trackIdMatch[1];
          foundTrack = hitsterTracks.find((track) => track.id === trackId);
        }
      }

      // Method 2: QR code contains track ID directly
      if (!foundTrack) {
        foundTrack = hitsterTracks.find((track) => track.id === qrData);
      }

      // Method 3: QR code contains track URI
      if (!foundTrack && qrData.startsWith('spotify:track:')) {
        const trackId = qrData.replace('spotify:track:', '');
        foundTrack = hitsterTracks.find((track) => track.id === trackId);
      }

      // Method 4: QR code contains song name or artist (fallback)
      if (!foundTrack) {
        const lowerQR = qrData.toLowerCase();
        foundTrack = hitsterTracks.find(
          (track) =>
            track.name?.toLowerCase().includes(lowerQR) ||
            track.artist?.toLowerCase().includes(lowerQR)
        );
      }

      if (foundTrack) {
        console.log('✅ Found matching track!');
        console.log('📀 Track details:', {
          name: foundTrack.name,
          artist: foundTrack.artist,
          id: foundTrack.id,
          uri: foundTrack.uri,
          hasPreview: !!foundTrack.previewUrl,
          isPremium: isSpotifyPremium,
        });

        // Stop any currently playing preview
        if (spotifyPreviewAudioRef.current) {
          spotifyPreviewAudioRef.current.pause();
          spotifyPreviewAudioRef.current = null;
        }

        // If user has Premium and track has URI, use SpotifyPlayer for full playback
        if (isSpotifyPremium && foundTrack.uri) {
          console.log(
            '🎵 Playing full track via SpotifyPlayer:',
            foundTrack.uri
          );
          // Set the URI directly - SpotifyPlayer will handle track switching internally
          setSpotifyTrackUri(foundTrack.uri);
          setCurrentTrack({
            name: foundTrack.name,
            artist: foundTrack.artist,
            image: foundTrack.image,
            source: 'spotify',
          });
          setIsPlaying(true);
        } else if (foundTrack.previewUrl) {
          console.log('🎵 Playing preview audio');
          // Fall back to preview if available
          const audio = new Audio(foundTrack.previewUrl);
          audio.play().catch((err) => {
            console.error('Error playing preview:', err);
            alert(
              'Unable to play preview. This track may not have a preview available.'
            );
          });

          spotifyPreviewAudioRef.current = audio;
          setCurrentTrack({
            ...foundTrack,
            previewUrl: foundTrack.previewUrl,
            source: 'spotify',
          });
          setIsPlaying(true);

          audio.onended = () => {
            setIsPlaying(false);
            spotifyPreviewAudioRef.current = null;
          };

          audio.onerror = () => {
            console.error('Error loading preview audio');
            setIsPlaying(false);
            spotifyPreviewAudioRef.current = null;
          };
        } else {
          console.warn('⚠️ Track found but no preview available');
          alert(
            `Found track: ${foundTrack.name} by ${foundTrack.artist}, but no preview is available.`
          );
        }
      } else {
        console.error('❌ Could not find matching track');
        console.log('📋 QR Code data received:', qrData);
        console.log('📋 QR Code full value:', JSON.stringify(qrData));
        console.log('📊 Total tracks in library:', hitsterTracks.length);
        console.log(
          '📊 Sample track IDs:',
          hitsterTracks
            .slice(0, 5)
            .map((t) => ({ id: t.id, name: t.name, artist: t.artist }))
        );
        alert(
          `Could not find a matching song for QR code: ${qrData}\n\nPlease make sure the QR code contains a valid Spotify track ID or song name.`
        );
      }
    },
    [hitsterTracks, isSpotifyPremium, handleBarcodeScanned]
  );

  // QR Code Scanner
  useEffect(() => {
    let html5QrCode = null;
    let isScanning = false;
    let isStopping = false; // Flag to prevent multiple simultaneous stop attempts
    let lastNotFoundLogTime = 0; // For throttling NotFoundException logs
    let scanAttemptCount = 0; // Track number of scan attempts
    const NOT_FOUND_LOG_INTERVAL = 2000; // Log "no QR code found" every 2 seconds

    // Helper function to safely stop the scanner
    const safeStopScanner = async () => {
      if (!html5QrCode || isStopping) return;

      isStopping = true;
      try {
        await html5QrCode.stop();
        html5QrCode.clear();
        isScanning = false;
        console.log('📷 QR Scanner stopped');
      } catch (err) {
        // Ignore "Cannot stop" errors - scanner might already be stopped or not running
        const errMsg = err?.message?.toLowerCase() || '';
        if (
          !errMsg.includes('cannot stop') &&
          !errMsg.includes('not running') &&
          !errMsg.includes('not paused')
        ) {
          console.error('Error stopping scanner:', err);
        }
        // Try to clear even if stop fails
        try {
          if (html5QrCode) {
            html5QrCode.clear();
          }
        } catch (clearErr) {
          // Ignore clear errors
        }
        isScanning = false;
      } finally {
        isStopping = false;
      }
    };

    const startScanner = async () => {
      if (!showQRScanner || !qrScannerRef.current || isScanning) return;

      try {
        const elementId = qrScannerRef.current.id || 'qr-reader';
        console.log('📷 Initializing QR Scanner...');
        console.log('📷 Scanner element ID:', elementId);
        html5QrCode = new Html5Qrcode(elementId);
        isScanning = true;
        scanAttemptCount = 0; // Reset counter when starting

        // Use full viewfinder for scanning (like native camera apps)
        // This function returns the full viewfinder dimensions for maximum detection area
        const qrBoxFunction = (viewfinderWidth, viewfinderHeight) => {
          // Use 100% of the viewfinder - no constraint on scanning area
          return {
            width: viewfinderWidth,
            height: viewfinderHeight,
          };
        };

        console.log('📷 Scanner configuration:', {
          facingMode: 'environment (back camera)',
          fps: 30,
          qrbox: 'full viewfinder (100%)',
          note: 'Scanning entire camera view like native camera apps',
        });

        await html5QrCode.start(
          { facingMode: 'environment' }, // Use back camera
          {
            fps: 30, // Higher fps for better detection
            qrbox: qrBoxFunction, // Scan entire viewfinder for maximum compatibility
          },
          async (decodedText) => {
            // QR code scanned successfully
            console.log('✅ QR Code scanned successfully!');
            console.log(
              `📊 Scan attempts before detection: ${scanAttemptCount}`
            );
            console.log('📋 QR Code data received:', decodedText);
            console.log('📋 QR Code type:', typeof decodedText);
            console.log('📋 QR Code length:', decodedText?.length);
            console.log(
              '📋 QR Code first 50 chars:',
              decodedText?.substring(0, 50)
            );

            // Stop scanner first before processing
            await safeStopScanner();
            html5QrCode = null;

            setShowQRScanner(false);

            // Process the QR code
            if (decodedText && decodedText.trim()) {
              handleQRCodeScanned(decodedText.trim());
            } else {
              console.warn('⚠️ Empty or invalid QR code data');
              alert('Invalid QR code. Please try scanning again.');
            }
          },
          (errorMessage) => {
            // Log scanning status for debugging
            if (errorMessage) {
              scanAttemptCount++;
              const isNotFound = errorMessage.includes('NotFoundException');
              const now = Date.now();

              if (isNotFound) {
                // Throttle NotFoundException logs - camera is working, just no QR code found yet
                if (now - lastNotFoundLogTime >= NOT_FOUND_LOG_INTERVAL) {
                  console.log(
                    `🔍 Camera active - scanning for QR code... (scan attempts: ${scanAttemptCount}, no QR code detected yet)`
                  );
                  console.log(
                    '💡 Tip: Make sure the QR code is clearly visible within the scanning area'
                  );
                  lastNotFoundLogTime = now;
                }
              } else {
                // Log other errors immediately (these are actual issues)
                console.log('📷 QR Scanner error/status:', errorMessage);
                console.log('📊 Total scan attempts so far:', scanAttemptCount);
              }
            }
          }
        );

        console.log(
          '✅ QR Scanner started successfully - camera is active and ready to scan'
        );
        console.log(
          '📱 Make sure the QR code is well-lit and clearly visible in the scanning area'
        );
      } catch (err) {
        console.error('❌ Error starting QR scanner:', err);
        isScanning = false;
        html5QrCode = null;

        // Provide more specific error messages
        let errorMessage = 'Failed to start camera. ';
        const errMsg = err?.message?.toLowerCase() || '';

        if (errMsg.includes('permission') || errMsg.includes('denied')) {
          errorMessage +=
            'Please allow camera access in your browser settings.';
        } else if (
          errMsg.includes('not found') ||
          errMsg.includes('no camera')
        ) {
          errorMessage += 'No camera found on this device.';
        } else if (
          errMsg.includes('in use') ||
          errMsg.includes('busy') ||
          errMsg.includes('already')
        ) {
          errorMessage +=
            'Camera is being used by another app. Please close other apps using the camera and try again.';
        } else {
          errorMessage +=
            'Please check permissions and ensure no other app is using the camera.';
        }

        alert(errorMessage);
        setShowQRScanner(false);
      }
    };

    const stopScanner = async () => {
      await safeStopScanner();
      html5QrCode = null;
      setShowQRScanner(false);
    };

    if (showQRScanner) {
      startScanner();
    }

    return () => {
      // Cleanup: stop scanner if it's running
      if (html5QrCode) {
        safeStopScanner().catch((err) => {
          // Ignore cleanup errors
          console.log('Cleanup: scanner already stopped');
        });
      }
    };
  }, [showQRScanner, handleQRCodeScanned]);

  // Fetch Hitster tracks from Firestore (or Spotify if not cached)
  useEffect(() => {
    const fetchHitsterPlaylist = async () => {
      if (!isHipsterExpanded) {
        return;
      }

      setHitsterLoading(true);
      setHitsterError(null);

      try {
        // First, try to load from Firestore cache
        const hitsterDocRef = doc(db, 'hitster-tracks', 'all-tracks');
        const hitsterDocSnap = await getDoc(hitsterDocRef);

        if (hitsterDocSnap.exists()) {
          const cachedData = hitsterDocSnap.data();
          const cachedTracks = cachedData.tracks || [];
          const lastUpdated = cachedData.lastUpdated;

          console.log(
            `✅ Loaded ${cachedTracks.length} Hitster tracks from Firestore cache (updated: ${lastUpdated})`
          );
          setHitsterTracks(cachedTracks);
          setHitsterLoading(false);
          return; // Use cached data, skip Spotify fetch
        }

        // If not in cache, fetch from Spotify (requires authentication)
        if (!spotifyAuth.isAuthenticated()) {
          setHitsterError(
            'Please connect to Spotify to load Hitster tracks for the first time.'
          );
          setHitsterLoading(false);
          return;
        }

        console.log('📡 Cache miss - fetching Hitster tracks from Spotify...');

        // First, try searching for Hitster playlist
        let hitsterPlaylist = null;
        const searchResults = await spotifyAPI.search(
          'Hitster',
          ['playlist'],
          20
        );
        hitsterPlaylist = searchResults.playlists?.find((p) =>
          p.name.toLowerCase().includes('hitster')
        );

        // If not found in search, check user's own playlists
        if (!hitsterPlaylist) {
          const userPlaylists = await spotifyAPI.getMyPlaylists(50);
          hitsterPlaylist = userPlaylists.items.find((p) =>
            p.name.toLowerCase().includes('hitster')
          );
        }

        if (!hitsterPlaylist) {
          setHitsterError(
            'Hitster playlist not found. Please make sure the playlist exists in Spotify and is named "Hitster".'
          );
          setHitsterLoading(false);
          return;
        }

        // Fetch all tracks from the playlist
        let allTracks = [];
        let offset = 0;
        const limit = 100;

        while (true) {
          const tracksData = await spotifyAPI.getPlaylistTracks(
            hitsterPlaylist.id,
            limit,
            offset
          );
          allTracks = [...allTracks, ...tracksData.items];

          if (!tracksData.next || tracksData.items.length < limit) {
            break;
          }
          offset += limit;
        }

        console.log(
          `✅ Loaded ${allTracks.length} tracks from Hitster playlist`
        );

        // Enhance tracks with original release years using metadata API
        // For compilation albums, fetch the original track release year from MusicBrainz/Last.fm
        const headers = await getAuthHeaders();
        const enhancedTracks = await Promise.all(
          allTracks.map(async (track) => {
            // If it's not a compilation, the album release date is accurate
            if (track.albumType !== 'compilation') {
              return track;
            }

            // For compilation albums, try to get original release year from metadata API
            try {
              const artistName = track.artist?.split(',')[0]?.trim(); // Use primary artist
              const trackName = track.name;

              if (artistName && trackName) {
                // Try MusicBrainz first for accurate release dates
                const mbResponse = await fetch(
                  `${API_BASE}/search-track-musicbrainz`,
                  {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      trackName,
                      artistName,
                      albumName: track.album,
                    }),
                  }
                );

                if (mbResponse.ok) {
                  const mbData = await mbResponse.json();
                  // API returns { success: true, data: { DisplayYear: ..., year: ..., ... } }
                  const year =
                    mbData.data?.DisplayYear ||
                    mbData.data?.year ||
                    mbData.year;
                  if (year) {
                    const yearNum =
                      typeof year === 'string' ? parseInt(year) : year;
                    if (!isNaN(yearNum)) {
                      console.log(
                        `✅ Found original year for ${trackName} by ${artistName}: ${yearNum} (from MusicBrainz)`
                      );
                      return {
                        ...track,
                        originalReleaseYear: yearNum,
                      };
                    }
                  }
                }

                // Fallback to Last.fm if MusicBrainz doesn't have it
                const lfResponse = await fetch(
                  `${API_BASE}/search-track-lastfm`,
                  {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      trackName,
                      artistName,
                    }),
                  }
                );

                if (lfResponse.ok) {
                  const lfData = await lfResponse.json();
                  // API returns { success: true, data: { DisplayYear: ..., year: ..., ... } }
                  const year =
                    lfData.data?.DisplayYear ||
                    lfData.data?.year ||
                    lfData.year;
                  if (year) {
                    const yearNum =
                      typeof year === 'string' ? parseInt(year) : year;
                    if (!isNaN(yearNum)) {
                      console.log(
                        `✅ Found original year for ${trackName} by ${artistName}: ${yearNum} (from Last.fm)`
                      );
                      return {
                        ...track,
                        originalReleaseYear: yearNum,
                      };
                    }
                  }
                }
              }
            } catch (err) {
              console.warn(
                `⚠️ Could not fetch original release year for ${track.name}:`,
                err
              );
            }

            // If we couldn't find original release year, return track as-is
            return track;
          })
        );

        // Store in Firestore cache for future use
        try {
          await setDoc(
            hitsterDocRef,
            {
              tracks: enhancedTracks,
              lastUpdated: new Date().toISOString(),
              trackCount: enhancedTracks.length,
              source: 'spotify',
            },
            { merge: false }
          ); // Use merge: false to overwrite completely
          console.log(
            `💾 Cached ${enhancedTracks.length} Hitster tracks to Firestore`
          );
        } catch (cacheErr) {
          console.warn(
            '⚠️ Could not cache Hitster tracks to Firestore:',
            cacheErr
          );
          // Continue anyway - tracks are still loaded
        }

        setHitsterTracks(enhancedTracks);
      } catch (err) {
        console.error('Error fetching Hitster playlist:', err);
        setHitsterError(err.message);
      } finally {
        setHitsterLoading(false);
      }
    };

    fetchHitsterPlaylist();
  }, [isHipsterExpanded]); // Removed spotifyAuth dependency - we check Firestore first

  // Track the last played random track URI to prevent restarting on flip
  const lastPlayedRandomTrackRef = useRef(null);

  // Auto-play random track when selected (only once per track)
  useEffect(() => {
    if (
      randomTrack &&
      randomTrack.uri &&
      randomTrack.uri !== lastPlayedRandomTrackRef.current
    ) {
      // Mark this track as played
      lastPlayedRandomTrackRef.current = randomTrack.uri;

      // Stop any currently playing preview
      if (spotifyPreviewAudioRef.current) {
        spotifyPreviewAudioRef.current.pause();
        spotifyPreviewAudioRef.current = null;
      }

      // If user has Premium and track has URI, use SpotifyPlayer for full playback
      if (isSpotifyPremium && randomTrack.uri) {
        console.log(
          '🎵 Playing random track via SpotifyPlayer:',
          randomTrack.uri
        );
        setSpotifyTrackUri(randomTrack.uri);
        setCurrentTrack({
          name: randomTrack.name,
          artist: randomTrack.artist,
          image: randomTrack.image,
          source: 'spotify',
        });
        setIsPlaying(true);
      } else if (randomTrack.previewUrl) {
        // Fall back to preview if available
        console.log('🎵 Playing random track preview:', randomTrack.previewUrl);
        const audio = new Audio(randomTrack.previewUrl);

        // Set up event handlers before playing
        audio.onended = () => {
          console.log('Preview audio ended');
          setIsPlaying(false);
          spotifyPreviewAudioRef.current = null;
        };
        audio.onerror = (e) => {
          console.error('Preview audio error:', e);
          setIsPlaying(false);
          spotifyPreviewAudioRef.current = null;
        };
        audio.oncanplay = () => {
          console.log('Preview audio can play');
        };
        audio.onloadstart = () => {
          console.log('Preview audio loading...');
        };

        spotifyPreviewAudioRef.current = audio;
        setCurrentTrack({
          ...randomTrack,
          previewUrl: randomTrack.previewUrl,
          source: 'spotify',
        });
        setIsPlaying(true);

        // Play the audio - this should work after user interaction (Go button click)
        audio
          .play()
          .then(() => {
            console.log('✅ Preview audio started playing');
          })
          .catch((err) => {
            console.error('❌ Error playing preview:', err);
            setIsPlaying(false);
            spotifyPreviewAudioRef.current = null;
          });
      } else {
        // No preview URL and no Premium - show message and offer to open in Spotify
        console.warn(
          '⚠️ Cannot play track - no preview URL and user is not Premium:',
          randomTrack
        );
        setCurrentTrack({
          name: randomTrack.name,
          artist: randomTrack.artist,
          image: randomTrack.image,
          source: 'spotify',
        });
        setIsPlaying(false);

        // Show alert with option to open in Spotify
        if (randomTrack.id || randomTrack.uri) {
          const spotifyUrl = randomTrack.uri
            ? `https://open.spotify.com/track/${randomTrack.uri.split(':')[2]}`
            : `https://open.spotify.com/track/${randomTrack.id}`;

          const openSpotify = window.confirm(
            `"${randomTrack.name}" by ${
              randomTrack.artist || 'Unknown Artist'
            }\n\n` +
              `This track doesn't have a preview available.\n\n` +
              `Would you like to open it in Spotify?`
          );

          if (openSpotify) {
            window.open(spotifyUrl, '_blank');
          }
        } else {
          alert(
            `"${randomTrack.name}" by ${
              randomTrack.artist || 'Unknown Artist'
            }\n\n` +
              `This track doesn't have a preview available. Please upgrade to Spotify Premium to play full tracks.`
          );
        }
      }
    }
  }, [randomTrack?.uri, isSpotifyPremium]); // Only depend on the URI, not the whole object

  // Helper function to normalize artist names (remove numbers/suffixes for matching)
  // e.g., "Elvis Presley 1" -> "Elvis Presley", "Elvis Presley 2" -> "Elvis Presley"
  const normalizeArtistName = (artistName) => {
    if (!artistName) return '';
    // Remove trailing numbers and spaces (e.g., "Elvis Presley 1" -> "Elvis Presley")
    return artistName.replace(/\s+\d+$/, '').trim();
  };

  // Organize Hitster tracks by Decade/Year/Artist/Song
  const organizeHitsterTracks = () => {
    const organized = {};

    hitsterTracks.forEach((track) => {
      // Extract year from album release date
      let year = null;

      if (track.albumReleaseDate) {
        // Extract year from release date (format: YYYY-MM-DD or YYYY)
        const yearMatch = track.albumReleaseDate.match(/^(\d{4})/);
        if (yearMatch) {
          year = parseInt(yearMatch[1]);
        }
      }

      // Fallback: try to extract from added date (when track was added to playlist)
      if (!year && track.addedAt) {
        const date = new Date(track.addedAt);
        year = date.getFullYear();
      }

      // Default to "Unknown Year" if no year found
      if (!year || isNaN(year)) {
        year = 'Unknown Year';
      }

      // Calculate decade
      const decade =
        year === 'Unknown Year'
          ? 'Unknown Decade'
          : `${Math.floor(year / 10) * 10}s`;

      // Get artist name
      const artist = track.artist || 'Unknown Artist';

      // Initialize structure
      if (!organized[decade]) {
        organized[decade] = {};
      }
      if (!organized[decade][year]) {
        organized[decade][year] = {};
      }
      if (!organized[decade][year][artist]) {
        organized[decade][year][artist] = [];
      }

      organized[decade][year][artist].push(track);
    });

    return organized;
  };

  // Toggle functions for nested accordions
  const toggleDecade = (decade) => {
    const newExpanded = new Set(expandedDecades);
    if (newExpanded.has(decade)) {
      newExpanded.delete(decade);
    } else {
      newExpanded.add(decade);
    }
    setExpandedDecades(newExpanded);
  };

  const toggleYear = (decade, year) => {
    const key = `${decade}-${year}`;
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedYears(newExpanded);
  };

  const toggleArtist = (decade, year, artist) => {
    const key = `${decade}-${year}-${artist}`;
    const newExpanded = new Set(expandedArtists);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedArtists(newExpanded);
  };

  // Handler for toggling artist selection
  const handleArtistToggle = (artistName) => {
    // Only accumulate if master playlist mode is enabled
    if (!isMasterPlaylistMode) return;

    setSelectedArtists((prev) => {
      // Check if artist (or variation) already exists
      const normalizedNew = normalizeArtistName(artistName);
      const existingIndex = prev.findIndex(
        (existing) => normalizeArtistName(existing) === normalizedNew
      );

      if (existingIndex >= 0) {
        // Remove artist if already selected (remove all variations)
        return prev.filter(
          (name, index) => normalizeArtistName(name) !== normalizedNew
        );
      } else {
        // Add artist if not selected
        return [...prev, artistName];
      }
    });
  };

  // Handler for "Done Searching" button - switches to browsing mode on mobile and enables filtering by selected artists
  const handleDoneSearching = async () => {
    console.log('🔍 Done Searching clicked');
    // Save the current navigation path so we can restore it when Back is clicked
    setSavedNavigationPath({ ...selectedPath });
    console.log('💾 Saved navigation path:', selectedPath);
    if (isMobile) {
      // On mobile, switch to browsing mode (hide nav, show albums)
      setMobileViewMode('browsing');
    } else {
      // On desktop, collapse the left panel
      toggleLeftPanelCollapse();
    }
    // Enable filtering by selectedArtists array
    setIsFilteringByArtists(true);
    // Uncheck Master Artist list checkbox
    setIsMasterPlaylistMode(false);
    // Collapse Navigation accordion
    console.log('📉 Collapsing Navigation accordion');
    setIsNavbarExpanded(false);
    // Show and expand Albums accordion to display selected albums
    console.log('📈 Expanding Albums accordion');
    setIsAlbumsExpanded(true);

    // Get DNP list for filtering
    const dnpList = await getDNPList();
    const dnpTrackKeys = new Set(dnpList.map((t) => t.trackKey));
    const isTrackInDNP = (track) => {
      const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${
        track.album?.album || ''
      }`;
      return dnpTrackKeys.has(trackKey);
    };

    // Check if there are queued albums - create playlist from them
    if (queuedAlbumIds.size > 0 && hierarchy && hierarchy.albums) {
      console.log(
        `📋 Creating playlist from ${queuedAlbumIds.size} queued album(s)`
      );

      // Find all queued albums from hierarchy
      const queuedAlbums = [];
      Object.keys(hierarchy.albums).forEach((artistKey) => {
        hierarchy.albums[artistKey].forEach((album) => {
          if (
            queuedAlbumIds.has(album.id) &&
            album.songs &&
            album.songs.length > 0
          ) {
            queuedAlbums.push(album);
          }
        });
      });

      if (queuedAlbums.length > 0) {
        console.log(
          `✅ Found ${queuedAlbums.length} queued album(s) with songs`
        );

        // Shuffle albums
        const shuffledAlbums = [...queuedAlbums].sort(
          () => Math.random() - 0.5
        );

        // Collect songs grouped by album for round-robin shuffling
        const songsByAlbum = new Map();
        shuffledAlbums.forEach((album) => {
          if (album.songs && album.songs.length > 0) {
            // Filter out unavailable tracks
            const availableSongs = album.songs.filter(
              (song) => song.available !== false
            );
            if (availableSongs.length > 0) {
              // Shuffle songs within each album
              const albumSongs = availableSongs
                .map((song) => ({
                  ...song,
                  album: album,
                }))
                .sort(() => Math.random() - 0.5);
              songsByAlbum.set(album.id, albumSongs);
            }
          }
        });

        // Round-robin shuffle: interleave songs from different albums
        const shuffled = [];
        const albumIds = Array.from(songsByAlbum.keys());
        let lastAlbumId = null;

        while (songsByAlbum.size > 0) {
          // Get albums that still have songs
          const availableAlbums = albumIds.filter(
            (id) => songsByAlbum.has(id) && songsByAlbum.get(id).length > 0
          );

          if (availableAlbums.length === 0) break;

          // Try to pick a different album than the last one
          let nextAlbumId;
          if (availableAlbums.length > 1 && lastAlbumId !== null) {
            const differentAlbums = availableAlbums.filter(
              (id) => id !== lastAlbumId
            );
            nextAlbumId =
              differentAlbums.length > 0
                ? differentAlbums[
                    Math.floor(Math.random() * differentAlbums.length)
                  ]
                : availableAlbums[0];
          } else {
            nextAlbumId =
              availableAlbums[
                Math.floor(Math.random() * availableAlbums.length)
              ];
          }

          // Take one song from this album (filter DNP tracks)
          const albumSongs = songsByAlbum.get(nextAlbumId);
          if (albumSongs && albumSongs.length > 0) {
            let song = albumSongs.shift();
            // Skip if track is in DNP list
            while (song && isTrackInDNP(song) && albumSongs.length > 0) {
              song = albumSongs.shift();
            }
            // Only add if not in DNP list
            if (song && !isTrackInDNP(song)) {
              shuffled.push(song);
              lastAlbumId = nextAlbumId;
            }

            // Remove album from map if empty
            if (albumSongs.length === 0) {
              songsByAlbum.delete(nextAlbumId);
            }
          }
        }

        console.log(
          `🎵 Creating playlist with ${shuffled.length} songs from ${queuedAlbums.length} queued albums (DNP tracks filtered)`
        );

        // Create and start playing the playlist
        if (shuffled.length > 0) {
          handleShufflePlay(shuffled);
        } else {
          console.warn('⚠️ No songs found in queued albums');
        }

        return; // Exit early if we processed queued albums
      } else {
        console.log('⚠️ No queued albums found with songs');
      }
    }

    // Autoplay: Shuffle and play filtered albums (original behavior for artist filtering)
    if (
      hierarchy &&
      hierarchy.albums &&
      selectedPath.type &&
      selectedArtists.length > 0
    ) {
      // Helper function to normalize artist names (remove numbers/suffixes)
      const normalizeArtistName = (name) => {
        if (!name) return '';
        return name.replace(/\s+\d+$/, '').trim();
      };

      // Get filtered albums based on selectedPath and selectedArtists
      const filteredAlbums = [];
      Object.keys(hierarchy.albums).forEach((artistKey) => {
        const [lang, genre, subgenre, artistFromKey] = artistKey.split('|');

        // Check if path matches
        const pathMatches =
          (!selectedPath.language || lang === selectedPath.language) &&
          (!selectedPath.genre || genre === selectedPath.genre) &&
          (!selectedPath.subgenre || subgenre === selectedPath.subgenre);

        if (pathMatches) {
          hierarchy.albums[artistKey].forEach((album) => {
            // Filter by type
            if (album.type !== selectedPath.type) return;

            // Filter by selected artists
            const albumArtist = album.artist || artistFromKey;
            const normalizedAlbumArtist = normalizeArtistName(albumArtist);
            const matchesSelectedArtist = selectedArtists.some(
              (selected) =>
                normalizeArtistName(selected) === normalizedAlbumArtist
            );

            if (
              matchesSelectedArtist &&
              album.songs &&
              album.songs.length > 0
            ) {
              filteredAlbums.push(album);
            }
          });
        }
      });

      if (filteredAlbums.length > 0) {
        // Shuffle albums
        const shuffledAlbums = [...filteredAlbums].sort(
          () => Math.random() - 0.5
        );

        // Collect songs grouped by album for round-robin shuffling
        const songsByAlbum = new Map();
        shuffledAlbums.forEach((album) => {
          if (album.songs && album.songs.length > 0) {
            // Shuffle songs within each album
            const albumSongs = album.songs
              .map((song) => ({
                ...song,
                album: album,
              }))
              .sort(() => Math.random() - 0.5);
            songsByAlbum.set(album.id, albumSongs);
          }
        });

        // Round-robin shuffle: interleave songs from different albums
        const shuffled = [];
        const albumIds = Array.from(songsByAlbum.keys());
        let lastAlbumId = null;

        while (songsByAlbum.size > 0) {
          // Get albums that still have songs
          const availableAlbums = albumIds.filter(
            (id) => songsByAlbum.has(id) && songsByAlbum.get(id).length > 0
          );

          if (availableAlbums.length === 0) break;

          // Try to pick a different album than the last one
          let nextAlbumId;
          if (availableAlbums.length > 1 && lastAlbumId !== null) {
            const differentAlbums = availableAlbums.filter(
              (id) => id !== lastAlbumId
            );
            nextAlbumId =
              differentAlbums.length > 0
                ? differentAlbums[
                    Math.floor(Math.random() * differentAlbums.length)
                  ]
                : availableAlbums[0];
          } else {
            nextAlbumId =
              availableAlbums[
                Math.floor(Math.random() * availableAlbums.length)
              ];
          }

          // Take one song from this album (filter DNP tracks)
          const albumSongs = songsByAlbum.get(nextAlbumId);
          if (albumSongs && albumSongs.length > 0) {
            let song = albumSongs.shift();
            // Skip if track is in DNP list
            while (song && isTrackInDNP(song) && albumSongs.length > 0) {
              song = albumSongs.shift();
            }
            // Only add if not in DNP list
            if (song && !isTrackInDNP(song)) {
              shuffled.push(song);
              lastAlbumId = nextAlbumId;
            }

            // Remove album from map if empty
            if (albumSongs.length === 0) {
              songsByAlbum.delete(nextAlbumId);
            }
          }
        }

        console.log(
          `🎵 Autoplay: Shuffling ${shuffled.length} songs from ${filteredAlbums.length} filtered albums (DNP tracks filtered)`
        );

        // Start playing
        if (shuffled.length > 0) {
          handleShufflePlay(shuffled);
        }
      }
    }
  };

  // Listen for gamepad volume changes globally
  useEffect(() => {
    const handleVolumeChange = (e) => {
      const delta = e.detail?.delta || 0;
      setGlobalVolume((prev) => {
        const newVol = Math.max(0, Math.min(1, prev + delta));
        console.log('🎮 Volume:', Math.round(newVol * 100) + '%');

        // Apply to any playing audio
        const audio = document.querySelector('audio');
        if (audio) audio.volume = newVol;

        // Show toast
        const existing = document.querySelector('.volume-toast');
        if (existing) existing.remove();
        const toast = document.createElement('div');
        toast.className = 'volume-toast';
        toast.textContent = `🔊 ${Math.round(newVol * 100)}%`;
        toast.style.cssText =
          'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(0,0,0,0.85);color:white;padding:1rem 2rem;border-radius:12px;font-size:1.5rem;font-weight:bold;z-index:99999;';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 800);

        return newVol;
      });
    };

    window.addEventListener('volumeChange', handleVolumeChange);
    return () => window.removeEventListener('volumeChange', handleVolumeChange);
  }, []);

  // Cleanup Spotify preview audio on unmount
  useEffect(() => {
    return () => {
      if (spotifyPreviewAudioRef.current) {
        spotifyPreviewAudioRef.current.pause();
        spotifyPreviewAudioRef.current = null;
      }
    };
  }, []);

  // Check if user needs to complete setup
  useEffect(() => {
    const checkSetupStatus = async () => {
      if (!user) return;

      // First check localStorage (fast)
      const localPrefs = localStorage.getItem(`jookbox_prefs_${user.uid}`);
      if (localPrefs) {
        try {
          const prefs = JSON.parse(localPrefs);
          setUserPreferences(prefs);
          if (prefs.setupCompleted) {
            setSetupChecked(true);
            return; // Already set up
          }
        } catch (e) {
          console.error('Error parsing local prefs:', e);
        }
      }

      // Then try Firebase
      try {
        const prefsDoc = await getDoc(doc(db, 'userPreferences', user.uid));
        if (prefsDoc.exists()) {
          const prefs = prefsDoc.data();
          setUserPreferences(prefs);
          // Save to localStorage for faster future access
          localStorage.setItem(
            `jookbox_prefs_${user.uid}`,
            JSON.stringify(prefs)
          );
          if (!prefs.setupCompleted) {
            setShowSetupWizard(true);
          }
        } else {
          // First time user - show wizard
          setShowSetupWizard(true);
        }
      } catch (error) {
        console.error('Error checking setup status:', error);
        // If Firebase fails and no local prefs, show wizard
        if (!localPrefs) {
          setShowSetupWizard(true);
        }
      }
      setSetupChecked(true);
    };

    checkSetupStatus();
  }, [user]);

  // Handle setup wizard completion
  const handleSetupComplete = async () => {
    setShowSetupWizard(false);
    // Reload user preferences
    try {
      const prefsDoc = await getDoc(doc(db, 'userPreferences', user.uid));
      if (prefsDoc.exists()) {
        setUserPreferences(prefsDoc.data());
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  // Load blocked artists from localStorage
  useEffect(() => {
    const blocked = localStorage.getItem('jookbox_blocked_artists');
    if (blocked) {
      setBlockedArtists(JSON.parse(blocked));
    }
  }, []);

  // Gamepad navigation - joystick flows naturally across all panels
  useEffect(() => {
    const handleNavigate = (e) => {
      const { direction } = e.detail;

      // Handle LEFT/RIGHT to flow between panels
      if (direction === 'left') {
        if (focusedPanel === 'right') {
          // Right → Middle
          setFocusedPanel('middle');
          console.log('🎮 Panel: RIGHT → MIDDLE');
          return;
        } else if (focusedPanel === 'middle') {
          // Middle → Left (expand if collapsed)
          if (leftPanelCollapsed) setLeftPanelCollapsed(false);
          setFocusedPanel('left');
          console.log('🎮 Panel: MIDDLE → LEFT');
          return;
        }
        // If already in left, let LeftAccordion handle it
      } else if (direction === 'right') {
        if (focusedPanel === 'left') {
          // Left → Middle (collapse left)
          setLeftPanelCollapsed(true);
          setFocusedPanel('middle');
          console.log('🎮 Panel: LEFT → MIDDLE');
          return;
        } else if (focusedPanel === 'middle' && !rightPanelCollapsed) {
          // Middle → Right
          setFocusedPanel('right');
          console.log('🎮 Panel: MIDDLE → RIGHT');
          return;
        }
        // If already in right or right panel is collapsed, stay
      }

      // Route UP/DOWN navigation to the focused panel
      if (direction === 'up' || direction === 'down') {
        if (focusedPanel === 'left') {
          // LeftAccordion handles its own navigation
          return;
        } else if (focusedPanel === 'middle') {
          window.dispatchEvent(
            new CustomEvent('gamepadAlbumNavigate', { detail: { direction } })
          );
        } else if (focusedPanel === 'right') {
          window.dispatchEvent(
            new CustomEvent('gamepadQueueNavigate', { detail: { direction } })
          );
        }
      }
    };

    window.addEventListener('gamepadNavigate', handleNavigate);

    return () => {
      window.removeEventListener('gamepadNavigate', handleNavigate);
    };
  }, [focusedPanel, leftPanelCollapsed, rightPanelCollapsed]);

  // Load user's saved playlists
  const loadSavedPlaylists = useCallback(
    async (retryCount = 0) => {
      if (!user) return;

      // Wait for auth.currentUser to be ready
      let currentUser = auth.currentUser;
      if (!currentUser) {
        // Wait a bit for auth to initialize
        await new Promise((resolve) => setTimeout(resolve, 500));
        currentUser = auth.currentUser;
      }

      if (!currentUser) {
        console.log('⏳ Waiting for auth.currentUser in loadSavedPlaylists...');
        if (retryCount < 3) {
          // Retry after a delay
          setTimeout(() => loadSavedPlaylists(retryCount + 1), 1000);
          return;
        } else {
          console.error(
            '❌ Auth not ready after retries in loadSavedPlaylists'
          );
          return;
        }
      }

      try {
        // Ensure auth token is ready
        const token = await currentUser.getIdToken();
        if (!token) {
          throw new Error('No auth token available');
        }

        const q = query(
          collection(db, 'playlists'),
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);
        const playlists = [];
        querySnapshot.forEach((doc) => {
          playlists.push({ id: doc.id, ...doc.data() });
        });

        // Sort by createdAt in JavaScript (no Firestore index needed)
        playlists.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setSavedPlaylists(playlists);
        console.log(`✓ Loaded ${playlists.length} playlist(s)`);
      } catch (error) {
        console.error('❌ Error loading playlists:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          user: user?.email,
          authUser: currentUser?.email,
        });

        // Handle permission errors with retry
        if (error.code === 'permission-denied') {
          console.error(
            '🔒 Permission denied. User might not be fully authenticated.'
          );
          if (retryCount < 2) {
            // Retry after a delay
            console.log(
              `🔄 Retrying in 1 second... (attempt ${retryCount + 1})`
            );
            setTimeout(() => loadSavedPlaylists(retryCount + 1), 1000);
            return;
          } else {
            console.error('❌ Failed to load playlists after retries');
          }
        } else {
          alert('Error loading playlists. Check console for details.');
        }
      }
    },
    [user]
  );

  // Load user's saved playlists on mount
  useEffect(() => {
    if (user) {
      loadSavedPlaylists();
    }
  }, [user, loadSavedPlaylists]);

  // Check for playlist to load from Members page
  useEffect(() => {
    const playlistData = sessionStorage.getItem('loadPlaylistFromMembers');
    if (playlistData) {
      try {
        const playlist = JSON.parse(playlistData);
        console.log('📥 Loading playlist from Members page:', playlist.name);
        handleLoadPlaylist(playlist);
        // Clear the stored playlist
        sessionStorage.removeItem('loadPlaylistFromMembers');
      } catch (error) {
        console.error('Error loading playlist from Members page:', error);
        sessionStorage.removeItem('loadPlaylistFromMembers');
      }
    }
  }, []); // Only run once on mount

  // Get Do Not Play (DNP) list from Firestore
  const getDNPList = useCallback(async () => {
    if (!user) return [];

    try {
      const dnpDocRef = doc(db, 'user-dnp-lists', user.uid);
      const dnpDoc = await getDoc(dnpDocRef);

      if (dnpDoc.exists()) {
        const data = dnpDoc.data();
        return data.tracks || [];
      }
      return [];
    } catch (error) {
      console.error('Error getting DNP list:', error);
      return [];
    }
  }, [user]);

  // Add track to Do Not Play (DNP) list
  const addTrackToDNPList = useCallback(
    async (track) => {
      if (!user) return;

      try {
        const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${
          track.album?.album || ''
        }`;
        const dnpDocRef = doc(db, 'user-dnp-lists', user.uid);
        const dnpDoc = await getDoc(dnpDocRef);

        let dnpTracks = [];
        if (dnpDoc.exists()) {
          dnpTracks = dnpDoc.data().tracks || [];
        }

        // Check if track is already in DNP list
        const exists = dnpTracks.some(
          (t) =>
            t.title === track.Title &&
            t.artist === track.album?.artist &&
            t.album === track.album?.album
        );

        if (!exists) {
          dnpTracks.push({
            title: track.Title,
            artist: track.album?.artist || '',
            album: track.album?.album || '',
            trackKey: trackKey,
            addedAt: new Date().toISOString(),
          });

          await setDoc(
            dnpDocRef,
            {
              userId: user.uid,
              userEmail: user.email,
              tracks: dnpTracks,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );

          console.log('✅ Added track to DNP list:', trackKey);
        }
      } catch (error) {
        console.error('Error adding track to DNP list:', error);
      }
    },
    [user]
  );

  // Create master playlist from selected Jookbox library playlists
  const handleCreateMasterPlaylist = useCallback(
    async (selectedPlaylistIds = null, appendOptions = null) => {
      // If live save mode is enabled, set up the destination playlist ID immediately
      if (appendOptions && appendOptions.isLiveSave && appendOptions.appendToPlaylistId) {
        setLiveSavePlaylistId(appendOptions.appendToPlaylistId);
        setLiveSaveMode(appendOptions.isAppending ? 'append' : 'create');
        console.log('✅ Live save enabled for destination playlist:', appendOptions.appendToPlaylistId);
        
        // Fetch playlist info for display
        try {
          const playlistDocRef = doc(db, 'playlists', appendOptions.appendToPlaylistId);
          const playlistDocSnap = await getDoc(playlistDocRef);
          if (playlistDocSnap.exists()) {
            const playlistData = playlistDocSnap.data();
            setLiveSavePlaylistInfo({
              name: playlistData.name || 'Unknown Playlist',
              trackCount: playlistData.trackCount || (playlistData.tracks || []).length,
            });
            console.log('✅ Loaded playlist info for display:', playlistData.name, 'with', playlistData.trackCount || (playlistData.tracks || []).length, 'tracks');
          }
        } catch (error) {
          console.error('❌ Error loading playlist info:', error);
        }
      }
      if (!user) {
        alert('You must be logged in to create a playlist');
        return;
      }

      setIsCreatingMaster(true);

      try {
        // Get DNP list
        const dnpList = await getDNPList();
        const dnpTrackKeys = new Set(dnpList.map((t) => t.trackKey));

        // Helper to check if track is in DNP list
        const isTrackInDNP = (track) => {
          const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${
            track.album?.album || ''
          }`;
          return dnpTrackKeys.has(trackKey);
        };

        // Fetch selected playlists from Firestore
        console.log('📋 Fetching selected playlists...');
        let allPlaylists = [];
        let masterPlaylistName = 'Playlist';
        let masterPlaylistCreator = user.email || user.uid || 'Unknown';
        let firstOtherMemberPlaylist = null; // Track first playlist from another member

        if (selectedPlaylistIds && selectedPlaylistIds.length > 0) {
          // Fetch specific selected playlists
          const playlistPromises = selectedPlaylistIds.map((playlistId) =>
            getDoc(doc(db, 'playlists', playlistId))
          );
          const playlistDocs = await Promise.all(playlistPromises);
          playlistDocs.forEach((docSnap) => {
            if (docSnap.exists()) {
              const playlistData = { id: docSnap.id, ...docSnap.data() };
              allPlaylists.push(playlistData);

              // Check if this playlist is from another member (not the current user)
              const playlistUserId =
                playlistData.userId ||
                playlistData.userEmail ||
                playlistData.createdBy;
              const isFromOtherMember =
                playlistUserId &&
                playlistUserId !== user.uid &&
                playlistUserId !== user.email;

              // Prioritize playlists from other members for metadata
              if (isFromOtherMember && !firstOtherMemberPlaylist) {
                firstOtherMemberPlaylist = playlistData;
              }

              // Use the first playlist's info as fallback if no other member playlists found
              if (allPlaylists.length === 1 && !firstOtherMemberPlaylist) {
                masterPlaylistName = playlistData.name || 'Playlist';
                masterPlaylistCreator =
                  playlistData.userEmail ||
                  playlistData.userId ||
                  playlistData.createdBy ||
                  user.email ||
                  user.uid ||
                  'Unknown';
              }
            }
          });

          // If we found a playlist from another member, use that for metadata
          if (firstOtherMemberPlaylist) {
            masterPlaylistName =
              firstOtherMemberPlaylist.name || 'Playlist';
            masterPlaylistCreator =
              firstOtherMemberPlaylist.userEmail ||
              firstOtherMemberPlaylist.userId ||
              firstOtherMemberPlaylist.createdBy ||
              'Unknown';
          }
        } else {
          // Fallback: Fetch all user's playlists (backward compatibility)
          const q = query(
            collection(db, 'playlists'),
            where('userId', '==', user.uid)
          );
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            allPlaylists.push({ id: doc.id, ...doc.data() });
          });
        }

        if (allPlaylists.length === 0) {
          alert('No playlists found. Please create some playlists first.');
          setIsCreatingMaster(false);
          return;
        }

        console.log(
          `📋 Found ${allPlaylists.length} playlists, combining tracks...`
        );

        // Combine tracks from all playlists
        const allTracks = [];
        const trackMap = new Map(); // Use Map to deduplicate tracks

        for (const playlist of allPlaylists) {
          if (playlist.tracks && Array.isArray(playlist.tracks)) {
            for (const track of playlist.tracks) {
              // Create a unique key for the track
              const trackKey = `${track.Title || ''}_${
                track.album?.artist || ''
              }_${track.album?.album || ''}`;

              // Skip if already added or in DNP list
              if (trackMap.has(trackKey)) continue;

              // Check if track is in DNP list
              if (!isTrackInDNP(track)) {
                trackMap.set(trackKey, track);
                allTracks.push(track);
              }
            }
          }
        }

        if (allTracks.length === 0) {
          alert(
            'No tracks found in your playlists (or all tracks are in Do Not Play list).'
          );
          setIsCreatingMaster(false);
          return;
        }

        // Shuffle tracks
        const shuffled = [...allTracks];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        // If appending to existing playlist WITHOUT live save, update it immediately
        // If live save is enabled, skip automatic addition and go to swipe mode
        if (appendOptions && appendOptions.isAppending && appendOptions.appendToPlaylistId && !appendOptions.isLiveSave) {
          try {
            const playlistDocRef = doc(db, 'playlists', appendOptions.appendToPlaylistId);
            const playlistDocSnap = await getDoc(playlistDocRef);
            
            if (!playlistDocSnap.exists()) {
              alert('Selected playlist not found');
              setIsCreatingMaster(false);
              return;
            }

            const existingPlaylist = playlistDocSnap.data();
            const existingTracks = existingPlaylist.tracks || [];
            
            // Combine tracks (avoid duplicates based on track key)
            const trackMap = new Map();
            existingTracks.forEach(track => {
              const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${track.album?.album || ''}`;
              trackMap.set(trackKey, track);
            });

            // Add new tracks (skip duplicates)
            shuffled.forEach(track => {
              const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${track.album?.album || ''}`;
              if (!trackMap.has(trackKey)) {
                trackMap.set(trackKey, track);
              }
            });

            const combinedTracks = Array.from(trackMap.values());

            await updateDoc(playlistDocRef, {
              tracks: combinedTracks,
              trackCount: combinedTracks.length,
              updatedAt: new Date().toISOString(),
            });

            alert(
              `✅ Added ${shuffled.length} song(s) to "${existingPlaylist.name}"! Total: ${combinedTracks.length} songs.`
            );
            setIsCreatingMaster(false);
            return;
          } catch (error) {
            console.error('Error appending to playlist:', error);
            alert(`Failed to append to playlist: ${error.message}`);
            setIsCreatingMaster(false);
            return;
          }
        }
        
        // If live save is enabled, skip automatic addition and proceed to swipe mode
        // The tracks will be added one by one as the user swipes/keeps them

        // Prepare swipe queue
        const swipeQueueData = shuffled.map((track) => ({
          ...track,
          kept: false,
          rejected: false,
        }));

        setSwipeQueue(swipeQueueData);
        setSwipeMode('preview'); // Set to preview mode
        setIsSwipeModeActive(true);
        setShowPlaylists(false);
        setIsPlaying(false);
        setIsNavbarExpanded(false);
        setIsAlbumsExpanded(true);
        // Set playlist metadata for display - use first playlist's info
        setCurrentPlaylist({
          name: masterPlaylistName,
          description: `Combined from ${
            selectedPlaylistIds
              ? selectedPlaylistIds.length
              : allPlaylists.length
          } playlist${
            (selectedPlaylistIds
              ? selectedPlaylistIds.length
              : allPlaylists.length) !== 1
              ? 's'
              : ''
          }`,
          createdBy: masterPlaylistCreator,
          createdAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error creating playlist:', error);
        alert(`Failed to create playlist: ${error.message}`);
      } finally {
        setIsCreatingMaster(false);
      }
    },
    [user, getDNPList]
  );

  const handlePathChange = (path) => {
    setSelectedPath(path);
    // Clear album selection when path changes
    setSelectedAlbum(null);
    // Don't auto-close drawer - let user click "Done" button

    // Set scanned mode flag based on subgenre
    setIsInScannedMode(path?.subgenre === 'Scanned');

    // Artists are now ONLY added when selected from the purple container buttons
    // No automatic collection from navigation
  };

  const handleAlbumSelect = async (album) => {
    console.log('🎵 handleAlbumSelect called with album:', {
      id: album?.id,
      album: album?.album || album?.albumName,
      artist: album?.artist || album?.artistName,
      songsCount: album?.songs?.length,
      hasSongs: !!album?.songs,
      isArray: Array.isArray(album?.songs),
      collection: album?.collection,
    });

    // Try to reload album from Firestore to ensure we have the songs array
    let fullAlbum = album;
    if (album?.id) {
      try {
        const collections = [
          album.collection || 'albums-flat',
          'albums-flat',
          'JBXX',
        ];
        for (const collection of collections) {
          try {
            const albumDoc = await getDoc(doc(db, collection, album.id));
            if (albumDoc.exists()) {
              const data = albumDoc.data();
              fullAlbum = { id: albumDoc.id, collection, ...data };
              console.log(`✅ Reloaded album from ${collection}:`, {
                id: fullAlbum.id,
                songsCount: fullAlbum.songs?.length,
                hasSongs: !!fullAlbum.songs,
                isArray: Array.isArray(fullAlbum.songs),
              });

              // If we found an album with songs, use it
              if (
                fullAlbum.songs &&
                Array.isArray(fullAlbum.songs) &&
                fullAlbum.songs.length > 0
              ) {
                break;
              }
            }
          } catch (err) {
            console.warn(`Error loading from ${collection}:`, err.message);
          }
        }
      } catch (error) {
        console.error('Error reloading album:', error);
        // Use original album if reload fails
      }
    }

    setSelectedAlbum(fullAlbum);
    // Open right drawer on mobile when album is selected
    if (window.innerWidth <= 768) {
      setRightDrawerOpen(true);
    } else {
      // Expand right panel on desktop when album is selected
      setRightPanelCollapsed(false);
    }

    // Auto-play the album when selected (not in Queue mode)
    if (
      fullAlbum &&
      fullAlbum.songs &&
      fullAlbum.songs.length > 0 &&
      fullAlbum.storageStatus !== 'unavailable'
    ) {
      // Create tracks and enrich DisplayTitle from JBXX if needed
      const albumQueue = await Promise.all(
        fullAlbum.songs.map(async (song, index) => {
          const displayTitle = await enrichSongDisplayTitle(song, album);

          const track = {
            ...song,
            DisplayTitle: displayTitle, // Use enriched DisplayTitle
            album: fullAlbum,
          };

          // Debug: Log first track
          if (index === 0) {
            console.log('🔍 Creating track from album.songs:', {
              originalDisplayTitle: song.DisplayTitle,
              enrichedDisplayTitle: displayTitle,
              title: song.Title,
            });
          }

          return track;
        })
      );
      
      const firstTrack = albumQueue[0];
      const isSpotifyTrack = firstTrack?.spotifyUri && (
        isInScannedMode || // Scanned albums are always Spotify
        fullAlbum?.storageStatus === 'spotify' ||
        firstTrack?.metadataSource === 'barcode-scan'
      );
      
      setShuffleQueue(albumQueue);
      setCurrentTrackIndex(0);
      
      if (isSpotifyTrack && isSpotifyPremium) {
        // Use SpotifyPlayer for Spotify tracks
        console.log('🎵 Auto-playing Spotify album track:', firstTrack.spotifyUri);
        setSpotifyTrackUri(firstTrack.spotifyUri);
        setCurrentTrack({
          ...firstTrack,
          album: fullAlbum,
          source: 'spotify',
          uri: firstTrack.spotifyUri,
        });
      } else if (isSpotifyTrack && !isSpotifyPremium) {
        // Spotify track but no premium - show error and don't auto-play
        console.warn('⚠️ Spotify Premium required for scanned albums');
        setCurrentTrack({ ...firstTrack, album: fullAlbum });
        setIsPlaying(false);
        alert('Spotify Premium is required to play tracks from scanned albums.');
      } else {
        // Local track - clear Spotify URI and use MediaPlayer
        if (spotifyTrackUri) {
          setSpotifyTrackUri(null);
        }
        setCurrentTrack({ ...firstTrack, album: fullAlbum });
      }
      
      setIsPlaying(true);
      console.log(
        `▶️ Auto-playing album: ${fullAlbum.album || fullAlbum.albumName} (${
          albumQueue.length
        } tracks)`
      );
    }
  };

  const handlePlayTrack = (track, album) => {
    console.log('=== handlePlayTrack CALLED ===');
    console.log('Track Title:', track?.Title);
    console.log('Scanned Mode Flag:', isInScannedMode);
    console.log('Track spotifyUri:', track?.spotifyUri);
    console.log('Track spotifyId:', track?.spotifyId);
    
    // Stop any Spotify preview that might be playing
    if (spotifyPreviewAudioRef.current) {
      spotifyPreviewAudioRef.current.pause();
      spotifyPreviewAudioRef.current = null;
    }

    // Get spotifyUri - use existing or construct from spotifyId
    let spotifyUri = track?.spotifyUri;
    if (!spotifyUri && track?.spotifyId) {
      spotifyUri = `spotify:track:${track.spotifyId}`;
      console.log('Constructed URI from spotifyId:', spotifyUri);
    }

    // Check if this is a Spotify track
    // If in scanned mode, always use SpotifyPlayer if track has spotifyUri or spotifyId
    const hasSpotifyData = spotifyUri || track?.spotifyId;
    const isSpotifyTrack = hasSpotifyData && (
      isInScannedMode || // Scanned albums always use Spotify
      album?.storageStatus === 'spotify' || 
      track?.metadataSource === 'barcode-scan' ||
      spotifyUri?.startsWith('spotify:track:')
    );
    
    console.log('🎵 handlePlayTrack:', {
      trackTitle: track?.Title,
      hasSpotifyUri: !!track?.spotifyUri,
      hasSpotifyId: !!track?.spotifyId,
      spotifyUri: spotifyUri || track?.spotifyUri,
      constructedUri: spotifyUri,
      albumStorageStatus: album?.storageStatus,
      trackMetadataSource: track?.metadataSource,
      isSpotifyTrack,
      isSpotifyPremium,
      isInScannedMode,
      willUseSpotifyPlayer: isSpotifyTrack && isSpotifyPremium
    });
    
    if (isSpotifyTrack && isSpotifyPremium && spotifyUri) {
      // Use SpotifyPlayer for Spotify tracks
      console.log('🎵 Playing Spotify track:', {
        trackTitle: track?.Title,
        trackNumber: track?.trackNumber,
        spotifyUri: spotifyUri,
        spotifyId: track?.spotifyId,
        hasValidUri: !!spotifyUri && spotifyUri.startsWith('spotify:track:')
      });
      if (!spotifyUri || !spotifyUri.startsWith('spotify:track:')) {
        console.error('❌ Invalid Spotify URI:', spotifyUri);
        alert(`Invalid Spotify URI for track "${track?.Title}". Cannot play.`);
        return;
      }
      setSpotifyTrackUri(spotifyUri);
      setCurrentTrack({
        ...track,
        album,
        source: 'spotify',
        uri: spotifyUri,
        spotifyUri: spotifyUri, // Ensure spotifyUri is set on the track object
      });
      setIsPlaying(true);
    } else if (isSpotifyTrack && !isSpotifyPremium) {
      // Spotify track but no premium - show error
      alert('Spotify Premium is required to play tracks from scanned albums.');
      return;
    } else {
      // Local track - clear Spotify URI and use MediaPlayer
      if (spotifyTrackUri) {
        setSpotifyTrackUri(null);
      }
      setCurrentTrack({ ...track, album });
      setIsPlaying(true);
    }
    
    // Expand right panel when play action is triggered
    setRightPanelCollapsed(false);
  };

  const handlePause = () => {
    // Stop Spotify preview audio if playing
    if (spotifyPreviewAudioRef.current) {
      spotifyPreviewAudioRef.current.pause();
      spotifyPreviewAudioRef.current = null;
    }
    // Clear Spotify track URI if using SpotifyPlayer
    if (spotifyTrackUri) {
      setSpotifyTrackUri(null);
    }
    setIsPlaying(false);
  };

  const handlePlayAlbum = (album, shuffle = false) => {
    console.log(
      `🎵 ${shuffle ? 'Shuffle' : 'Play All'} clicked for album:`,
      album
    );
    console.log('   Songs count:', album?.songs?.length);

    if (album.songs && album.songs.length > 0) {
      // Filter out unavailable tracks
      const availableSongs = album.songs.filter(
        (song) => song.available !== false
      );
      if (availableSongs.length === 0) {
        console.log('   Album has no available tracks');
        return;
      }
      // Create a queue from the album's songs
      let albumQueue = availableSongs.map((song) => ({
        ...song,
        album: album,
      }));

      // Shuffle if requested
      if (shuffle) {
        albumQueue = [...albumQueue].sort(() => Math.random() - 0.5);
        console.log('   🔀 Queue shuffled');
      }

      console.log('   Created queue with', albumQueue.length, 'tracks');

      // Update the queue and play the first track
      const firstTrack = albumQueue[0];
      const isSpotifyTrack = firstTrack?.spotifyUri && (
        isInScannedMode || // Scanned albums are always Spotify
        album?.storageStatus === 'spotify' ||
        firstTrack?.metadataSource === 'barcode-scan'
      );
      
      setShuffleQueue(albumQueue);
      setCurrentTrackIndex(0);
      
      if (isSpotifyTrack && isSpotifyPremium) {
        // Use SpotifyPlayer for Spotify tracks
        console.log('🎵 Playing Spotify album track:', firstTrack.spotifyUri);
        setSpotifyTrackUri(firstTrack.spotifyUri);
        setCurrentTrack({
          ...firstTrack,
          album,
          source: 'spotify',
          uri: firstTrack.spotifyUri,
        });
      } else if (isSpotifyTrack && !isSpotifyPremium) {
        // Spotify track but no premium - show error
        alert('Spotify Premium is required to play tracks from scanned albums.');
        return;
      } else {
        // Local track - clear Spotify URI and use MediaPlayer
        if (spotifyTrackUri) {
          setSpotifyTrackUri(null);
        }
        setCurrentTrack({ ...firstTrack, album });
      }
      
      setIsPlaying(true);
      // Expand right panel when play action is triggered
      setRightPanelCollapsed(false);
    } else {
      console.log('   ❌ No songs in album or songs array is empty');
    }
  };

  const handleAddToQueue = (album, shouldRemove = false) => {
    if (shouldRemove) {
      // Remove album from queued set only (don't modify playback queue until Done Searching)
      console.log(`➖ Removing from Queue:`, album.album);
      setQueuedAlbumIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(album.id);
        return newSet;
      });
      console.log(
        `   Album removed from queue (playlist will be updated when Done Searching is clicked)`
      );
    } else {
      // Add album to queued set only (don't add songs to playback queue yet)
      console.log(`➕ Add to Queue clicked for album:`, album.album);
      setQueuedAlbumIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(album.id);
        return newSet;
      });
      console.log(
        `   Album marked as queued (playlist will be created when Done Searching is clicked)`
      );
    }
  };

  // Query Musicbrainz for queued albums and populate metadata
  const handleQueryMusicbrainz = async (queuedAlbums, selectedPath = null) => {
    if (!queuedAlbums || queuedAlbums.length === 0) {
      alert('No albums in queue to query');
      return;
    }

    const confirmMessage = `Query Musicbrainz for ${queuedAlbums.length} album(s)?\n\nThis will:\n- Search Musicbrainz for each album\n- Populate DisplayArtist, DisplayAlbum, DisplayTitle, Year, and Length\n- Update the albums in Firestore`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const results = [];
    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < queuedAlbums.length; i++) {
        const album = queuedAlbums[i];
        // Use album path information to better identify the album
        const albumName = album.album || album.albumName || '';
        const artistName = album.artist || album.artistName || '';
        const albumId = album.id || album.firestoreId;

        // Extract path information from album or selectedPath
        const language = album.language || selectedPath?.language || '';
        const genre = album.genre || selectedPath?.genre || '';
        const subgenre = album.subgenre || selectedPath?.subgenre || '';
        const type = album.type || selectedPath?.type || '';

        // Build path context for better identification
        const pathContext = {
          language,
          genre,
          subgenre,
          type,
          artist: artistName,
        };

        console.log(
          `\n🎵 Processing album ${i + 1}/${
            queuedAlbums.length
          }: "${albumName}" by "${artistName}"`
        );
        console.log(
          `   📍 Path context: ${type}/${language}/${genre}/${subgenre}/${artistName}`
        );

        if (!albumId) {
          console.error(`   ❌ Album missing ID, skipping`);
          results.push({
            album: albumName,
            artist: artistName,
            status: 'error',
            message: 'Missing album ID',
          });
          errorCount++;
          continue;
        }

        try {
          // Step 1: Match album to Musicbrainz
          // Include path context to help identify the exact album
          const headers = await getAuthHeaders();
          const matchResponse = await fetch(`${API_BASE}/match-single-album`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              firestoreId: albumId,
              fromCollection: 'albums-flat',
              collection: 'JBXX',
              // Include path context for better matching
              pathContext: pathContext,
            }),
          });

          if (!matchResponse.ok) {
            throw new Error(`Match API error: ${matchResponse.statusText}`);
          }

          const matchData = await matchResponse.json();

          if (!matchData.matches || matchData.matches.length === 0) {
            console.log(`   ⚠️ No Musicbrainz matches found`);
            results.push({
              album: albumName,
              artist: artistName,
              status: 'no_match',
              message: 'No Musicbrainz matches found',
            });
            errorCount++;
            continue;
          }

          // Use the first match (highest confidence)
          const bestMatch = matchData.matches[0];
          const mbid = bestMatch.mbRelease?.mbid;

          if (!mbid) {
            console.log(`   ⚠️ Match found but no MBID`);
            results.push({
              album: albumName,
              artist: artistName,
              status: 'error',
              message: 'Match found but no MBID',
            });
            errorCount++;
            continue;
          }

          console.log(
            `   ✅ Found match: ${bestMatch.mbRelease.album} (MBID: ${mbid})`
          );

          // Step 2: Execute update
          const updateResponse = await fetch(`${API_BASE}/execute-update`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              mbid: mbid,
              firestoreId: albumId,
              collection: 'JBXX',
              syncToFlat: true,
            }),
          });

          if (!updateResponse.ok) {
            throw new Error(`Update API error: ${updateResponse.statusText}`);
          }

          const updateData = await updateResponse.json();

          if (updateData.success) {
            console.log(
              `   ✅ Successfully updated: ${
                updateData.updated?.songsUpdated || 0
              } songs`
            );
            results.push({
              album: albumName,
              artist: artistName,
              status: 'success',
              message: `Updated ${updateData.updated?.songsUpdated || 0} songs`,
              mbAlbum: bestMatch.mbRelease.album,
              mbArtist: bestMatch.mbRelease.artist,
              year: bestMatch.mbRelease.year,
            });
            successCount++;
          } else {
            throw new Error(updateData.error || 'Update failed');
          }

          // Add a small delay to avoid rate limiting
          if (i < queuedAlbums.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error(`   ❌ Error processing album:`, error);
          results.push({
            album: albumName,
            artist: artistName,
            status: 'error',
            message: error.message,
          });
          errorCount++;
        }
      }

      // Show results summary
      const summary =
        `Musicbrainz Query Complete!\n\n` +
        `✅ Success: ${successCount}\n` +
        `❌ Errors/No Match: ${errorCount}\n\n` +
        `Results:\n${results
          .map(
            (r) =>
              `  ${r.status === 'success' ? '✅' : '❌'} ${r.album} - ${
                r.artist
              }: ${r.message}`
          )
          .join('\n')}`;

      alert(summary);
      console.log('📊 Query Results:', results);

      // Reload hierarchy to show updated data
      if (successCount > 0) {
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ Error in Musicbrainz query:', error);
      alert(`Error querying Musicbrainz: ${error.message}`);
    }
  };

  // Query Spotify for queued albums
  const handleQuerySpotify = async (queuedAlbums, selectedPath = null) => {
    if (!queuedAlbums || queuedAlbums.length === 0) {
      alert('No albums in queue to query');
      return;
    }

    // Check if user is authenticated with Spotify
    if (!spotifyAuth.isAuthenticated()) {
      const authMessage =
        `🔐 Spotify Authentication Required!\n\n` +
        `You need to authenticate with Spotify before you can query albums.\n\n` +
        `To authenticate:\n` +
        `1. Go to the Services page (/services)\n` +
        `2. Click "Connect Spotify"\n` +
        `3. Authorize the app\n` +
        `4. Then return here to query albums\n\n` +
        `Would you like to go to the Services page now to authenticate?`;

      if (window.confirm(authMessage)) {
        // Navigate to Services page where user can authenticate
        navigate('/services');
        return;
      } else {
        return;
      }
    }

    const confirmMessage = `Query Spotify for ${queuedAlbums.length} album(s)?\n\nThis will search Spotify for album metadata and display results.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const results = [];
    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < queuedAlbums.length; i++) {
        const album = queuedAlbums[i];
        const albumName = album.album || album.albumName || '';
        const artistName = album.artist || album.artistName || '';

        console.log(
          `\n🟢 Processing album ${i + 1}/${
            queuedAlbums.length
          }: "${albumName}" by "${artistName}"`
        );

        try {
          // Search Spotify for the album
          // Try multiple query formats for better matching
          let searchQuery = '';
          if (artistName && albumName) {
            // Format 1: Exact album and artist
            searchQuery = `album:${albumName} artist:${artistName}`;
          } else if (albumName) {
            searchQuery = albumName;
          } else if (artistName) {
            searchQuery = artistName;
          } else {
            throw new Error('Missing album name and artist name');
          }

          console.log(`   🔍 Searching Spotify with query: "${searchQuery}"`);

          const spotifyResults = await spotifyAPI.search(
            searchQuery,
            ['album'],
            10
          );

          console.log(`   📊 Spotify API returned:`, {
            albumsCount: spotifyResults.albums?.length || 0,
            hasAlbums: !!spotifyResults.albums,
            results: spotifyResults,
          });

          if (!spotifyResults.albums || spotifyResults.albums.length === 0) {
            console.log(
              `   ⚠️ No Spotify matches found for query: "${searchQuery}"`
            );
            // Try a simpler search without field qualifiers
            if (artistName && albumName && searchQuery.includes('album:')) {
              console.log(
                `   🔄 Trying simpler search without field qualifiers...`
              );
              const simpleQuery = `${artistName} ${albumName}`;
              const simpleResults = await spotifyAPI.search(
                simpleQuery,
                ['album'],
                10
              );
              console.log(`   📊 Simple search returned:`, {
                albumsCount: simpleResults.albums?.length || 0,
              });

              if (simpleResults.albums && simpleResults.albums.length > 0) {
                // Use the simple search results
                const bestMatch = simpleResults.albums[0];
                console.log(
                  `   ✅ Found match with simple search: ${bestMatch.name} by ${bestMatch.artist}`
                );

                results.push({
                  album: albumName,
                  artist: artistName,
                  status: 'success',
                  message: 'Found Spotify match (simple search)',
                  spotifyAlbum: bestMatch.name,
                  spotifyArtist: bestMatch.artist,
                  releaseDate: bestMatch.releaseDate,
                  image: bestMatch.image,
                  uri: bestMatch.uri,
                  matchCount: simpleResults.albums.length,
                });
                successCount++;
                continue;
              }
            }

            results.push({
              album: albumName,
              artist: artistName,
              status: 'no_match',
              message: `No Spotify matches found for "${searchQuery}"`,
            });
            errorCount++;
            continue;
          }

          const bestMatch = spotifyResults.albums[0];
          console.log(
            `   ✅ Found match: ${bestMatch.name} by ${bestMatch.artist}`
          );

          // Check if there are multiple albums (could be a set)
          const isMultiAlbumSet = spotifyResults.albums.length > 1;

          if (isMultiAlbumSet) {
            console.log(
              `   📦 Detected ${spotifyResults.albums.length} album matches (possible album set)`
            );
            spotifyResults.albums.forEach((match, idx) => {
              console.log(
                `      ${idx + 1}. ${match.name} (${
                  match.releaseDate || 'Unknown'
                }) - ${match.uri}`
              );
            });
          }

          // Collect all matches for display (up to 5 albums)
          const allMatches = spotifyResults.albums.slice(0, 5).map((a) => ({
            name: a.name,
            artist: a.artist,
            releaseDate: a.releaseDate,
            uri: a.uri,
            image: a.image,
          }));

          results.push({
            album: albumName,
            artist: artistName,
            status: 'success',
            message: isMultiAlbumSet
              ? `Found ${spotifyResults.albums.length} Spotify matches (album set)`
              : 'Found Spotify match',
            spotifyAlbum: bestMatch.name,
            spotifyArtist: bestMatch.artist,
            releaseDate: bestMatch.releaseDate,
            image: bestMatch.image,
            uri: bestMatch.uri,
            allMatches: isMultiAlbumSet ? allMatches : null,
            matchCount: spotifyResults.albums.length,
            isMultiAlbumSet: isMultiAlbumSet,
          });
          successCount++;

          // Add delay to avoid rate limiting
          if (i < queuedAlbums.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`   ❌ Error processing album:`, error);
          let errorMessage = error.message || 'Unknown error';

          // Provide more helpful error messages
          if (
            errorMessage.includes('Not authenticated') ||
            errorMessage.includes('authentication')
          ) {
            errorMessage =
              'Spotify authentication required. Please authenticate with Spotify first.';
          } else if (errorMessage.includes('session expired')) {
            errorMessage = 'Spotify session expired. Please re-authenticate.';
          } else if (errorMessage.includes('401')) {
            errorMessage =
              'Spotify authentication failed. Please re-authenticate.';
          }

          results.push({
            album: albumName,
            artist: artistName,
            status: 'error',
            message: errorMessage,
          });
          errorCount++;
        }
      }

      // Show results summary
      const summary =
        `Spotify Query Complete!\n\n` +
        `✅ Found: ${successCount}\n` +
        `❌ Errors/No Match: ${errorCount}\n\n` +
        `Results:\n${results
          .map((r) => {
            let resultText = `  ${r.status === 'success' ? '✅' : '❌'} ${
              r.album
            } - ${r.artist}: ${r.message}`;
            if (r.spotifyAlbum) {
              resultText += `\n     → ${r.spotifyAlbum} by ${r.spotifyArtist}`;
              if (r.releaseDate) resultText += ` (${r.releaseDate})`;
              if (r.uri) resultText += `\n     🔗 ${r.uri}`;
              if (r.isMultiAlbumSet && r.allMatches) {
                resultText += `\n     📦 Album Set: ${r.matchCount} matches found`;
                r.allMatches.forEach((match, idx) => {
                  resultText += `\n        ${idx + 1}. ${match.name}${
                    match.releaseDate ? ` (${match.releaseDate})` : ''
                  }`;
                  if (match.uri) resultText += `\n           🔗 ${match.uri}`;
                });
              }
            }
            return resultText;
          })
          .join('\n\n')}`;

      alert(summary);
      console.log('📊 Spotify Query Results:', results);
    } catch (error) {
      console.error('❌ Error in Spotify query:', error);
      let errorMessage = error.message;

      if (
        errorMessage.includes('Not authenticated') ||
        errorMessage.includes('authentication')
      ) {
        errorMessage =
          'Spotify authentication required. Please visit the Spotify Library page to authenticate first.';
      }

      alert(`Error querying Spotify: ${errorMessage}`);
    }
  };

  // Query Last.fm for queued albums
  const handleQueryLastfm = async (queuedAlbums, selectedPath = null) => {
    if (!queuedAlbums || queuedAlbums.length === 0) {
      alert('No albums in queue to query');
      return;
    }

    const confirmMessage = `Query Last.fm for ${queuedAlbums.length} album(s)?\n\nThis will search Last.fm for album metadata and display results.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const results = [];
    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < queuedAlbums.length; i++) {
        const album = queuedAlbums[i];
        const albumName = album.album || album.albumName || '';
        const artistName = album.artist || album.artistName || '';

        console.log(
          `\n🎧 Processing album ${i + 1}/${
            queuedAlbums.length
          }: "${albumName}" by "${artistName}"`
        );

        if (!artistName || !albumName) {
          console.log(`   ⚠️ Missing artist or album name`);
          results.push({
            album: albumName,
            artist: artistName,
            status: 'error',
            message: 'Missing artist or album name',
          });
          errorCount++;
          continue;
        }

        try {
          // Query Last.fm API via backend endpoint
          const headers = await getAuthHeaders();
          const lastfmResponse = await fetch(`${API_BASE}/lastfm-album-info`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              album: albumName,
              artist: artistName,
            }),
          });

          if (!lastfmResponse.ok) {
            // If endpoint doesn't exist, show info message
            if (lastfmResponse.status === 404) {
              console.log(`   ⚠️ Last.fm API endpoint not yet implemented`);
              results.push({
                album: albumName,
                artist: artistName,
                status: 'info',
                message: 'Last.fm endpoint requires backend implementation',
              });
              errorCount++;
              continue;
            }
            throw new Error(`Last.fm API error: ${lastfmResponse.statusText}`);
          }

          const lastfmData = await lastfmResponse.json();

          if (!lastfmData.album || lastfmData.error) {
            console.log(`   ⚠️ No Last.fm match found`);
            results.push({
              album: albumName,
              artist: artistName,
              status: 'no_match',
              message: lastfmData.error || 'No Last.fm matches found',
            });
            errorCount++;
            continue;
          }

          const albumInfo = lastfmData.album;
          console.log(
            `   ✅ Found match: ${albumInfo.name} by ${albumInfo.artist}`
          );

          results.push({
            album: albumName,
            artist: artistName,
            status: 'success',
            message: 'Found Last.fm match',
            lastfmAlbum: albumInfo.name,
            lastfmArtist: albumInfo.artist,
            year: albumInfo.wiki?.published
              ? new Date(albumInfo.wiki.published).getFullYear()
              : null,
            image:
              albumInfo.image?.find((img) => img.size === 'large')?.['#text'] ||
              null,
          });
          successCount++;

          // Add delay to respect rate limiting (200ms between requests)
          if (i < queuedAlbums.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 250));
          }
        } catch (error) {
          console.error(`   ❌ Error processing album:`, error);
          results.push({
            album: albumName,
            artist: artistName,
            status: 'error',
            message: error.message,
          });
          errorCount++;
        }
      }

      // Show results summary
      const summary =
        `Last.fm Query Complete!\n\n` +
        `✅ Found: ${successCount}\n` +
        `❌ Errors/No Match: ${errorCount}\n\n` +
        `Results:\n${results
          .map(
            (r) =>
              `  ${r.status === 'success' ? '✅' : '❌'} ${r.album} - ${
                r.artist
              }: ${r.message}${
                r.lastfmAlbum
                  ? `\n     → ${r.lastfmAlbum} by ${r.lastfmArtist}${
                      r.year ? ` (${r.year})` : ''
                    }`
                  : ''
              }`
          )
          .join('\n')}`;

      alert(summary);
      console.log('📊 Last.fm Query Results:', results);
    } catch (error) {
      console.error('❌ Error in Last.fm query:', error);
      alert(`Error querying Last.fm: ${error.message}`);
    }
  };

  const toggleLeftDrawer = () => {
    setLeftDrawerOpen(!leftDrawerOpen);
    if (rightDrawerOpen) setRightDrawerOpen(false);
  };

  // Toggle left panel collapse (desktop "Done Searching" feature)
  const toggleLeftPanelCollapse = () => {
    setLeftPanelCollapsed(!leftPanelCollapsed);
  };

  // Handle Navigation button click - ensure library loads
  const handleNavigationClick = () => {
    setIsNavbarExpanded(!isNavbarExpanded);
    // Ensure left panel is visible so Navigation accordion can be seen
    setLeftPanelCollapsed(false);
    // Hierarchy will load automatically via LeftAccordion component
    console.log('📚 Navigation button clicked - library will load automatically');
  };

  // Handle Back button click - go up one level in navigation hierarchy
  const handleBackClick = () => {
    console.log('🔙 Back button clicked');
    console.log('🔙 isFilteringByArtists:', isFilteringByArtists);
    console.log('🔙 savedNavigationPath:', savedNavigationPath);
    console.log('🔙 current selectedPath:', selectedPath);
    
    // If Done button has been clicked, go back to the last open level in navigation
    if (isFilteringByArtists) {
      console.log('🔙 Done was clicked - restoring navigation');
      // Hide Albums and Hipster accordions
      setIsAlbumsExpanded(false);
      setIsHipsterExpanded(false);
      // Show Navigation accordion
      setIsNavbarExpanded(true);
      setLeftPanelCollapsed(false);
      // On mobile, switch back to searching mode so Navigation accordion is visible
      if (isMobile) {
        setMobileViewMode('searching');
      }
      // Restore the navigation path that was saved when Done was clicked
      if (savedNavigationPath) {
        setSelectedPath(savedNavigationPath);
        console.log('📚 Back button: Restoring navigation path:', savedNavigationPath);
      } else {
        // Fallback: if no saved path, reset to default
        setSelectedPath({ type: null, language: null, genre: null, subgenre: null, artist: null });
        console.log('📚 Back button: No saved path, resetting to default');
      }
      // Clear filters but keep the navigation path
      setIsFilteringByArtists(false);
      setSelectedArtists([]);
      setSelectedFromMasterList([]);
      setIsMasterPlaylistMode(false);
      setSelectedAlbum(null);
      // Clear saved path after restoring
      setSavedNavigationPath(null);
    } else {
      // Go up one level: Artist -> Subgenre -> Genre -> Language -> Type -> null
      if (selectedPath.artist) {
        // Go from Artist to Subgenre
        setSelectedPath({ ...selectedPath, artist: null });
      } else if (selectedPath.subgenre) {
        // Go from Subgenre to Genre
        setSelectedPath({ ...selectedPath, subgenre: null, artist: null });
      } else if (selectedPath.genre) {
        // Go from Genre to Language
        setSelectedPath({ ...selectedPath, genre: null, subgenre: null, artist: null });
      } else if (selectedPath.language) {
        // Go from Language to Type
        setSelectedPath({ ...selectedPath, language: null, genre: null, subgenre: null, artist: null });
      } else if (selectedPath.type) {
        // Go from Type to null (show all)
        setSelectedPath({ type: null, language: null, genre: null, subgenre: null, artist: null });
      }
      // Ensure left panel is visible
      setLeftPanelCollapsed(false);
    }
  };

  // Toggle right panel collapse (desktop "Now Playing" feature)
  const toggleRightPanelCollapse = () => {
    setRightPanelCollapsed(!rightPanelCollapsed);
  };

  const toggleRightDrawer = () => {
    setRightDrawerOpen(!rightDrawerOpen);
    if (leftDrawerOpen) setLeftDrawerOpen(false);
  };

  const handleShufflePlay = (shuffledSongs) => {
    if (shuffledSongs && shuffledSongs.length > 0) {
      // Enforce queue size limit to prevent memory issues
      let songsToPlay = shuffledSongs;
      if (shuffledSongs.length > MAX_QUEUE_SIZE) {
        songsToPlay = shuffledSongs.slice(0, MAX_QUEUE_SIZE);
        console.warn(
          `⚠️ Shuffle queue limited to ${MAX_QUEUE_SIZE} songs (had ${shuffledSongs.length})`
        );
      }

      setShuffleQueue(songsToPlay);
      setCurrentTrackIndex(0);
      setCurrentTrack(songsToPlay[0]);
      setIsPlaying(true);

      // Show the album detail for the first track's album
      setSelectedAlbum(songsToPlay[0].album);
      // Expand right panel when shuffle play is triggered
      setRightPanelCollapsed(false);
    }
  };

  // Swipe mode handlers
  const handleStartSwipeMode = (mode = 'listening') => {
    if (shuffleQueue.length === 0) {
      alert('Please shuffle some albums first!');
      return;
    }

    // Prepare swipe queue with keep/reject flags
    const swipeQueueData = shuffleQueue.map((track) => ({
      ...track,
      kept: false,
      rejected: false,
    }));

    setSwipeQueue(swipeQueueData);
    setSwipeMode(mode);
    setIsSwipeModeActive(true);
    setIsPlaying(false); // Pause during swipe mode

    // If preview mode, close Navigation accordion and ensure Albums accordion is open
    if (mode === 'preview') {
      setIsNavbarExpanded(false);
      setIsAlbumsExpanded(true);
    }
  };

  // Save a single track to playlist in real-time (for live saving)
  const saveTrackToPlaylistLive = async (playlistId, track, user) => {
    if (!playlistId || !track || !user) return;

    try {
      const playlistDocRef = doc(db, 'playlists', playlistId);
      const playlistDocSnap = await getDoc(playlistDocRef);
      
      if (!playlistDocSnap.exists()) {
        console.error('Playlist not found for live save:', playlistId);
        return;
      }

      const existingPlaylist = playlistDocSnap.data();
      const existingTracks = existingPlaylist.tracks || [];
      
      // Prepare track with userId
      const trackToAdd = {
        Title: track.Title,
        DisplayTitle: track.DisplayTitle,
        Duration: track.Duration,
        Id: track.Id,
        addedByUserId: user.uid, // Track which user added this song
        addedByUserEmail: user.email,
        addedAt: new Date().toISOString(),
        album: {
          id: track.album.id,
          album: track.album.album,
          artist: track.album.artist,
          language: track.album.language,
          genre: track.album.genre,
          subgenre: track.album.subgenre,
          type: track.album.type,
          filePath: track.album.filePath,
          coverUrl: track.album.coverUrl,
        },
      };

      // Check for duplicates
      const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${track.album?.album || ''}`;
      const existingKey = existingTracks.find(t => 
        `${t.Title || ''}_${t.album?.artist || ''}_${t.album?.album || ''}` === trackKey
      );

      if (existingKey) {
        console.log('Track already exists in playlist, skipping:', trackKey);
        return;
      }

      // Add track to playlist
      const updatedTracks = [...existingTracks, trackToAdd];

      await updateDoc(playlistDocRef, {
        tracks: updatedTracks,
        trackCount: updatedTracks.length,
        updatedAt: new Date().toISOString(),
      });

      // Update live save playlist info to reflect new track count
      if (liveSavePlaylistInfo) {
        setLiveSavePlaylistInfo({
          ...liveSavePlaylistInfo,
          trackCount: updatedTracks.length,
        });
      }

      console.log('✅ Track saved live to playlist:', track.DisplayTitle || track.Title);
    } catch (error) {
      console.error('❌ Error saving track live:', error);
    }
  };

  const handleSwipeKeep = async (index) => {
    const newQueue = [...swipeQueue];
    newQueue[index].kept = true;
    newQueue[index].rejected = false; // Clear rejected flag when keeping
    setSwipeQueue(newQueue);

    // If we have a live save playlist ID, save immediately
    if (liveSavePlaylistId && user) {
      const track = newQueue[index];
      await saveTrackToPlaylistLive(liveSavePlaylistId, track, user);
    }

    // Move to next track
    if (index < swipeQueue.length - 1) {
      // Continue to next
    }
  };

  const handleSwipeReject = async (index) => {
    const newQueue = [...swipeQueue];
    newQueue[index].rejected = true;
    newQueue[index].kept = false; // Clear kept flag when rejecting
    setSwipeQueue(newQueue);

    // Add track to Do Not Play (DNP) list
    const track = newQueue[index];
    await addTrackToDNPList(track);

    // Move to next track
    if (index < swipeQueue.length - 1) {
      // Continue to next
    }
  };

  const handleBlockPermanently = (track) => {
    const artist = track.album.artist;
    if (!blockedArtists.includes(artist)) {
      const newBlocked = [...blockedArtists, artist];
      setBlockedArtists(newBlocked);

      // Save to localStorage for persistence
      localStorage.setItem(
        'jookbox_blocked_artists',
        JSON.stringify(newBlocked)
      );

      console.log(`Permanently blocked: ${artist}`);
    }
  };

  const handleSaveSwipePlaylist = async (keptTracks, playlistData) => {
    if (!user) {
      alert('You must be logged in to save playlists');
      return;
    }

    console.log(
      `💾 Saving playlist: isAppending=${playlistData.isAppending}, appendToPlaylistId=${playlistData.appendToPlaylistId}, isLiveSave=${playlistData.isLiveSave}`
    );

    // If live save mode, set up real-time saving
    if (playlistData.isLiveSave && playlistData.playlistId) {
      setLiveSavePlaylistId(playlistData.playlistId);
      setLiveSaveMode(playlistData.isAppending ? 'append' : 'create');
      console.log('✅ Live save enabled for playlist:', playlistData.playlistId);
      // Don't close the dialog or show alert - let user continue swiping
      return;
    }

    // If append mode and not live save, set up live saving for future keeps
    if (playlistData.isAppending && playlistData.appendToPlaylistId && !playlistData.isLiveSave) {
      setLiveSavePlaylistId(playlistData.appendToPlaylistId);
      setLiveSaveMode('append');
      console.log('✅ Live save enabled for append mode:', playlistData.appendToPlaylistId);
      // Don't close the dialog - let user continue swiping
      return;
    }

    try {
      // Prepare tracks for storage (remove functions and circular refs)
      // Add userId to track metadata for delete functionality
      const tracksToSave = keptTracks.map((track) => ({
        Title: track.Title,
        DisplayTitle: track.DisplayTitle,
        Duration: track.Duration,
        Id: track.Id,
        addedByUserId: user.uid, // Track which user added this song
        addedByUserEmail: user.email,
        addedAt: new Date().toISOString(),
        album: {
          id: track.album.id,
          album: track.album.album,
          artist: track.album.artist,
          language: track.album.language,
          genre: track.album.genre,
          subgenre: track.album.subgenre,
          type: track.album.type,
          filePath: track.album.filePath,
          coverUrl: track.album.coverUrl,
        },
      }));

      if (playlistData.isAppending && playlistData.appendToPlaylistId) {
        // Append to existing playlist
        const playlistDocRef = doc(db, 'playlists', playlistData.appendToPlaylistId);
        const playlistDocSnap = await getDoc(playlistDocRef);
        
        if (!playlistDocSnap.exists()) {
          alert('Selected playlist not found');
          return;
        }

        const existingPlaylist = playlistDocSnap.data();
        const existingTracks = existingPlaylist.tracks || [];
        
        // Combine tracks (avoid duplicates based on track key)
        const trackMap = new Map();
        existingTracks.forEach(track => {
          const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${track.album?.album || ''}`;
          trackMap.set(trackKey, track);
        });

        // Add new tracks (skip duplicates)
        tracksToSave.forEach(track => {
          const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${track.album?.album || ''}`;
          if (!trackMap.has(trackKey)) {
            trackMap.set(trackKey, track);
          }
        });

        const combinedTracks = Array.from(trackMap.values());

        await updateDoc(playlistDocRef, {
          tracks: combinedTracks,
          trackCount: combinedTracks.length,
          updatedAt: new Date().toISOString(),
        });

        alert(
          `✅ Added ${tracksToSave.length} song(s) to "${existingPlaylist.name}"! Total: ${combinedTracks.length} songs.`
        );
      } else {
        // Create new playlist
        console.log(
          `💾 Creating new playlist: "${playlistData.name}" with ${keptTracks.length} tracks`
        );

        const playlistDoc = {
          name: playlistData.name,
          description: playlistData.description || '',
          userId: user.uid,
          userEmail: user.email,
          tracks: tracksToSave,
          trackCount: tracksToSave.length,
          isPublic: playlistData.isPublic || false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        console.log('📤 Saving to Firestore...', {
          userId: user.uid,
          trackCount: tracksToSave.length,
        });
        const docRef = await addDoc(collection(db, 'playlists'), playlistDoc);
        console.log('✅ Playlist saved with ID:', docRef.id);
        alert(
          `✓ Playlist "${playlistData.name}" saved with ${keptTracks.length} songs!`
        );
      }

      // Update the main queue with kept tracks
      setShuffleQueue(keptTracks);
      setCurrentTrackIndex(0);

      if (keptTracks.length > 0) {
        setCurrentTrack(keptTracks[0]);
        setSelectedAlbum(keptTracks[0].album);
      }

      // Reload playlists
      console.log('🔄 Reloading playlists...');
      await loadSavedPlaylists();

      setIsSwipeModeActive(false);
    } catch (error) {
      console.error('❌ Error saving playlist:', error);
      alert(`Failed to save playlist: ${error.message}`);
    }
  };

  const handleCloseSwipeMode = () => {
    setIsSwipeModeActive(false);
    // Expand Navigation accordion to load library as if Navigation button was clicked
    setIsNavbarExpanded(true);
    // Ensure left panel is not collapsed so Navigation is visible
    setLeftPanelCollapsed(false);
  };

  // Delete a track from a playlist (only if user added it)
  const handleDeleteTrackFromPlaylist = async (playlistId, trackIndex, track) => {
    if (!user || !playlistId) return;

    // Check if user added this track
    if (track.addedByUserId && track.addedByUserId !== user.uid) {
      alert('You can only delete tracks that you added to this playlist.');
      return;
    }

    if (!window.confirm(`Remove "${track.DisplayTitle || track.Title}" from this playlist?`)) {
      return;
    }

    try {
      const playlistDocRef = doc(db, 'playlists', playlistId);
      const playlistDocSnap = await getDoc(playlistDocRef);
      
      if (!playlistDocSnap.exists()) {
        alert('Playlist not found');
        return;
      }

      const playlist = playlistDocSnap.data();
      const tracks = playlist.tracks || [];
      
      // Remove the track at the specified index
      const updatedTracks = tracks.filter((_, index) => index !== trackIndex);

      await updateDoc(playlistDocRef, {
        tracks: updatedTracks,
        trackCount: updatedTracks.length,
        updatedAt: new Date().toISOString(),
      });

      console.log('✅ Track deleted from playlist');
      
      // Reload playlists to reflect the change
      await loadSavedPlaylists();
      
      // If this playlist is currently loaded, update the queue
      if (currentPlaylist && currentPlaylist.id === playlistId) {
        const updatedQueue = updatedTracks.map((track) => ({
          ...track,
          kept: false,
          rejected: false,
        }));
        setSwipeQueue(updatedQueue);
      }
    } catch (error) {
      console.error('❌ Error deleting track from playlist:', error);
      alert(`Failed to delete track: ${error.message}`);
    }
  };

  const handleLoadPlaylist = (playlist, shuffle = false) => {
    // Create a copy of tracks to avoid mutating the original
    let tracksToLoad = [...playlist.tracks];
    
    // Always shuffle when loading a playlist
    // Fisher-Yates shuffle algorithm
    for (let i = tracksToLoad.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tracksToLoad[i], tracksToLoad[j]] = [tracksToLoad[j], tracksToLoad[i]];
    }
    console.log(`🔀 Shuffled playlist: ${playlist.name}`);
    
    // Store playlist metadata for display (including ID for delete operations)
    setCurrentPlaylist({
      id: playlist.id, // Store playlist ID for delete operations
      name: playlist.name,
      description: playlist.description,
      createdBy:
        playlist.createdBy ||
        playlist.userEmail ||
        playlist.userId ||
        'Unknown',
      createdAt: playlist.createdAt,
    });

    if (tracksToLoad.length > 0) {
      // Prepare swipe queue with keep/reject flags for preview mode
      const swipeQueueData = tracksToLoad.map((track) => ({
        ...track,
        kept: false,
        rejected: false,
      }));

      setShuffleQueue(tracksToLoad);
      setSwipeQueue(swipeQueueData);
      setCurrentTrackIndex(0);
      setSwipeMode('preview');
      setIsSwipeModeActive(true);
      setIsPlaying(false); // Pause during swipe mode
      
      // Close Navigation accordion and ensure Albums accordion is open for preview mode
      setIsNavbarExpanded(false);
      setIsAlbumsExpanded(true);
      setRightPanelCollapsed(false);
    }

    setShowPlaylists(false);
    console.log(
      `Loaded playlist: ${playlist.name} with ${tracksToLoad.length} tracks (shuffled) - Opening in Preview Mode`
    );
  };

  const handleDeletePlaylist = async (playlistId) => {
    try {
      await deleteDoc(doc(db, 'playlists', playlistId));
      await loadSavedPlaylists();
      console.log('Playlist deleted');
    } catch (error) {
      console.error('Error deleting playlist:', error);
      alert('Failed to delete playlist');
    }
  };

  const handleTogglePlaylistPublic = async (playlistId, currentIsPublic) => {
    try {
      const newIsPublic = !currentIsPublic;
      console.log(
        `🔄 Toggling playlist ${playlistId}: ${currentIsPublic} -> ${newIsPublic}`
      );
      await updateDoc(doc(db, 'playlists', playlistId), {
        isPublic: newIsPublic,
        updatedAt: new Date().toISOString(),
      });
      console.log(
        `✅ Playlist ${newIsPublic ? 'made public' : 'made private'}`
      );
      await loadSavedPlaylists();
    } catch (error) {
      console.error('Error toggling playlist public status:', error);
      alert('Failed to update playlist visibility');
    }
  };

  const handleNextTrack = () => {
    if (
      shuffleQueue.length > 0 &&
      currentTrackIndex < shuffleQueue.length - 1
    ) {
      const nextIndex = currentTrackIndex + 1;
      const nextTrack = shuffleQueue[nextIndex];
      const nextAlbum = nextTrack.album;
      // If in scanned mode, always use SpotifyPlayer if track has spotifyUri
      const isSpotifyTrack = nextTrack?.spotifyUri && (
        isInScannedMode || // Scanned albums always use Spotify
        nextAlbum?.storageStatus === 'spotify' ||
        nextTrack?.metadataSource === 'barcode-scan'
      );
      
      setCurrentTrackIndex(nextIndex);
      setSelectedAlbum(nextAlbum);
      
      if (isSpotifyTrack && isSpotifyPremium) {
        // Use SpotifyPlayer for Spotify tracks
        console.log('🎵 Playing next Spotify track:', nextTrack.spotifyUri);
        setSpotifyTrackUri(nextTrack.spotifyUri);
        setCurrentTrack({
          ...nextTrack,
          album: nextAlbum,
          source: 'spotify',
          uri: nextTrack.spotifyUri,
        });
        setIsPlaying(true);
      } else if (isSpotifyTrack && !isSpotifyPremium) {
        // Spotify track but no premium - show error
        console.warn('⚠️ Spotify Premium required for scanned albums');
        setCurrentTrack({ ...nextTrack, album: nextAlbum });
        setIsPlaying(false);
        alert('Spotify Premium is required to play tracks from scanned albums.');
      } else {
        // Local track - clear Spotify URI and use MediaPlayer
        if (spotifyTrackUri) {
          setSpotifyTrackUri(null);
        }
        setCurrentTrack({ ...nextTrack, album: nextAlbum });
        setIsPlaying(true);
      }
    }
  };

  const handlePreviousTrack = () => {
    if (shuffleQueue.length > 0 && currentTrackIndex > 0) {
      const prevIndex = currentTrackIndex - 1;
      const prevTrack = shuffleQueue[prevIndex];
      const prevAlbum = prevTrack.album;
      const isSpotifyTrack = prevTrack?.spotifyUri && (
        isInScannedMode || // Scanned albums are always Spotify
        prevAlbum?.storageStatus === 'spotify' ||
        prevTrack?.metadataSource === 'barcode-scan'
      );
      
      setCurrentTrackIndex(prevIndex);
      setSelectedAlbum(prevAlbum);
      
      if (isSpotifyTrack && isSpotifyPremium) {
        // Use SpotifyPlayer for Spotify tracks
        console.log('🎵 Playing previous Spotify track:', prevTrack.spotifyUri);
        setSpotifyTrackUri(prevTrack.spotifyUri);
        setCurrentTrack({
          ...prevTrack,
          album: prevAlbum,
          source: 'spotify',
          uri: prevTrack.spotifyUri,
        });
        setIsPlaying(true);
      } else if (isSpotifyTrack && !isSpotifyPremium) {
        // Spotify track but no premium - show error
        console.warn('⚠️ Spotify Premium required for scanned albums');
        setCurrentTrack({ ...prevTrack, album: prevAlbum });
        setIsPlaying(false);
        alert('Spotify Premium is required to play tracks from scanned albums.');
      } else {
        // Local track - clear Spotify URI and use MediaPlayer
        if (spotifyTrackUri) {
          setSpotifyTrackUri(null);
        }
        setCurrentTrack({ ...prevTrack, album: prevAlbum });
        setIsPlaying(true);
      }
    }
  };

  const handleRestartTrack = () => {
    // Restart the current track from the beginning
    // This will be handled by MediaPlayer when it receives the signal
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 100);
  };

  // Note: Skip forward/backward will be connected to MediaPlayer's audio element
  const handleSkipForward = () => {
    // Will dispatch event to MediaPlayer
    window.dispatchEvent(
      new CustomEvent('audioSkip', { detail: { seconds: 10 } })
    );
  };

  const handleSkipBackward = () => {
    // Will dispatch event to MediaPlayer
    window.dispatchEvent(
      new CustomEvent('audioSkip', { detail: { seconds: -10 } })
    );
  };

  return (
    <div
      className={`library-layout ${mediaPlayerVisible ? 'player-visible' : ''}`}
    >
      {/* Header */}
      <header className="library-header">
        {/* Mobile: Drawer Toggle Buttons */}
        <button
          className="mobile-drawer-toggle left"
          onClick={toggleLeftDrawer}
          aria-label="Toggle navigation"
        >
          ☰
        </button>

        {/* Desktop: Show Back button when navigating (has any path selected) OR when Done has been clicked */}
        {(isFilteringByArtists || selectedPath.type || selectedPath.language || selectedPath.genre || selectedPath.subgenre || selectedPath.artist) && (
          <button
            className="show-nav-btn"
            onClick={handleBackClick}
            title="Go back one level"
            style={{ display: 'flex' }}
          >
            ← Back
          </button>
        )}

        <div className="library-logo" style={{ display: 'none' }}>
          <h1>🎵 JOOKBOX</h1>
          {isLoadingHierarchy && (
            <div
              className="library-loading-indicator"
              title="Loading library..."
            >
              <span className="loading-text">Loading ...</span>
              <span className="loading-spinner">⏳</span>
            </div>
          )}
        </div>

        <div className="library-user">
          <span>Welcome, {user?.email}</span>
        </div>

        {/* Turntable Button - Hidden */}
        {false && (
          <button
            className={`turntable-toggle-btn ${showTurntable ? 'active' : ''}`}
            onClick={() => setShowTurntable(!showTurntable)}
            title="Show Turntable"
          >
            💿
          </button>
        )}

        {/* Playlists Button */}
        <button
          className="playlists-toggle-btn"
          onClick={() => setShowPlaylists(true)}
          title="My Playlists"
        >
          📋 <span className="playlists-count">{savedPlaylists.length}</span>
        </button>

        {/* Master Artist List Checkbox */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            marginLeft: '1rem',
            marginRight: '1rem',
          }}
        >
          <input
            type="checkbox"
            checked={isMasterPlaylistMode}
            onChange={(e) => {
              setIsMasterPlaylistMode(e.target.checked);
              // Auto-expand Albums accordion when Master Artist mode is enabled
              if (e.target.checked) {
                setIsAlbumsExpanded(true);
              }
            }}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span
            style={{
              color: '#fff',
              fontWeight: '600',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
            }}
          >
            Master Artist list
          </span>
        </label>

        {/* Navigation Links */}
        <div className="header-nav-links">
          <Link to="/" className="header-nav-link" title="Home">
            Home
          </Link>
          {getNavigationItems(!!user).map((item) =>
            item.external ? (
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className="header-nav-link"
                title={item.title}
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className="header-nav-link"
                title={item.title}
              >
                {item.label}
              </Link>
            )
          )}
          <button
            className="header-nav-link logout-btn"
            onClick={handleLogout}
            title="Logout"
          >
            Logout
          </button>
        </div>

        {/* Desktop: Show Now Playing button when right panel is collapsed and something is playing */}
        {rightPanelCollapsed && currentTrack && (
          <button
            className="show-now-playing-btn"
            onClick={toggleRightPanelCollapse}
            title="Show Now Playing"
          >
            🎵 Now Playing
          </button>
        )}

        {/* Mobile: Album Detail Toggle Button */}
        {selectedAlbum && (
          <button
            className="mobile-drawer-toggle right"
            onClick={toggleRightDrawer}
            aria-label="Show album details"
          >
            ℹ️
          </button>
        )}

        {/* Mobile: Back Button - Show when navigating (has any path selected) OR when Done has been clicked */}
        {isMobile && (isFilteringByArtists || selectedPath.type || selectedPath.language || selectedPath.genre || selectedPath.subgenre || selectedPath.artist) && (
          <button
            className="back-to-nav-btn"
            onClick={handleBackClick}
            aria-label="Go back one level"
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #1db954 0%, #1ed760 100%)',
              border: 'none',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 10px rgba(29, 185, 84, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginLeft: 'auto',
            }}
          >
            ← Back
          </button>
        )}
      </header>

      {/* Navigation Accordion - Between Header and Green Border Container */}
      <div
        className={`viewport-top-column accordion-section ${
          isMobile && mobileViewMode === 'browsing' ? 'mobile-hidden' : ''
        } ${!isNavbarExpanded ? 'collapsed' : ''}`}
      >
        {/* Accordion Header for Navbar */}
        <div
          className="accordion-header"
          onClick={handleNavigationClick}
          style={{
            padding: '0.75rem 1rem',
            background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
            borderBottom: 'none',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            userSelect: 'none',
          }}
        >
          <span style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}>
            Navigation
          </span>
          <span
            style={{
              color: '#fff',
              fontSize: '1.2rem',
              transition: 'transform 0.3s ease',
              transform: isNavbarExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            ▼
          </span>
        </div>

        {/* Accordion Content */}
        {isNavbarExpanded && (
          <div
            className="accordion-content"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {/* English and Spanish Lists - At Top, Hidden when artists are selected */}
            {false &&
              isMasterPlaylistMode &&
              selectedArtists.length > 0 &&
              selectedFromMasterList.length === 0 && (
                <div style={{ order: 1 }}>
                  {(() => {
                    const normalizeArtistName = (name) => {
                      if (!name) return '';
                      return name.replace(/\s+\d+$/, '').trim();
                    };

                    // Filter artists by language
                    const getArtistsByLanguage = (language) => {
                      const languageArtists = [];
                      if (hierarchy && hierarchy.albums) {
                        selectedArtists.forEach((artist) => {
                          const artistHasLanguage = Object.keys(
                            hierarchy.albums
                          ).some((artistKey) => {
                            const [lang, genre, subgenre, artistFromKey] =
                              artistKey.split('|');
                            if (lang === language) {
                              const hasMatchingArtist = hierarchy.albums[
                                artistKey
                              ].some((album) => {
                                const albumArtist =
                                  album.artist || artistFromKey;
                                return (
                                  normalizeArtistName(albumArtist) ===
                                  normalizeArtistName(artist)
                                );
                              });
                              return hasMatchingArtist;
                            }
                            return false;
                          });
                          if (artistHasLanguage) {
                            languageArtists.push(artist);
                          }
                        });
                      }

                      const artistMap = new Map();
                      languageArtists.forEach((artist) => {
                        const normalized = normalizeArtistName(artist);
                        if (!artistMap.has(normalized)) {
                          artistMap.set(normalized, []);
                        }
                        artistMap.get(normalized).push(artist);
                      });

                      const uniqueBaseNames = Array.from(
                        artistMap.keys()
                      ).sort();
                      return uniqueBaseNames.map((baseName) => ({
                        baseName,
                        variations: artistMap.get(baseName).sort(),
                      }));
                    };

                    const englishArtists = getArtistsByLanguage('English');
                    const spanishArtists = getArtistsByLanguage('Spanish');

                    const renderLanguageList = (title, artists) => (
                      <div
                        className="language-artist-list"
                        style={{
                          flex: '1 1 0',
                          minWidth: 0,
                          height: '100%',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          padding: '0.75rem',
                          background: '#1a1a1a',
                          border: 'none',
                          borderRadius: '6px',
                        }}
                      >
                        <div
                          style={{
                            marginBottom: '0.75rem',
                            color: '#fff',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            borderBottom: 'none',
                            paddingBottom: '0.5rem',
                          }}
                        >
                          {title} ({artists.length})
                        </div>
                        {artists.length === 0 ? (
                          <div
                            style={{
                              textAlign: 'center',
                              color: '#aaa',
                              padding: '1rem',
                              fontSize: '0.85rem',
                            }}
                          >
                            None
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              flexWrap: 'wrap',
                              gap: '0.4rem',
                            }}
                          >
                            {artists.map(({ baseName, variations }) => {
                              const isSelected = selectedFromMasterList.some(
                                (selected) =>
                                  normalizeArtistName(selected) ===
                                  normalizeArtistName(baseName)
                              );

                              return (
                                <button
                                  key={baseName}
                                  onClick={() => {
                                    // Toggle selection - add or remove all variations
                                    const normalized =
                                      normalizeArtistName(baseName);
                                    const isCurrentlySelected =
                                      selectedFromMasterList.some(
                                        (selected) =>
                                          normalizeArtistName(selected) ===
                                          normalized
                                      );

                                    if (isCurrentlySelected) {
                                      // Remove all variations
                                      setSelectedFromMasterList((prev) =>
                                        prev.filter(
                                          (artist) =>
                                            normalizeArtistName(artist) !==
                                            normalized
                                        )
                                      );
                                    } else {
                                      // Add all variations
                                      setSelectedFromMasterList((prev) => {
                                        const combined = new Set(prev);
                                        variations.forEach((variation) =>
                                          combined.add(variation)
                                        );
                                        return Array.from(combined);
                                      });
                                    }
                                  }}
                                  style={{
                                    background: isSelected
                                      ? '#1db954'
                                      : '#2a2a2a',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    border: 'none',
                                    color: '#fff',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    textAlign: 'left',
                                    boxShadow: isSelected
                                      ? '0 4px 12px rgba(29, 185, 84, 0.3)'
                                      : 'none',
                                    width: 'auto',
                                    flex: '0 0 auto',
                                    whiteSpace: 'nowrap',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = '#333';
                                      e.currentTarget.style.borderColor =
                                        '#555';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background =
                                        '#2a2a2a';
                                      e.currentTarget.style.borderColor =
                                        '#444';
                                    }
                                  }}
                                >
                                  {baseName} {isSelected && ' ✓'}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );

                    // Only show lists that have artists
                    const hasEnglishArtists = englishArtists.length > 0;
                    const hasSpanishArtists = spanishArtists.length > 0;

                    // Don't render anything if both lists are empty
                    if (!hasEnglishArtists && !hasSpanishArtists) {
                      return null;
                    }

                    return (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          borderBottom: 'none',
                          minHeight: '150px',
                          maxHeight: '200px',
                        }}
                      >
                        {hasEnglishArtists &&
                          renderLanguageList('English', englishArtists)}
                        {hasSpanishArtists &&
                          renderLanguageList('Spanish', spanishArtists)}
                      </div>
                    );
                  })()}
                </div>
              )}

            {/* Navigation Content - Below English/Spanish Lists */}
            <div className="viewport-nav-content" style={{ order: 2 }}>
              <LeftAccordion
                selectedPath={selectedPath}
                onPathChange={handlePathChange}
                onHierarchyLoad={(hierarchyData) => {
                  setHierarchy(hierarchyData);
                  setIsLoadingHierarchy(false);
                  // Keep Navigation accordion collapsed on startup (user can expand it manually)
                  // setIsNavbarExpanded remains false
                }}
                user={user}
                onClose={() => setLeftDrawerOpen(false)}
                onDoneSearching={handleDoneSearching}
                isCollapsed={false}
                onAlbumSelect={(album) => {
                  // Select the album and start playing
                  console.log('🎵 Playing album from nav:', album.album);
                  handleAlbumSelect(album);
                }}
                isFocused={focusedPanel === 'left'}
                showNavLinks={showNavLinks}
                setShowNavLinks={setShowNavLinks}
                selectedArtists={selectedArtists}
                onArtistToggle={handleArtistToggle}
                isMasterPlaylistMode={isMasterPlaylistMode}
                onMasterPlaylistModeChange={setIsMasterPlaylistMode}
                selectedFromMasterList={selectedFromMasterList}
                onMasterListArtistToggle={(artistName, variations) => {
                  // Toggle selection - add or remove all variations
                  const normalizeArtistName = (name) => {
                    if (!name) return '';
                    return name.replace(/\s+\d+$/, '').trim();
                  };
                  const normalized = normalizeArtistName(artistName);
                  const isSelected = selectedFromMasterList.some(
                    (selected) => normalizeArtistName(selected) === normalized
                  );

                  if (isSelected) {
                    // Remove all variations
                    setSelectedFromMasterList((prev) =>
                      prev.filter(
                        (artist) => normalizeArtistName(artist) !== normalized
                      )
                    );
                  } else {
                    // Add all variations
                    setSelectedFromMasterList((prev) => {
                      const combined = new Set(prev);
                      variations.forEach((variation) =>
                        combined.add(variation)
                      );
                      return Array.from(combined);
                    });
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Main 3-Column Layout */}
      <div
        className={`library-main ${
          leftPanelCollapsed ? 'left-collapsed' : ''
        } ${rightPanelCollapsed ? 'right-collapsed' : ''}`}
      >
        {/* Viewport Container */}
        <div
          className="viewport-container"
          style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
        >
          {/* Middle Column - Remaining Height */}
          {/* Albums Accordion - Always visible, content shown when expanded */}
          <div
            className={`viewport-bottom-column accordion-section ${
              !isAlbumsExpanded ? 'collapsed' : ''
            }`}
            style={{
              display: 'flex',
              visibility: 'visible',
              opacity: 1,
              width: '100%',
              minHeight: '48px',
              position: 'relative',
              zIndex: 10,
            }}
          >
            {/* Accordion Header for Albums */}
            <div
              className="accordion-header"
              onClick={() => setIsAlbumsExpanded(!isAlbumsExpanded)}
              style={{
                padding: '0.75rem 1rem',
                background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
                borderBottom: 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none',
              }}
            >
              <span
                style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}
              >
                {isMasterPlaylistMode ? 'Artist Selection' : 'Albums'}
              </span>
              <span
                style={{
                  color: '#fff',
                  fontSize: '1.2rem',
                  transition: 'transform 0.3s ease',
                  transform: isAlbumsExpanded
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                }}
              >
                ▼
              </span>
            </div>

            {/* Accordion Content */}
            {isAlbumsExpanded && (
              <div className="accordion-content">
                {/* Current Playlist Info - Show when a playlist is loaded */}
                {currentPlaylist && (
                  <div
                    className="current-playlist-header"
                    style={{
                      padding: '1rem 1.5rem',
                      background: 'rgba(30, 144, 255, 0.1)',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <h3
                      style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '1.5rem',
                        color: '#fff',
                        fontWeight: '600',
                      }}
                    >
                      {currentPlaylist.name}
                    </h3>
                    {currentPlaylist.description && (
                      <p
                        style={{
                          margin: '0',
                          fontSize: '1rem',
                          color: '#b3b3b3',
                          lineHeight: '1.5',
                        }}
                      >
                        {currentPlaylist.description}
                      </p>
                    )}
                  </div>
                )}
                {/* Master Artist list Mode: Show artists list instead of albums */}
                {isMasterPlaylistMode ? (
                  (() => {
                    // Get unique, sorted artist names for display (A-Z)
                    const normalizeArtistName = (name) => {
                      if (!name) return '';
                      return name.replace(/\s+\d+$/, '').trim();
                    };

                    // Master Artist List - ALL selected artists (not filtered by current path)
                    // Group all selected artists by normalized name
                    const masterArtistMap = new Map();
                    selectedArtists.forEach((artist) => {
                      const normalized = normalizeArtistName(artist);
                      if (!masterArtistMap.has(normalized)) {
                        masterArtistMap.set(normalized, []);
                      }
                      masterArtistMap.get(normalized).push(artist);
                    });

                    // Get unique base names (normalized) and sort A-Z
                    const masterUniqueBaseNames = Array.from(
                      masterArtistMap.keys()
                    ).sort();

                    // Return array with base name and all variations for Master List
                    const masterUniqueArtists = masterUniqueBaseNames.map(
                      (baseName) => ({
                        baseName,
                        variations: masterArtistMap.get(baseName).sort(),
                      })
                    );

                    // Filter artists by language (English and Spanish)
                    const getArtistsByLanguage = (language) => {
                      const languageArtists = [];
                      if (hierarchy && hierarchy.albums) {
                        selectedArtists.forEach((artist) => {
                          // Check if this artist has albums in the specified language
                          const artistHasLanguage = Object.keys(
                            hierarchy.albums
                          ).some((artistKey) => {
                            const [lang, genre, subgenre, artistFromKey] =
                              artistKey.split('|');

                            if (lang === language) {
                              // Check if this artist key contains albums with the artist name
                              const hasMatchingArtist = hierarchy.albums[
                                artistKey
                              ].some((album) => {
                                const albumArtist =
                                  album.artist || artistFromKey;
                                return (
                                  normalizeArtistName(albumArtist) ===
                                  normalizeArtistName(artist)
                                );
                              });
                              return hasMatchingArtist;
                            }
                            return false;
                          });

                          if (artistHasLanguage) {
                            languageArtists.push(artist);
                          }
                        });
                      }

                      // Group by normalized name
                      const artistMap = new Map();
                      languageArtists.forEach((artist) => {
                        const normalized = normalizeArtistName(artist);
                        if (!artistMap.has(normalized)) {
                          artistMap.set(normalized, []);
                        }
                        artistMap.get(normalized).push(artist);
                      });

                      const uniqueBaseNames = Array.from(
                        artistMap.keys()
                      ).sort();
                      return uniqueBaseNames.map((baseName) => ({
                        baseName,
                        variations: artistMap.get(baseName).sort(),
                      }));
                    };

                    const englishArtists = getArtistsByLanguage('English');
                    const spanishArtists = getArtistsByLanguage('Spanish');

                    // Handler for toggling artist selection from Master Artist list
                    const handleMasterListToggle = (artistName, variations) => {
                      const normalized = normalizeArtistName(artistName);
                      const isSelected = selectedFromMasterList.some(
                        (selected) =>
                          normalizeArtistName(selected) === normalized
                      );

                      if (isSelected) {
                        // Remove all variations
                        setSelectedFromMasterList((prev) =>
                          prev.filter(
                            (artist) =>
                              normalizeArtistName(artist) !== normalized
                          )
                        );
                      } else {
                        // Add all variations
                        setSelectedFromMasterList((prev) => {
                          const combined = new Set(prev);
                          variations.forEach((variation) =>
                            combined.add(variation)
                          );
                          return Array.from(combined);
                        });
                      }
                    };

                    // Helper function to render an artist list
                    const renderArtistList = (title, artists, emptyMessage) => (
                      <div
                        className="language-artist-list"
                        style={{
                          flex: '1 1 0',
                          minWidth: 0,
                          height: '100%',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          padding: '1rem',
                          background: '#1a1a1a',
                          border: 'none',
                          borderRadius: '6px',
                        }}
                      >
                        <div
                          style={{
                            marginBottom: '1rem',
                            color: '#fff',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            borderBottom: 'none',
                            paddingBottom: '0.5rem',
                          }}
                        >
                          {title} ({artists.length} artist
                          {artists.length !== 1 ? 's' : ''})
                        </div>
                        {artists.length === 0 ? (
                          <div
                            style={{
                              textAlign: 'center',
                              color: '#aaa',
                              padding: '2rem',
                              fontSize: '0.9rem',
                            }}
                          >
                            {emptyMessage}
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.5rem',
                            }}
                          >
                            {artists.map(({ baseName, variations }) => {
                              const isSelected = selectedFromMasterList.some(
                                (selected) =>
                                  normalizeArtistName(selected) ===
                                  normalizeArtistName(baseName)
                              );

                              return (
                                <div
                                  key={baseName}
                                  onClick={() =>
                                    handleMasterListToggle(baseName, variations)
                                  }
                                  className={`master-artist-card ${
                                    isSelected ? 'selected' : ''
                                  }`}
                                  style={{
                                    background: isSelected
                                      ? '#1db954'
                                      : '#2a2a2a',
                                    padding: '0.75rem',
                                    borderRadius: '6px',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: isSelected
                                      ? '0 4px 12px rgba(29, 185, 84, 0.3)'
                                      : 'none',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = '#333';
                                      e.currentTarget.style.borderColor =
                                        '#555';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background =
                                        '#2a2a2a';
                                      e.currentTarget.style.borderColor =
                                        '#444';
                                    }
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: '600',
                                      fontSize: '1rem',
                                      marginBottom:
                                        variations.length > 1 ? '0.5rem' : '0',
                                    }}
                                  >
                                    {baseName}{' '}
                                    {variations.length > 1
                                      ? `(${variations.length} variations)`
                                      : ''}
                                  </div>
                                  {variations.length > 1 && (
                                    <div
                                      style={{
                                        fontSize: '0.85rem',
                                        color: '#aaa',
                                        marginTop: '0.25rem',
                                      }}
                                    >
                                      {variations.slice(0, 3).join(', ')}
                                      {variations.length > 3 ? '...' : ''}
                                    </div>
                                  )}
                                  {isSelected && (
                                    <span
                                      style={{
                                        fontSize: '1.2rem',
                                        float: 'right',
                                      }}
                                    >
                                      ✓
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );

                    // Filter selectedArtists to only include artists that match the current path (for bottom section)
                    const filteredArtistsForCurrentPath = [];
                    if (hierarchy && hierarchy.albums) {
                      selectedArtists.forEach((artist) => {
                        // Check if this artist has albums matching the current path
                        const artistMatchesPath = Object.keys(
                          hierarchy.albums
                        ).some((artistKey) => {
                          const [lang, genre, subgenre, artistFromKey] =
                            artistKey.split('|');

                          // Check if path matches
                          const pathMatches =
                            (!selectedPath.type ||
                              hierarchy.albums[artistKey].some(
                                (album) => album.type === selectedPath.type
                              )) &&
                            (!selectedPath.language ||
                              lang === selectedPath.language) &&
                            (!selectedPath.genre ||
                              genre === selectedPath.genre) &&
                            (!selectedPath.subgenre ||
                              subgenre === selectedPath.subgenre);

                          if (pathMatches) {
                            // Check if this artist key contains albums with the artist name
                            const hasMatchingArtist = hierarchy.albums[
                              artistKey
                            ].some((album) => {
                              const albumArtist = album.artist || artistFromKey;
                              return (
                                normalizeArtistName(albumArtist) ===
                                normalizeArtistName(artist)
                              );
                            });
                            return hasMatchingArtist;
                          }
                          return false;
                        });

                        if (artistMatchesPath) {
                          filteredArtistsForCurrentPath.push(artist);
                        }
                      });
                    } else {
                      // If no hierarchy, show all selected artists
                      filteredArtistsForCurrentPath.push(...selectedArtists);
                    }

                    // Group filtered artists by normalized name
                    const filteredArtistMap = new Map();
                    filteredArtistsForCurrentPath.forEach((artist) => {
                      const normalized = normalizeArtistName(artist);
                      if (!filteredArtistMap.has(normalized)) {
                        filteredArtistMap.set(normalized, []);
                      }
                      filteredArtistMap.get(normalized).push(artist);
                    });

                    // Get unique base names (normalized) and sort A-Z
                    const filteredUniqueBaseNames = Array.from(
                      filteredArtistMap.keys()
                    ).sort();

                    // Return array with base name and all variations for filtered list
                    const filteredUniqueArtists = filteredUniqueBaseNames.map(
                      (baseName) => ({
                        baseName,
                        variations: filteredArtistMap.get(baseName).sort(),
                      })
                    );

                    // Get all unique artists matching current path (not just from selectedArtists)
                    // If no path is selected, show all artists
                    const allArtistsMatchingPath = [];
                    if (hierarchy && hierarchy.albums) {
                      Object.keys(hierarchy.albums).forEach((artistKey) => {
                        const [lang, genre, subgenre, artistFromKey] =
                          artistKey.split('|');

                        // If no path is selected, show all artists
                        // Otherwise, check if path matches
                        const pathMatches =
                          !selectedPath.type &&
                          !selectedPath.language &&
                          !selectedPath.genre &&
                          !selectedPath.subgenre
                            ? true // Show all if no path selected
                            : (!selectedPath.type ||
                                hierarchy.albums[artistKey].some(
                                  (album) => album.type === selectedPath.type
                                )) &&
                              (!selectedPath.language ||
                                lang === selectedPath.language) &&
                              (!selectedPath.genre ||
                                genre === selectedPath.genre) &&
                              (!selectedPath.subgenre ||
                                subgenre === selectedPath.subgenre);

                        if (pathMatches) {
                          hierarchy.albums[artistKey].forEach((album) => {
                            const albumArtist = album.artist || artistFromKey;
                            if (
                              albumArtist &&
                              !allArtistsMatchingPath.includes(albumArtist)
                            ) {
                              allArtistsMatchingPath.push(albumArtist);
                            }
                          });
                        }
                      });
                    }

                    // Group by normalized name
                    const pathArtistMap = new Map();
                    allArtistsMatchingPath.forEach((artist) => {
                      const normalized = normalizeArtistName(artist);
                      if (!pathArtistMap.has(normalized)) {
                        pathArtistMap.set(normalized, []);
                      }
                      pathArtistMap.get(normalized).push(artist);
                    });

                    const pathUniqueBaseNames = Array.from(
                      pathArtistMap.keys()
                    ).sort();
                    const pathUniqueArtists = pathUniqueBaseNames.map(
                      (baseName) => ({
                        baseName,
                        variations: pathArtistMap.get(baseName).sort(),
                      })
                    );

                    return (
                      <div
                        style={{
                          height: '100%',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          padding: '1rem',
                          background: '#1a1a1a',
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        {pathUniqueArtists.length === 0 ? (
                          <div
                            style={{
                              textAlign: 'center',
                              color: '#aaa',
                              padding: '2rem',
                              fontSize: '1rem',
                            }}
                          >
                            {!selectedPath.type &&
                            !selectedPath.language &&
                            !selectedPath.genre &&
                            !selectedPath.subgenre
                              ? 'Select Albums or Singles to see artists, or navigate through the hierarchy to filter artists.'
                              : 'No artists match the current path. Select different levels to see artists.'}
                          </div>
                        ) : (
                          <>
                            <div
                              style={{
                                marginBottom: '1rem',
                                color: '#fff',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                borderBottom: 'none',
                                paddingBottom: '0.5rem',
                              }}
                            >
                              Artists Matching Current Path (
                              {pathUniqueArtists.length})
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '0.75rem',
                              }}
                            >
                              {pathUniqueArtists.map(
                                ({ baseName, variations }) => {
                                  const isSelected = selectedArtists.some(
                                    (selected) =>
                                      normalizeArtistName(selected) ===
                                      normalizeArtistName(baseName)
                                  );

                                  return (
                                    <button
                                      key={baseName}
                                      onClick={() =>
                                        handleArtistToggle(baseName)
                                      }
                                      style={{
                                        background: isSelected
                                          ? '#1db954'
                                          : '#2a2a2a',
                                        padding: '0.75rem 1.25rem',
                                        borderRadius: '8px',
                                        border: 'none',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        fontSize: '0.95rem',
                                        fontWeight: '500',
                                        boxShadow: isSelected
                                          ? '0 4px 12px rgba(29, 185, 84, 0.3)'
                                          : 'none',
                                        whiteSpace: 'nowrap',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.background =
                                            '#333';
                                          e.currentTarget.style.borderColor =
                                            '#555';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.background =
                                            '#2a2a2a';
                                          e.currentTarget.style.borderColor =
                                            '#444';
                                        }
                                      }}
                                    >
                                      {baseName} {isSelected && ' ✓'}
                                    </button>
                                  );
                                }
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()
                ) : shuffleQueue.length > 0 ? (
                  // Show playlist queue when shuffleQueue has items
                  <div
                    style={{
                      height: '100%',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      padding: '1rem',
                      background: '#1a1a1a',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '1rem',
                        color: '#fff',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        borderBottom: '1px solid #333',
                        paddingBottom: '0.5rem',
                      }}
                    >
                      📋 Playlist Queue ({shuffleQueue.length}{' '}
                      {shuffleQueue.length === 1 ? 'song' : 'songs'})
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      {shuffleQueue.map((track, index) => (
                        <div
                          key={track.id || index}
                          onClick={() => {
                            setCurrentTrackIndex(index);
                            setCurrentTrack(track);
                            setSelectedAlbum(track.album);
                            setIsPlaying(true);
                          }}
                          style={{
                            background:
                              index === currentTrackIndex
                                ? '#1db954'
                                : '#2a2a2a',
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border:
                              index === currentTrackIndex
                                ? '2px solid #1db954'
                                : '1px solid #333',
                            color: '#fff',
                          }}
                          onMouseEnter={(e) => {
                            if (index !== currentTrackIndex) {
                              e.currentTarget.style.background = '#333';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== currentTrackIndex) {
                              e.currentTarget.style.background = '#2a2a2a';
                            }
                          }}
                        >
                          <div
                            style={{
                              fontWeight:
                                index === currentTrackIndex ? '600' : '400',
                              fontSize: '0.95rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {track.DisplayTitle ||
                              track.Title ||
                              track.title ||
                              `Track ${index + 1}`}
                          </div>
                          <div
                            style={{
                              fontSize: '0.85rem',
                              color:
                                index === currentTrackIndex ? '#fff' : '#aaa',
                              opacity: 0.9,
                            }}
                          >
                            {track.artist ||
                              track.album?.artist ||
                              'Unknown Artist'}{' '}
                            •{' '}
                            {track.album?.album ||
                              track.album?.albumName ||
                              'Unknown Album'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Tools/Utility Row - Extracted from AlbumGrid */}
                    <div className="viewport-albums-tools">
                      {/* Tools will be rendered here */}
                    </div>

                    {/* Albums Grid */}
                    <div className="viewport-albums-content">
                      <AlbumGrid
                        selectedPath={selectedPath}
                        selectedAlbum={selectedAlbum}
                        onAlbumSelect={handleAlbumSelect}
                        hierarchy={hierarchy}
                        onShufflePlay={handleShufflePlay}
                        queuedAlbumIds={queuedAlbumIds}
                        onAddToQueue={handleAddToQueue}
                        onQueueModeChange={(enabled) => {
                          if (enabled) {
                            // When entering Queue mode, collapse left panel to maximize album grid
                            setLeftPanelCollapsed(true);
                          }
                        }}
                        onQueryMusicbrainz={handleQueryMusicbrainz}
                        onQuerySpotify={handleQuerySpotify}
                        onQueryLastfm={handleQueryLastfm}
                        hideUtilityRow={true}
                        mediaPlayerVisible={mediaPlayerVisible}
                        onDoneSearching={handleDoneSearching}
                        selectedArtists={selectedArtists}
                        isFilteringByArtists={isFilteringByArtists}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Hipster Accordion - Right after Albums */}
          <div
            className={`viewport-bottom-column accordion-section ${
              !isHipsterExpanded ? 'collapsed' : ''
            }`}
            style={{
              display: 'flex',
              visibility: 'visible',
              opacity: 1,
              width: '100%',
              minHeight: '48px',
              position: 'relative',
              zIndex: 10,
            }}
          >
            {/* Accordion Header for Hipster */}
            <div
              className="accordion-header"
              onClick={() => setIsHipsterExpanded(!isHipsterExpanded)}
              style={{
                padding: '0.75rem 1rem',
                background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
                borderBottom: 'none',
                cursor: 'pointer',
                display: 'flex',
                visibility: 'visible',
                opacity: 1,
                width: '100%',
                minHeight: '48px',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none',
                position: 'relative',
                zIndex: 11,
              }}
            >
              <span
                style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}
              >
                Hipster
              </span>
              <span
                style={{
                  color: '#fff',
                  fontSize: '1.2rem',
                  transition: 'transform 0.3s ease',
                  transform: isHipsterExpanded
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                }}
              >
                ▼
              </span>
            </div>

            {/* Accordion Content */}
            {isHipsterExpanded && (
              <div className="accordion-content">
                <div
                  style={{
                    height: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '1rem',
                    background: '#1a1a1a',
                  }}
                >
                  {hitsterLoading ? (
                    <div
                      style={{
                        textAlign: 'center',
                        color: '#aaa',
                        padding: '2rem',
                        fontSize: '0.9rem',
                      }}
                    >
                      Loading Hitster playlist...
                    </div>
                  ) : hitsterError ? (
                    <div
                      style={{
                        textAlign: 'center',
                        color: '#ff6b6b',
                        padding: '2rem',
                        fontSize: '0.9rem',
                      }}
                    >
                      {hitsterError}
                    </div>
                  ) : hitsterTracks.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        color: '#aaa',
                        padding: '2rem',
                        fontSize: '0.9rem',
                      }}
                    >
                      {spotifyAuth.isAuthenticated()
                        ? 'No tracks found in Hitster playlist'
                        : 'Please connect to Spotify to view Hitster playlist'}
                    </div>
                  ) : (
                    <>
                      {/* Go Button and Random Card */}
                      <div
                        style={{ marginBottom: '1rem', textAlign: 'center' }}
                      >
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                          }}
                        >
                          <button
                            onClick={() => {
                              // Get a random track from hitsterTracks
                              if (hitsterTracks.length > 0) {
                                // For non-Premium users, filter to only tracks with preview URLs
                                let availableTracks = hitsterTracks;
                                if (!isSpotifyPremium) {
                                  availableTracks = hitsterTracks.filter(
                                    (track) => track.previewUrl
                                  );
                                  if (availableTracks.length === 0) {
                                    alert(
                                      'No tracks with previews available. Please upgrade to Spotify Premium to play all tracks.'
                                    );
                                    return;
                                  }
                                }

                                const randomIndex = Math.floor(
                                  Math.random() * availableTracks.length
                                );
                                const random = availableTracks[randomIndex];
                                setRandomTrack(random);
                                setSelectedTrackForCard(null); // Clear any previously selected track
                              }
                            }}
                            style={{
                              padding: '1rem 2rem',
                              background:
                                'linear-gradient(135deg, #1db954 0%, #1ed760 100%)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '12px',
                              fontSize: '1.2rem',
                              fontWeight: '700',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              boxShadow: '0 4px 12px rgba(29, 185, 84, 0.4)',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform =
                                'translateY(-2px)';
                              e.currentTarget.style.boxShadow =
                                '0 6px 16px rgba(29, 185, 84, 0.5)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow =
                                '0 4px 12px rgba(29, 185, 84, 0.4)';
                            }}
                          >
                            <span>🎯</span>
                            <span>Go!</span>
                          </button>

                          <button
                            onClick={() => setShowQRScanner(true)}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: '#2a2a2a',
                              color: '#fff',
                              border: '1px solid #444',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#333';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#2a2a2a';
                            }}
                          >
                            <span>📷</span>
                            <span>Scan QR Code / Barcode</span>
                          </button>
                        </div>
                      </div>

                      {/* Random Card Display */}
                      {randomTrack && (
                        <div
                          style={{
                            marginBottom: '2rem',
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '1rem',
                          }}
                        >
                          {(() => {
                            // Extract year - prefer original release year for compilations, otherwise use album release date
                            let year = null;
                            let yearConfidence = 'uncertain';

                            // Determine year and confidence
                            if (randomTrack.originalReleaseYear) {
                              // We have the original release year from MusicBrainz/Last.fm - this is accurate
                              year = randomTrack.originalReleaseYear.toString();
                              yearConfidence = 'accurate';
                            } else if (randomTrack.albumReleaseDate) {
                              const yearMatch =
                                randomTrack.albumReleaseDate.match(/^(\d{4})/);
                              year = yearMatch ? yearMatch[1] : null;

                              // Determine confidence based on album type
                              if (randomTrack.albumType === 'compilation') {
                                // Compilation album - this is NOT the original release date
                                yearConfidence = 'compilation';
                              } else if (
                                randomTrack.albumType === 'album' ||
                                randomTrack.albumType === 'single'
                              ) {
                                // Regular album or single - this IS the original release date
                                yearConfidence = 'accurate';
                              } else {
                                // Unknown album type - uncertain
                                yearConfidence = 'uncertain';
                              }
                            }

                            return (
                              <HitsterCard
                                key={
                                  randomTrack.id ||
                                  randomTrack.uri ||
                                  Date.now()
                                }
                                artist={randomTrack.artist || 'Unknown Artist'}
                                year={year || '?'}
                                songTitle={randomTrack.name || 'Unknown Song'}
                                trackUri={randomTrack.uri}
                                trackId={randomTrack.id}
                                size="medium"
                                flipped={true}
                                yearConfidence={yearConfidence}
                              />
                            );
                          })()}
                        </div>
                      )}

                      {/* Nested Accordion Structure - HIDDEN (commented out for game mode) */}
                      {false && (
                        <div style={{ display: 'none' }}>
                          {(() => {
                            const organized = organizeHitsterTracks();
                            const decades = Object.keys(organized).sort(
                              (a, b) => {
                                // Sort decades: 2020s, 2010s, 2000s, etc.
                                const aNum = parseInt(a.replace('s', ''));
                                const bNum = parseInt(b.replace('s', ''));
                                return bNum - aNum; // Descending
                              }
                            );

                            return (
                              <div key="hipster-accordion">
                                {decades.map((decade) => {
                                  const isDecadeExpanded =
                                    expandedDecades.has(decade);
                                  const years = Object.keys(
                                    organized[decade]
                                  ).sort((a, b) => {
                                    if (a === 'Unknown Year') return 1;
                                    if (b === 'Unknown Year') return -1;
                                    return parseInt(b) - parseInt(a); // Descending order
                                  });

                                  return (
                                    <div
                                      key={decade}
                                      style={{ marginBottom: '0.5rem' }}
                                    >
                                      {/* Decade Header */}
                                      <div
                                        onClick={() => toggleDecade(decade)}
                                        style={{
                                          padding: '0.75rem 1rem',
                                          background: '#2a2a2a',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          marginBottom: '0.25rem',
                                          userSelect: 'none',
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.background =
                                            '#333')
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.background =
                                            '#2a2a2a')
                                        }
                                      >
                                        <span
                                          style={{
                                            color: '#fff',
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                          }}
                                        >
                                          {isDecadeExpanded ? '▼' : '▶'}{' '}
                                          {decade}
                                        </span>
                                        <span
                                          style={{
                                            color: '#aaa',
                                            fontSize: '0.9rem',
                                          }}
                                        >
                                          {years.length} year
                                          {years.length !== 1 ? 's' : ''}
                                        </span>
                                      </div>

                                      {/* Years */}
                                      {isDecadeExpanded &&
                                        years.map((year) => {
                                          const yearKey = `${decade}-${year}`;
                                          const isYearExpanded =
                                            expandedYears.has(yearKey);
                                          const artists = Object.keys(
                                            organized[decade][year]
                                          ).sort();

                                          return (
                                            <div
                                              key={year}
                                              style={{
                                                marginLeft: '1rem',
                                                marginBottom: '0.5rem',
                                              }}
                                            >
                                              {/* Year Header */}
                                              <div
                                                onClick={() =>
                                                  toggleYear(decade, year)
                                                }
                                                style={{
                                                  padding: '0.6rem 0.75rem',
                                                  background: '#252525',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  justifyContent:
                                                    'space-between',
                                                  alignItems: 'center',
                                                  marginBottom: '0.25rem',
                                                  userSelect: 'none',
                                                }}
                                                onMouseEnter={(e) =>
                                                  (e.currentTarget.style.background =
                                                    '#2d2d2d')
                                                }
                                                onMouseLeave={(e) =>
                                                  (e.currentTarget.style.background =
                                                    '#252525')
                                                }
                                              >
                                                <span
                                                  style={{
                                                    color: '#fff',
                                                    fontWeight: '500',
                                                    fontSize: '0.95rem',
                                                  }}
                                                >
                                                  {isYearExpanded ? '▼' : '▶'}{' '}
                                                  {year}
                                                </span>
                                                <span
                                                  style={{
                                                    color: '#aaa',
                                                    fontSize: '0.85rem',
                                                  }}
                                                >
                                                  {artists.length} artist
                                                  {artists.length !== 1
                                                    ? 's'
                                                    : ''}
                                                </span>
                                              </div>

                                              {/* Artists */}
                                              {isYearExpanded &&
                                                artists.map((artist) => {
                                                  const artistKey = `${decade}-${year}-${artist}`;
                                                  const isArtistExpanded =
                                                    expandedArtists.has(
                                                      artistKey
                                                    );
                                                  const songs =
                                                    organized[decade][year][
                                                      artist
                                                    ];

                                                  return (
                                                    <div
                                                      key={artist}
                                                      style={{
                                                        marginLeft: '1rem',
                                                        marginBottom: '0.5rem',
                                                      }}
                                                    >
                                                      {/* Artist Header */}
                                                      <div
                                                        onClick={() =>
                                                          toggleArtist(
                                                            decade,
                                                            year,
                                                            artist
                                                          )
                                                        }
                                                        style={{
                                                          padding:
                                                            '0.5rem 0.75rem',
                                                          background: '#1f1f1f',
                                                          borderRadius: '6px',
                                                          cursor: 'pointer',
                                                          display: 'flex',
                                                          justifyContent:
                                                            'space-between',
                                                          alignItems: 'center',
                                                          marginBottom:
                                                            '0.25rem',
                                                          userSelect: 'none',
                                                        }}
                                                        onMouseEnter={(e) =>
                                                          (e.currentTarget.style.background =
                                                            '#272727')
                                                        }
                                                        onMouseLeave={(e) =>
                                                          (e.currentTarget.style.background =
                                                            '#1f1f1f')
                                                        }
                                                      >
                                                        <span
                                                          style={{
                                                            color: '#fff',
                                                            fontSize: '0.9rem',
                                                          }}
                                                        >
                                                          {isArtistExpanded
                                                            ? '▼'
                                                            : '▶'}{' '}
                                                          {artist}
                                                        </span>
                                                        <span
                                                          style={{
                                                            color: '#aaa',
                                                            fontSize: '0.8rem',
                                                          }}
                                                        >
                                                          {songs.length} song
                                                          {songs.length !== 1
                                                            ? 's'
                                                            : ''}
                                                        </span>
                                                      </div>

                                                      {/* Songs */}
                                                      {isArtistExpanded && (
                                                        <div
                                                          style={{
                                                            marginLeft: '1rem',
                                                          }}
                                                        >
                                                          {songs.map(
                                                            (song, index) => {
                                                              const isSelected =
                                                                selectedTrackForCard?.id ===
                                                                song.id;
                                                              // Extract year from album release date
                                                              let year = null;
                                                              if (
                                                                song.albumReleaseDate
                                                              ) {
                                                                const yearMatch =
                                                                  song.albumReleaseDate.match(
                                                                    /^(\d{4})/
                                                                  );
                                                                year = yearMatch
                                                                  ? yearMatch[1]
                                                                  : null;
                                                              }

                                                              return (
                                                                <div
                                                                  key={
                                                                    song.id ||
                                                                    index
                                                                  }
                                                                >
                                                                  <div
                                                                    onClick={(
                                                                      e
                                                                    ) => {
                                                                      e.stopPropagation(); // Prevent parent accordion from toggling
                                                                      // Toggle card display
                                                                      if (
                                                                        isSelected
                                                                      ) {
                                                                        setSelectedTrackForCard(
                                                                          null
                                                                        );
                                                                      } else {
                                                                        setSelectedTrackForCard(
                                                                          song
                                                                        );
                                                                      }

                                                                      // Also play the track if available
                                                                      if (
                                                                        spotifyPreviewAudioRef.current
                                                                      ) {
                                                                        spotifyPreviewAudioRef.current.pause();
                                                                        spotifyPreviewAudioRef.current =
                                                                          null;
                                                                      }

                                                                      // If user has Premium and track has URI, use SpotifyPlayer for full playback
                                                                      if (
                                                                        isSpotifyPremium &&
                                                                        song.uri
                                                                      ) {
                                                                        console.log(
                                                                          '🎵 Playing track via SpotifyPlayer:',
                                                                          song.uri
                                                                        );
                                                                        setSpotifyTrackUri(
                                                                          song.uri
                                                                        );
                                                                        setCurrentTrack(
                                                                          {
                                                                            name: song.name,
                                                                            artist:
                                                                              song.artist,
                                                                            image:
                                                                              song.image,
                                                                            source:
                                                                              'spotify',
                                                                          }
                                                                        );
                                                                        setIsPlaying(
                                                                          true
                                                                        );
                                                                      } else if (
                                                                        song.previewUrl
                                                                      ) {
                                                                        // Fall back to preview if available
                                                                        const audio =
                                                                          new Audio(
                                                                            song.previewUrl
                                                                          );
                                                                        audio
                                                                          .play()
                                                                          .catch(
                                                                            (
                                                                              err
                                                                            ) => {
                                                                              console.error(
                                                                                'Error playing preview:',
                                                                                err
                                                                              );
                                                                            }
                                                                          );
                                                                        spotifyPreviewAudioRef.current =
                                                                          audio;
                                                                        setCurrentTrack(
                                                                          {
                                                                            ...song,
                                                                            previewUrl:
                                                                              song.previewUrl,
                                                                            source:
                                                                              'spotify',
                                                                          }
                                                                        );
                                                                        setIsPlaying(
                                                                          true
                                                                        );
                                                                        audio.onended =
                                                                          () => {
                                                                            setIsPlaying(
                                                                              false
                                                                            );
                                                                            spotifyPreviewAudioRef.current =
                                                                              null;
                                                                          };
                                                                        audio.onerror =
                                                                          () => {
                                                                            setIsPlaying(
                                                                              false
                                                                            );
                                                                            spotifyPreviewAudioRef.current =
                                                                              null;
                                                                          };
                                                                      }
                                                                    }}
                                                                    style={{
                                                                      padding:
                                                                        '0.5rem 0.75rem',
                                                                      background:
                                                                        isSelected
                                                                          ? '#2a2a2a'
                                                                          : '#1a1a1a',
                                                                      borderRadius:
                                                                        '4px',
                                                                      marginBottom:
                                                                        '0.25rem',
                                                                      cursor:
                                                                        'pointer',
                                                                      userSelect:
                                                                        'none',
                                                                      border:
                                                                        isSelected
                                                                          ? '2px solid #1db954'
                                                                          : 'none',
                                                                    }}
                                                                    onMouseEnter={(
                                                                      e
                                                                    ) => {
                                                                      e.currentTarget.style.background =
                                                                        isSelected
                                                                          ? '#333'
                                                                          : '#222';
                                                                    }}
                                                                    onMouseLeave={(
                                                                      e
                                                                    ) => {
                                                                      e.currentTarget.style.background =
                                                                        isSelected
                                                                          ? '#2a2a2a'
                                                                          : '#1a1a1a';
                                                                    }}
                                                                  >
                                                                    <div
                                                                      style={{
                                                                        color:
                                                                          '#fff',
                                                                        fontSize:
                                                                          '0.85rem',
                                                                      }}
                                                                    >
                                                                      {song.name ||
                                                                        'Unknown Song'}
                                                                    </div>
                                                                    {song.album && (
                                                                      <div
                                                                        style={{
                                                                          color:
                                                                            '#aaa',
                                                                          fontSize:
                                                                            '0.75rem',
                                                                          marginTop:
                                                                            '0.25rem',
                                                                        }}
                                                                      >
                                                                        {
                                                                          song.album
                                                                        }
                                                                      </div>
                                                                    )}
                                                                  </div>

                                                                  {/* Show HitsterCard when this track is selected */}
                                                                  {isSelected && (
                                                                    <div
                                                                      style={{
                                                                        margin:
                                                                          '0.5rem 0 1rem 0',
                                                                        display:
                                                                          'flex',
                                                                        justifyContent:
                                                                          'center',
                                                                        padding:
                                                                          '1rem',
                                                                        background:
                                                                          '#1a1a1a',
                                                                        borderRadius:
                                                                          '8px',
                                                                      }}
                                                                    >
                                                                      <HitsterCard
                                                                        artist={
                                                                          song.artist ||
                                                                          'Unknown Artist'
                                                                        }
                                                                        year={
                                                                          year ||
                                                                          '?'
                                                                        }
                                                                        songTitle={
                                                                          song.name ||
                                                                          'Unknown Song'
                                                                        }
                                                                        trackUri={
                                                                          song.uri
                                                                        }
                                                                        trackId={
                                                                          song.id
                                                                        }
                                                                        size="medium"
                                                                        flipped={
                                                                          true
                                                                        }
                                                                      />
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              );
                                                            }
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                            </div>
                                          );
                                        })}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {/* End of hidden accordion */}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Column - 10% Height */}
          <div className="viewport-bottom-small-column">
            {/* Floating Action Buttons - Inline on medium/large, 2 rows on mobile */}
            <div className="floating-action-buttons">
              {/* On mobile: Row 1: Media Controls (Centered) */}
              {isMobile && (mobileViewMode === 'browsing' || isSwipeModeActive) && (
                <div className="fab-row fab-row-media">
                  <div className="fab-column center">
                    {/* Previous Track */}
                    <button
                      className="fab-btn media-control-btn"
                      onClick={handlePreviousTrack}
                      disabled={!handlePreviousTrack || currentTrackIndex === 0}
                      title="Previous Track"
                    >
                      ⏮
                    </button>
                    {/* Play/Pause */}
                    <button
                      className="fab-btn media-control-btn media-control-play"
                      onClick={() => setIsPlaying(!isPlaying)}
                      disabled={!currentTrack}
                      title={isPlaying ? 'Pause' : 'Play'}
                    >
                      {isPlaying ? '⏸' : '▶'}
                    </button>
                    {/* Next Track */}
                    <button
                      className="fab-btn media-control-btn"
                      onClick={handleNextTrack}
                      disabled={
                        !handleNextTrack ||
                        currentTrackIndex >= shuffleQueue.length - 1
                      }
                      title="Next Track"
                    >
                      ⏭
                    </button>
                  </div>
                </div>
              )}

              {/* Row 2: Tools (with inline media controls on medium/large) */}
              <div className="fab-row fab-row-tools">
                {/* Left Column */}
                <div className="fab-column left">
                  {/* Menu Button (shows/hides navigation links in drawer, or switches back to searching on mobile) */}
                  <button
                    className="fab-btn menu-fab"
                    onClick={() => {
                      if (isMobile && mobileViewMode === 'browsing') {
                        // On mobile in browsing mode, switch back to searching mode
                        setMobileViewMode('searching');
                      } else {
                        // On desktop or in searching mode, toggle nav links
                        setShowNavLinks(!showNavLinks);
                      }
                    }}
                    title={
                      isMobile && mobileViewMode === 'browsing'
                        ? 'Back to Search'
                        : showNavLinks
                        ? 'Hide Menu'
                        : 'Show Menu'
                    }
                  >
                    {isMobile && mobileViewMode === 'browsing'
                      ? '🔍'
                      : showNavLinks
                      ? '▼'
                      : '☰'}
                  </button>

                  {/* Voice Command Button */}
                  <button
                    className="fab-btn voice-fab"
                    onClick={() => {
                      // Trigger voice assistant via ref
                      if (voiceAssistantRef.current) {
                        voiceAssistantRef.current.startListening();
                      }
                    }}
                    title="Hey Jook - Voice Commands"
                  >
                    🎙️
                  </button>
                  {/* Preview Mode Button (Lightning Bolt) - Only show when queue has items */}
                  {shuffleQueue.length > 0 && (
                    <button
                      className="fab-btn swipe-mode-fab preview"
                      onClick={() => handleStartSwipeMode('preview')}
                      title="Preview Mode - Fast filtering"
                    >
                      ⚡
                    </button>
                  )}
                </div>

                {/* Center Column - Media Controls and Done Searching on medium/large */}
                <div
                  className="fab-column center"
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  {/* On medium/large: Media Controls */}
                  {!isMobile && (
                    <>
                      {/* Previous Track */}
                      <button
                        className="fab-btn media-control-btn"
                        onClick={handlePreviousTrack}
                        disabled={
                          !handlePreviousTrack || currentTrackIndex === 0
                        }
                        title="Previous Track"
                      >
                        ⏮
                      </button>
                      {/* Play/Pause */}
                      <button
                        className="fab-btn media-control-btn media-control-play"
                        onClick={() => setIsPlaying(!isPlaying)}
                        disabled={!currentTrack}
                        title={isPlaying ? 'Pause' : 'Play'}
                      >
                        {isPlaying ? '⏸' : '▶'}
                      </button>
                      {/* Next Track */}
                      <button
                        className="fab-btn media-control-btn"
                        onClick={handleNextTrack}
                        disabled={
                          !handleNextTrack ||
                          currentTrackIndex >= shuffleQueue.length - 1
                        }
                        title="Next Track"
                      >
                        ⏭
                      </button>
                    </>
                  )}
                  {/* Done Searching Button - Show when selection made (both mobile and desktop) */}
                  {(selectedPath.type ||
                    selectedPath.language ||
                    selectedPath.genre) && (
                    <button
                      className="fab-btn done-searching-fab"
                      onClick={handleDoneSearching}
                      title="Done Searching - Show Albums"
                      style={{
                        marginLeft: !isMobile ? '0.5rem' : '0',
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </button>
                  )}
                </div>

                {/* Right Column */}
                <div className="fab-column right">
                  {/* Swipe Mode Buttons - Only show when queue has items */}
                  {shuffleQueue.length > 0 && (
                    <button
                      className="fab-btn swipe-mode-fab listening"
                      onClick={() => handleStartSwipeMode('listening')}
                      title="Listening Mode - Full control"
                    >
                      🎵
                    </button>
                  )}
                  <button
                    className="fab-btn admin-fab"
                    onClick={() =>
                      window.dispatchEvent(new CustomEvent('openAdminPanel'))
                    }
                    title="Admin Prompts"
                  >
                    🛠️
                  </button>
                  <button
                    className="fab-btn setup-fab"
                    onClick={() => setShowSetupWizard(true)}
                    title="Customize your music collection"
                  >
                    ⚙️
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Right Edge Flyout Trigger */}
        {(selectedAlbum || currentTrack) && (
          <div
            className={`flyout-edge-trigger right ${
              rightDrawerOpen ? 'hidden' : ''
            }`}
            onClick={toggleRightDrawer}
            aria-label="Open album details"
          >
            <span className="edge-trigger-icon">‹</span>
          </div>
        )}

        {/* MIDDLE: Filters + Album Grid - HIDDEN (using viewport layout instead) */}
        {/* <main className="library-middle">
          <AlbumGrid
            selectedPath={selectedPath}
            selectedAlbum={selectedAlbum}
            onAlbumSelect={handleAlbumSelect}
            hierarchy={hierarchy}
            onShufflePlay={handleShufflePlay}
            queuedAlbumIds={queuedAlbumIds}
            onAddToQueue={handleAddToQueue}
            onQueueModeChange={(enabled) => {
              if (enabled) {
                // When entering Queue mode, collapse left panel to maximize album grid
                setLeftPanelCollapsed(true);
              }
            }}
            onQueryMusicbrainz={handleQueryMusicbrainz}
            onQuerySpotify={handleQuerySpotify}
            onQueryLastfm={handleQueryLastfm}
            mediaPlayerVisible={mediaPlayerVisible}
          />
        </main> */}

        {/* Track Edit Popup */}
        {showTrackEditPopup && selectedTrackForEdit && (
          <div
            className="track-edit-popup-overlay"
            onClick={() => setShowTrackEditPopup(false)}
          >
            <div
              className="track-edit-popup"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="track-edit-popup-header">
                <h3>
                  Edit Track:{' '}
                  {selectedTrackForEdit.DisplayTitle ||
                    selectedTrackForEdit.Title ||
                    'Unknown Track'}
                </h3>
                <div
                  style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
                >
                  <button
                    className="track-edit-musicbrainz-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate to Dashboard with album info to trigger MusicBrainz search
                      const album = selectedAlbumForEdit || selectedAlbum;
                      if (album) {
                        const artistName = album.artist || album.artistName;
                        const albumId = album.id || album.firestoreId;
                        const albumName = album.album || album.albumName;

                        // Store in sessionStorage as backup
                        sessionStorage.setItem(
                          'metadataUpdate',
                          JSON.stringify({
                            open: true,
                            artistName: artistName,
                            albumId: albumId,
                            albumName: albumName,
                          })
                        );

                        navigate('/dashboard', {
                          state: {
                            openMetadataUpdate: true,
                            artistName: artistName,
                            albumId: albumId,
                            albumName: albumName,
                          },
                          replace: false,
                        });
                      } else {
                        alert(
                          '❌ Cannot search MusicBrainz: Album information not available'
                        );
                      }
                    }}
                    title="Search MusicBrainz for this album's metadata"
                    style={{
                      padding: '6px 12px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    🎵 MusicBrainz
                  </button>
                  <button
                    className="track-edit-popup-close"
                    onClick={() => setShowTrackEditPopup(false)}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="track-edit-popup-content">
                <TrackEditAccordion
                  track={selectedTrackForEdit}
                  album={selectedAlbumForEdit}
                  onUpdateTrack={async (updatedTrack) => {
                    // Update the track in the selectedAlbum
                    if (selectedAlbumForEdit && selectedAlbumForEdit.songs) {
                      const updatedSongs = selectedAlbumForEdit.songs.map(
                        (song) =>
                          song.Id === updatedTrack.Id ||
                          song.Title === updatedTrack.Title
                            ? {
                                ...song,
                                DisplayTitle: updatedTrack.DisplayTitle,
                              }
                            : song
                      );
                      const updatedAlbum = {
                        ...selectedAlbumForEdit,
                        songs: updatedSongs,
                      };
                      setSelectedAlbumForEdit(updatedAlbum);
                      setSelectedAlbum(updatedAlbum);

                      // Update selectedTrackForEdit to reflect the change
                      setSelectedTrackForEdit(updatedTrack);

                      // Update currentTrack if it's the same track
                      if (
                        currentTrack &&
                        (currentTrack.Id === updatedTrack.Id ||
                          currentTrack.Title === updatedTrack.Title)
                      ) {
                        setCurrentTrack({
                          ...currentTrack,
                          DisplayTitle: updatedTrack.DisplayTitle,
                        });
                      }

                      // Update tracks in shuffleQueue if they match
                      setShuffleQueue((prevQueue) =>
                        prevQueue.map((queueTrack) =>
                          queueTrack.Id === updatedTrack.Id ||
                          queueTrack.Title === updatedTrack.Title
                            ? {
                                ...queueTrack,
                                DisplayTitle: updatedTrack.DisplayTitle,
                              }
                            : queueTrack
                        )
                      );

                      // Don't save to Firestore here - user will click "Update database" button
                    }
                  }}
                  onSaveToDatabase={async (pendingUpdates) => {
                    // Save pending updates to Firestore
                    // Note: JBXX collection is read-only from client, so we only update albums-flat
                    if (pendingUpdates.track && selectedAlbumForEdit) {
                      try {
                        // Only update albums-flat (JBXX is read-only from client)
                        await saveTrackDisplayTitleToFirestore(
                          pendingUpdates.track,
                          selectedAlbumForEdit
                        );
                      } catch (error) {
                        console.error(
                          'Error saving track DisplayTitle to Firestore:',
                          error
                        );
                        throw error;
                      }
                    }

                    if (pendingUpdates.album) {
                      try {
                        // Only update albums-flat (JBXX is read-only from client)
                        await saveAlbumDisplayTitlesToFirestore(
                          pendingUpdates.album
                        );
                      } catch (error) {
                        console.error(
                          'Error saving album DisplayTitles to Firestore:',
                          error
                        );
                        throw error;
                      }
                    }

                    // Save multiple albums (for "all albums for artist" feature)
                    if (
                      pendingUpdates.albums &&
                      Array.isArray(pendingUpdates.albums)
                    ) {
                      try {
                        console.log(
                          `💾 Saving ${pendingUpdates.albums.length} album(s) to Firestore...`
                        );
                        let successCount = 0;
                        let errorCount = 0;

                        for (const updatedAlbum of pendingUpdates.albums) {
                          try {
                            await saveAlbumDisplayTitlesToFirestore(
                              updatedAlbum
                            );
                            successCount++;
                          } catch (error) {
                            console.error(
                              `Error saving album ${
                                updatedAlbum.album || updatedAlbum.id
                              }:`,
                              error
                            );
                            errorCount++;
                          }
                        }

                        if (errorCount > 0) {
                          throw new Error(
                            `Failed to save ${errorCount} of ${pendingUpdates.albums.length} album(s). ${successCount} saved successfully.`
                          );
                        }

                        console.log(
                          `✅ Successfully saved ${successCount} album(s) to Firestore`
                        );
                      } catch (error) {
                        console.error(
                          'Error saving albums DisplayTitles to Firestore:',
                          error
                        );
                        throw error;
                      }
                    }
                  }}
                  onUpdateAlbum={async (updatedAlbum) => {
                    // Update the entire album
                    setSelectedAlbumForEdit(updatedAlbum);
                    setSelectedAlbum(updatedAlbum);

                    // Update currentTrack if it's in this album
                    if (currentTrack && updatedAlbum.songs) {
                      const matchingSong = updatedAlbum.songs.find(
                        (song) =>
                          song.Id === currentTrack.Id ||
                          song.Title === currentTrack.Title
                      );
                      if (matchingSong) {
                        setCurrentTrack({
                          ...currentTrack,
                          DisplayTitle: matchingSong.DisplayTitle,
                        });
                      }
                    }

                    // Update tracks in shuffleQueue if they're in this album
                    setShuffleQueue((prevQueue) =>
                      prevQueue.map((queueTrack) => {
                        if (
                          queueTrack.album &&
                          queueTrack.album.id === updatedAlbum.id
                        ) {
                          const matchingSong = updatedAlbum.songs.find(
                            (song) =>
                              song.Id === queueTrack.Id ||
                              song.Title === queueTrack.Title
                          );
                          if (matchingSong) {
                            return {
                              ...queueTrack,
                              DisplayTitle: matchingSong.DisplayTitle,
                            };
                          }
                        }
                        return queueTrack;
                      })
                    );

                    // Don't save to Firestore here - user will click "Update database" button
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* RIGHT: Album Detail */}
        <aside
          className={`library-right ${rightDrawerOpen ? 'mobile-open' : ''} ${
            rightPanelCollapsed ? 'collapsed' : ''
          }`}
        >
          <AlbumDetail
            album={selectedAlbum}
            currentTrack={currentTrack}
            isPlaying={isPlaying}
            onPlayTrack={handlePlayTrack}
            onPlayAlbum={handlePlayAlbum}
            onAddToQueue={handleAddToQueue}
            onClose={() => setRightDrawerOpen(false)}
            onCollapse={toggleRightPanelCollapse}
            isCollapsed={rightPanelCollapsed}
            isInScannedMode={isInScannedMode}
            onAlbumUpdate={(updatedAlbum) => {
              setSelectedAlbum(updatedAlbum);
            }}
            onTrackEdit={(song, album) => {
              setSelectedTrackForEdit(song);
              setSelectedAlbumForEdit(album);
              setShowTrackEditPopup(true);
            }}
            onUpdateMetadata={(album) => {
              console.log('🛠️ onUpdateMetadata called', {
                album,
                artist: album.artist || album.artistName,
              });
              // Navigate to Dashboard with specific album info (not just artist)
              const artistName = album.artist || album.artistName;
              const albumId = album.id || album.firestoreId;
              const albumName = album.album || album.albumName;

              console.log('🛠️ Navigating to Dashboard with album:', {
                albumId,
                albumName,
                artistName,
              });

              // Store in sessionStorage as backup in case location.state doesn't work
              sessionStorage.setItem(
                'metadataUpdate',
                JSON.stringify({
                  open: true,
                  artistName: artistName,
                  albumId: albumId,
                  albumName: albumName,
                })
              );

              navigate('/dashboard', {
                state: {
                  openMetadataUpdate: true,
                  artistName: artistName,
                  albumId: albumId,
                  albumName: albumName,
                },
                replace: false, // Don't replace, allow back button
              });
            }}
          />
        </aside>
      </div>

      {/* Overlay for mobile drawers */}
      {(leftDrawerOpen || rightDrawerOpen) && (
        <div
          className="library-overlay"
          onClick={() => {
            setLeftDrawerOpen(false);
            setRightDrawerOpen(false);
          }}
        />
      )}

      {/* Hidden Audio Player - Only handles audio element, controls are in toolbar */}
      <div style={{ display: 'none' }}>
        {/* Spotify Player for full track playback */}
        {currentTrack?.source === 'spotify' &&
          spotifyTrackUri &&
          isSpotifyPremium && (
            <SpotifyPlayer
              trackUri={spotifyTrackUri}
              currentTrack={currentTrack}
              onTrackEnd={() => {
                setIsPlaying(false);
                setSpotifyTrackUri(null);
                setCurrentTrack(null);
              }}
              onNext={handleNextTrack}
              onPrevious={handlePreviousTrack}
              queueLength={shuffleQueue.length}
              currentIndex={currentTrackIndex}
            />
          )}

        {/* MediaPlayer for local files */}
        {(!currentTrack ||
          currentTrack?.source !== 'spotify' ||
          !spotifyTrackUri ||
          !isSpotifyPremium) && (
          <MediaPlayer
            currentTrack={currentTrack}
            isPlaying={isPlaying}
            onPlay={() => setIsPlaying(true)}
            onPause={handlePause}
            onNext={handleNextTrack}
            onPrevious={handlePreviousTrack}
            queueLength={shuffleQueue.length}
            currentIndex={currentTrackIndex}
            onTrackEdit={(song, album) => {
              setSelectedTrackForEdit(song);
              setSelectedAlbumForEdit(album);
              setShowTrackEditPopup(true);
            }}
          />
        )}
      </div>

      {/* QR Code Scanner Modal */}
      {showQRScanner && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
          }}
        >
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
            }}
          >
            <h2 style={{ color: '#fff', margin: 0, fontSize: '1.5rem' }}>
              Scan QR Code or Barcode
            </h2>
            <div
              id="qr-reader"
              ref={(el) => {
                qrScannerRef.current = el;
                if (el && !el.id) {
                  el.id = 'qr-reader';
                }
              }}
              style={{
                width: '100%',
                maxWidth: '400px',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            />
            <p
              style={{
                color: '#aaa',
                textAlign: 'center',
                margin: 0,
                fontSize: '0.9rem',
              }}
            >
              Point your camera at a QR code or album barcode to play
            </p>
            <button
              onClick={() => setShowQRScanner(false)}
              style={{
                padding: '0.75rem 2rem',
                background: '#ff6b6b',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Swipe Mode Overlay */}
      {isSwipeModeActive && (
        <SwipeOverlay
          queue={swipeQueue}
          currentIndex={swipeQueue.findIndex((t) => !t.kept && !t.rejected)}
          onKeep={handleSwipeKeep}
          onReject={handleSwipeReject}
          onClose={handleCloseSwipeMode}
          mode={swipeMode}
          onModeChange={setSwipeMode}
          onSavePlaylist={handleSaveSwipePlaylist}
          onBlockPermanently={handleBlockPermanently}
          currentPlaylist={currentPlaylist}
          user={user}
          isLiveSave={!!liveSavePlaylistId} // Pass live save status to hide Save button
          liveSavePlaylistInfo={liveSavePlaylistInfo} // Pass playlist info for display
        />
      )}

      {/* Saved Playlists Panel */}
      {showPlaylists && (
        <SavedPlaylists
          playlists={savedPlaylists}
          onLoadPlaylist={handleLoadPlaylist}
          onDeletePlaylist={handleDeletePlaylist}
          onTogglePublic={handleTogglePlaylistPublic}
          onClose={() => setShowPlaylists(false)}
          onCreateMasterPlaylist={handleCreateMasterPlaylist}
          isCreatingMaster={isCreatingMaster}
          user={user}
        />
      )}

      {/* Setup Wizard */}
      {showSetupWizard && (
        <SetupWizard
          user={user}
          hierarchy={hierarchy}
          onComplete={handleSetupComplete}
          onClose={() => setShowSetupWizard(false)}
        />
      )}

      {/* Turntable Overlay */}
      {showTurntable && (
        <div className="turntable-overlay">
          <div className="turntable-overlay-content">
            <button
              className="turntable-close-btn"
              onClick={() => setShowTurntable(false)}
            >
              ✕
            </button>
            <Turntable
              isPlaying={isPlaying}
              currentTrack={currentTrack}
              albumCover={
                currentTrack?.album?.coverUrl || selectedAlbum?.coverUrl
              }
              onPlayPause={() => setIsPlaying(!isPlaying)}
              onNext={handleNextTrack}
              onPrevious={handlePreviousTrack}
              onRestart={handleRestartTrack}
              onSkipForward={handleSkipForward}
              onSkipBackward={handleSkipBackward}
              queueLength={shuffleQueue.length}
              currentIndex={currentTrackIndex}
              showJukebox={true}
            />
          </div>
        </div>
      )}

      {/* Xbox/Gamepad Controller Support */}
      <GamepadController
        onPlayPause={() => setIsPlaying(!isPlaying)}
        onNext={handleNextTrack}
        onPrevious={handlePreviousTrack}
        onVoiceCommand={() => {
          // Trigger the voice assistant button click
          const voiceBtn = document.querySelector('.voice-assistant-fab');
          if (voiceBtn) voiceBtn.click();
        }}
        onVolumeUp={(delta) => {
          window.dispatchEvent(
            new CustomEvent('volumeChange', {
              detail: { delta: delta || 0.05 },
            })
          );
        }}
        onVolumeDown={(delta) => {
          window.dispatchEvent(
            new CustomEvent('volumeChange', {
              detail: { delta: delta || -0.05 },
            })
          );
        }}
        onTogglePlayer={() => setMediaPlayerVisible(!mediaPlayerVisible)}
        onShuffle={() => {
          if (shuffleQueue.length > 0) {
            const shuffled = [...shuffleQueue].sort(() => Math.random() - 0.5);
            setShuffleQueue(shuffled);
            setCurrentTrackIndex(0);
            setCurrentTrack(shuffled[0]);
            setIsPlaying(true);
          }
        }}
        isPlaying={isPlaying}
      />

      {/* Voice Assistant */}
      <VoiceAssistant
        ref={voiceAssistantRef}
        userName={user?.displayName || user?.email?.split('@')[0]}
        availableArtists={hierarchy?.artists || []}
        onCreatePlaylist={(params) => {
          console.log('🎙️ Voice: Create playlist', params);
          // TODO: Implement playlist creation from artists
          // For now, we can search and queue
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onNext={handleNextTrack}
        onPrevious={handlePreviousTrack}
        onShuffle={() => {
          if (shuffleQueue.length > 0) {
            const shuffled = [...shuffleQueue].sort(() => Math.random() - 0.5);
            setShuffleQueue(shuffled);
            setCurrentTrackIndex(0);
            setCurrentTrack(shuffled[0]);
            setIsPlaying(true);
          }
        }}
        onSavePlaylist={(name) => {
          console.log('🎙️ Voice: Save playlist as', name);
          // TODO: Implement save with name
        }}
        onSearch={(query) => {
          console.log('🎙️ Voice: Search for', query);
          // TODO: Implement search
        }}
        isPlaying={isPlaying}
      />
    </div>
  );
}

export default LibraryLayout;
